import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import messages from '../../statics/messages.json';
import orderContext from '../../context/OrderContext';
import viewIcon from '../../statics/images/view.png';
import filterIcon from '../../statics/images/filter.png';
import excelIcon from '../../statics/images/excel.png';
import OrderRefresh from './OrderRefresh';
import FloatingRefreshButton from './FloatingRefreshButton';
import useAutoRefreshTimer, { AUTO_REFRESH_INTERVAL_SECONDS } from './useAutoRefreshTimer';
import OrderPopup from './OrderPopup';
import InlineOrderDropdown from './InlineOrderDropdown';
import HoverPO from '../HoverPO';
import OrderCommentsButton from '../comments/OrderCommentsButton';
import StickyNotesCell from '../StickyNotes/StickyNotesCell';
import useStickyNotes from '../StickyNotes/useStickyNotes';
import useShippingInstructions from '../StickyNotes/useShippingInstructions';
import { useNotifications } from '../../context/NotificationsContext';
import { getCommentThreadKey, getOrderThreadKey, useCommentDock } from '../../context/CommentDockContext';
import OrderStatusDropdown, { collectCustomOrderStatusTags, orderStatusMenuPosition } from "./OrderStatusDropdown";
import OrderHistoryModal from '../orderHistory/OrderHistoryModal';
import AllOrdersHistoryModal from '../orderHistory/AllOrdersHistoryModal';
import OrderHistoryIconButton from '../orderHistory/OrderHistoryIconButton';
import { useOrderHistoryUnread, notifyOrderHistoryChanged } from '../orderHistory/useOrderHistoryUnread';
import { recordOrderAccessHistoryHint } from '../orderHistory/orderAccessHistoryHints';
import { syncOrders, getStatus, getShipmentMethods, getCountries, getCustomer, manageOrderUrl, saveComment, deleteComment, saveLineStatus, getUsersByEmail } from '../../context/CommonContex';
import * as XLSX from 'xlsx';
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { Modal, Button, Form } from 'react-bootstrap';
import ColumnGroupsModal from '../Common/ColumnGroupsModal';
import ColumnVisibilityModal from '../Common/ColumnVisibilityModal';
import ViewModeToggle from '../Common/ViewModeToggle';
import HeaderLineTabs, { LAYOUT_HEADER, LAYOUT_LINE, readLayoutView, saveLayoutView } from './HeaderLineTabs';
import OrderLineView from './OrderLineView';
import BomAvailabilityPill from './BomAvailabilityPill';
import {
    flattenOrdersToLines,
    headerLinesMissingDetails,
    collectOrderComments,
    findCommentLineId,
    findOrderLineById,
    orderLinesForHeader,
} from './orderLineRows';
import PresenceBar from '../Common/PresenceBar';
import CellPresence from '../Common/CellPresence';
import usePresence from '../../hooks/usePresence';
import useRowPresence from '../../hooks/useRowPresence';
import useFocusedCell from '../../hooks/useFocusedCell';
import { cellKey, mergeRowPresence } from '../../utils/presenceKeys';
import { toStoredComment } from '../../utils/commentText';
import {
    loadInlineDropdownModeFromPreference,
    loadViewModeDefault,
    saveViewModeDefault,
    isInlineViewMode,
} from '../../utils/viewModePreference';
import {
    ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY,
    loadOrdersFilterState,
    saveOrdersFilterState,
    clearOrdersFilterState,
    hasActiveOrdersFilter,
} from '../../utils/ordersFilterPersistence';
import '../../Css/style.css';
import './OrderValidation.css';
import {
    DEFAULT_ORDER_VALIDATION_COLUMN_ORDER,
    NORMAL_GROUP_ID,
    ORDER_SET_GROUP_ID,
    ORDER_SET_MERGED_ID,
    GROUPABLE_COLUMN_IDS,
    COLUMN_LABELS as OV_COLUMN_LABELS,
    buildEffectiveColumnOrder,
    expandMergedColumnOrder,
    getEffectiveColumnOrder,
    getMergedIdForGroup,
    getSourceIdsForGroup,
    isMergedColumnId,
    loadColumnGroupsState,
    saveColumnGroupsState,
    sanitizeColumnOrder,
    loadColumnVisibility,
    saveColumnVisibility,
} from './orderValidationColumnConfig';

/**
 * Strips archived BOM parts out of freshly-fetched order lines so the order popup / inline order
 * dropdown never show them. Only call this on data just returned from fetchOrderValidationData —
 * NOT on state that's being optimistically patched by the archive/unarchive action, since that
 * action needs the just-toggled part to stay visible (with its archived styling) until the next
 * real refetch.
 */
function stripArchivedBomParts(orderLines) {
    if (!Array.isArray(orderLines)) return orderLines;
    return orderLines.map((item) => {
        const bomParts = item?.bom?.bomParts;
        if (!Array.isArray(bomParts)) return item;
        const filtered = bomParts.filter((b) => !b?.archived);
        if (filtered.length === bomParts.length) return item;
        return { ...item, bom: { ...item.bom, bomParts: filtered } };
    });
}

/** Same aggregation as KPI “missing parts”; callable before orderLineStatusReportMap useMemo (filter runs early in render). */
function buildOrderLineStatusReportMapFromDetails(orderDetailsData, excludedAvailabilityPartsByOrder = {}) {
    const map = {};
    if (!orderDetailsData || typeof orderDetailsData !== 'object') return map;
    Object.entries(orderDetailsData).forEach(([orderNum, orderLines]) => {
        if (!Array.isArray(orderLines) || orderLines.length === 0) return;
        const excludedPartsForOrder =
            excludedAvailabilityPartsByOrder?.[orderNum] ||
            excludedAvailabilityPartsByOrder?.[String(orderNum)] ||
            {};

        const bomPartMap = new Map();
        orderLines.forEach((item) => {
            const lineShipQty = Number(item?.balancedue) || 0;
            const bomParts = item?.bom?.bomParts || [];

            bomParts.forEach((bom) => {
                const key = bom?.prcpart;
                if (!key) {
                    return;
                }

                const qtyPerTop = Number(bom?.qty_per_top) || 0;
                const qtyOrder = qtyPerTop * lineShipQty;
                const part = bom?.part || {};
                const qohBom = Number(part?.qoh_cache) || 0;
                const qrBom = Number(part?.qr_cache) || 0;

                if (bomPartMap.has(key)) {
                    const existing = bomPartMap.get(key);
                    existing.qtyOrder += qtyOrder;
                } else {
                    bomPartMap.set(key, {
                        prcpart: key,
                        qtyPerTop,
                        qtyOrder,
                        qohBom,
                        qrBom,
                        qtyAvail: qohBom - qrBom,
                    });
                }
            });
        });

        const aggregatedBomParts = Array.from(bomPartMap.values());
        let missingPartsCount = 0;
        aggregatedBomParts.forEach((bomPart) => {
            if (bomPart.qtyAvail < 0 && !excludedPartsForOrder[bomPart.prcpart]) {
                missingPartsCount += 1;
            }
        });

        map[orderNum] = {
            missingPartsCount,
            totalPartsCount: aggregatedBomParts.length,
            orderLineCount: orderLines.length,
        };
    });
    return map;
}

/** Best-effort date when a line/order entered the system (for week-over-week “new orders” stats). */
function getOrderLineEntryDate(line) {
    const raw =
        line?.enterdate ||
        line?.imported_date ||
        line?.enterDate ||
        line?.importedDate ||
        line?.order?.imported_date ||
        line?.order?.enterdate ||
        line?.order?.enterDate ||
        '';
    if (!raw || !String(raw).trim()) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
}

// Order Status Tags with colors (display order)
const ORDER_STATUS_TAGS = [
    { label: 'CC Hold', color: '#E39827' },
    { label: 'Wire Hold', color: '#E39827' },
    { label: 'Credit Hold', color: '#E39827' },
    { label: 'PPA', color: '#E39827' },
    { label: 'Paid', color: '#28a745' },
    { label: 'Partial', color: '#EF9884' },
    { label: 'CoT Required', color: '#4AB2D5' },
    { label: 'PMA', color: '#4AB2D5' },
    { label: 'AS9102', color: '#4AB2D5' },
    { label: 'Burn Test', color: '#4AB2D5' },
    { label: 'No Overage', color: '#BD83B8' },
    { label: 'Specific Lengths Required', color: '#BD83B8' },
    { label: 'Do Not Insure', color: '#BD83B8' },
    { label: 'No Charge', color: '#BD83B8' },
    { label: 'Hot Order/AOG', color: '#FF6B35', icon: 'fa fa-fire' },
    { label: 'PO Review', color: '#ffc400' }
];

// Line Status Tags (display order)
const LINE_STATUS_TAGS = [
    { label: 'Released', color: '#02f77c' },
    { label: 'Shipped', color: '#2D895F' },
    { label: 'In Kitting', color: '#CB5722' },
    { label: 'In Process', color: '#9288FF' },
    { label: 'In Testing', color: '#84E6F8' },
    { label: 'In Outgoing Inspection', color: '#B8B2E1' },
    { label: 'Not Released', color: '#F3DC6B' },
    { label: 'On Hold Cart', color: '#C56A00' },
    { label: 'Staging Cart', color: '#1DA1DB' },
    { label: 'Waiting On Components', color: '#A40202' },
    { label: 'PMA', color: '#594E9F' },
    { label: 'RMA', color: '#EB4F4F' },
    { label: 'ENG Review', color: '#625C5C' },
    { label: 'Charge', color: '#1d545e' }
];

// ---- Bridge Order Validation: draggable column reorder + column groups ----
const ORDER_VALIDATION_COLUMN_ORDER_STORAGE_KEY = 'bridgeOrderValidation.columnOrder.v1';

function loadOrderValidationColumnOrder() {
    try {
        const saved = JSON.parse(localStorage.getItem(ORDER_VALIDATION_COLUMN_ORDER_STORAGE_KEY));
        if (Array.isArray(saved)) return sanitizeColumnOrder(saved);
    } catch (e) { /* ignore corrupt storage */ }
    return [...DEFAULT_ORDER_VALIDATION_COLUMN_ORDER];
}

/** Order date from Cetec `oorderdate` (YYYY-MM-DD). */
function getOrderedOnDate(line) {
    const raw = line?.order?.oorderdate || line?.oorderdate || '';
    if (!raw) return '-';
    const datePart = String(raw).trim().split(/[ T]/)[0];
    return datePart || '-';
}

/** Stacked label/value lines inside a merged table column. */
function CombinedColumnCell({ lines, className = 'orders-column-group-cell text-start' }) {
    const visibleLines = (lines || []).filter(
        (line) => line.show !== false && line.content != null && line.content !== ''
    );
    if (!visibleLines.length) {
        return <span className="text-muted">—</span>;
    }
    return (
        <div className={className}>
            {visibleLines.map((line, index) => (
                <div key={`${line.label}-${index}`} className="orders-column-group-line">
                    {line.label ? (
                        <span className="orders-column-group-label">{line.label}: </span>
                    ) : null}
                    <span
                        className={`orders-column-group-value${line.valueClassName ? ` ${line.valueClassName}` : ''}`}
                        style={line.valueStyle}
                    >
                        {line.content}
                    </span>
                </div>
            ))}
        </div>
    );
}

function getLineItemMismatchStyle(negativeCountStr) {
    const mismatch = negativeCountStr && negativeCountStr.includes('/')
        && parseInt(negativeCountStr.split('/')[0], 10) !== parseInt(negativeCountStr.split('/')[1], 10);
    if (!mismatch) return undefined;
    return {
        backgroundColor: '#e74c3c',
        color: 'white',
        fontWeight: 'bold',
        padding: '1px 4px',
        borderRadius: '2px',
        display: 'inline-block',
    };
}

function ReorderableCells({ order, draggable = false, dragHandlers, draggedColumnId, mergedCells = {}, columnVisibility = {}, onToggleColumn, isHeader = false, children }) {
    const cells = React.Children.toArray(children).filter(
        (child) => React.isValidElement(child) && (typeof child.type === 'string' || typeof child.type === 'function')
    );
    if (cells.length !== DEFAULT_ORDER_VALIDATION_COLUMN_ORDER.length) {
        return <>{cells}</>;
    }
    const byId = {};
    DEFAULT_ORDER_VALIDATION_COLUMN_ORDER.forEach((id, i) => { byId[id] = cells[i]; });
    const activeOrder = Array.isArray(order) && order.length ? order : DEFAULT_ORDER_VALIDATION_COLUMN_ORDER;
    const visibleCount = activeOrder.filter((cid) => columnVisibility[cid] !== false).length;
    return (
        <>
            {activeOrder.map((id) => {
                const cell = isMergedColumnId(id) ? mergedCells?.[id] : byId[id];
                if (!React.isValidElement(cell) || (typeof cell.type !== 'string' && typeof cell.type !== 'function')) {
                    return null;
                }
                // Hidden column: collapse to a thin vertical marker. The header segment is the
                // click target to restore it; body segments continue the divider. Column count is
                // preserved so colSpan / sticky headers / horizontal scroll are unaffected.
                if (columnVisibility[id] === false) {
                    if (isHeader) {
                        return (
                            <th
                                key={id}
                                className="gf-col-hidden-marker"
                                title="Show hidden column"
                                onClick={(e) => { e.stopPropagation(); onToggleColumn && onToggleColumn(id); }}
                            >
                                <span className="gf-col-hidden-bar" aria-label="Show hidden column"></span>
                            </th>
                        );
                    }
                    return (
                        <td key={id} className="gf-col-hidden-marker">
                            <span className="gf-col-hidden-bar"></span>
                        </td>
                    );
                }
                // Visible header cell: add a right-edge handle to hide the column (Excel-like).
                // It is absolutely positioned (width-independent) and stops propagation so the
                // column's sort click and drag-to-reorder keep working.
                let renderCell = cell;
                if (isHeader && onToggleColumn && visibleCount > 1) {
                    const edgeHandle = (
                        <span
                            key="gf-hide-edge"
                            className="gf-col-hide-edge"
                            role="button"
                            title="Hide column"
                            aria-label="Hide column"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); onToggleColumn(id); }}
                        ></span>
                    );
                    const existingChildren = React.Children.toArray(cell.props.children);
                    renderCell = React.cloneElement(
                        cell,
                        { style: { ...(cell.props.style || {}), position: 'relative' } },
                        ...existingChildren,
                        edgeHandle
                    );
                }
                if (!draggable || !dragHandlers) {
                    return React.cloneElement(renderCell, { key: id });
                }
                const isDragging = draggedColumnId === id;
                return React.cloneElement(renderCell, {
                    key: id,
                    draggable: true,
                    onDragStart: (e) => dragHandlers.onDragStart(e, id),
                    onDragOver: (e) => dragHandlers.onDragOver(e, id),
                    onDrop: (e) => dragHandlers.onDrop(e, id),
                    onDragEnd: dragHandlers.onDragEnd,
                    title: 'Drag to reorder column',
                    style: {
                        ...(renderCell.props.style || {}),
                        cursor: 'grab',
                        opacity: isDragging ? 0.4 : 1,
                    },
                });
            })}
        </>
    );
}

/** Presence room id for this page (matches the route path). */
const PRESENCE_PAGE = 'order-validation';

const OrderValidation = () => {
    const { orderFetch, fetchOrderValidationData, fetchAllNotAcceptedOrders, getNewOrders, updateOrderReview, updateOrderAccept, updateOrderReady, updateArchiveOrder, updateArchiveOrderLineItem, updateArchiveBomPart } = orderContext();
    /** Hydrate once from localStorage so filter toggles survive remount / browser refresh. */
    const [ordersFilterHydration] = useState(() => loadOrdersFilterState(ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY));
    const [newOrdersData, setNewOrdersData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState(() => ({
        key: 'orig_shipdate',
        // Orders Due KPI uses newest shipping dates first; otherwise earliest first
        direction: ordersFilterHydration.kpiQuickFilter === 'due' ? 'descending' : 'ascending',
    }));
    const [searchTerm, setSearchTerm] = useState(ordersFilterHydration.searchTerm);
    const [tempSearchTerm, setTempSearchTerm] = useState(ordersFilterHydration.searchTerm);
    const [itemsPerPage, setItemsPerPage] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [weekFilter, setWeekFilter] = useState(ordersFilterHydration.weekFilter);
    const [monthFilter, setMonthFilter] = useState(ordersFilterHydration.monthFilter);
    const [assembliesFilter, setAssembliesFilter] = useState(ordersFilterHydration.assembliesFilter);
    const [filterTabOpen, setFilterTab] = useState(false);
    const [lastRefreshData, setLastRefreshData] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);
    const KPI_SLOT_STORAGE_KEY = 'ecommerce-order-validation-kpi-slot-config-v1';
    const EXCLUDED_BOM_PARTS_STORAGE_KEY = 'ecommerce-orders-excluded-bom-parts-v1';
    const DEFAULT_KPI_SLOTS = [
        { preset: 'total' },
        { preset: 'due' },
        { preset: 'missing' },
        { preset: 'ready' }
    ];

    const normalizeKpiSlotConfig = (raw) => {
        const allowed = new Set(['total', 'due', 'missing', 'ready', 'order', 'line']);
        if (!Array.isArray(raw) || raw.length !== 4) return DEFAULT_KPI_SLOTS.map((s) => ({ ...s }));
        return raw.map((s, i) => {
            const def = DEFAULT_KPI_SLOTS[i];
            if (!s || typeof s !== 'object') return { ...def };
            const preset = allowed.has(s.preset) ? s.preset : def.preset;
            if (preset === 'order' || preset === 'line') {
                const label = typeof s.label === 'string' && s.label.trim() ? s.label.trim() : 'Ready to Ship';
                return { preset, label };
            }
            return { preset };
        });
    };

    const [kpiQuickFilter, setKpiQuickFilter] = useState(ordersFilterHydration.kpiQuickFilter);

    // When Orders Due KPI is selected, show newest shipping dates first (user can still toggle the header).
    useEffect(() => {
        if (kpiQuickFilter === 'due') {
            setSortConfig({ key: 'orig_shipdate', direction: 'descending' });
        }
    }, [kpiQuickFilter]);

    const [kpiSlotConfig, setKpiSlotConfig] = useState(() => {
        try {
            const raw = localStorage.getItem(KPI_SLOT_STORAGE_KEY);
            if (!raw) return normalizeKpiSlotConfig(null);
            return normalizeKpiSlotConfig(JSON.parse(raw));
        } catch {
            return normalizeKpiSlotConfig(null);
        }
    });
    const [kpiTagPickerSlot, setKpiTagPickerSlot] = useState(null);
    const [totalOrdersStatExpanded, setTotalOrdersStatExpanded] = useState(false);
    const pageShellRef = useRef(null);
    const titleWrapRef = useRef(null);
    const stickyBandRef = useRef(null);
    const kpiStatsRef = useRef(null);
    const controlsRowRef = useRef(null);
    const tableScrollRef = useRef(null);
    const [excludedAvailabilityPartsByOrder, setExcludedAvailabilityPartsByOrder] = useState(() => {
        try {
            const raw = localStorage.getItem(EXCLUDED_BOM_PARTS_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });

    const [defaultViewMode, setDefaultViewMode] = useState(() => loadViewModeDefault(localStorage.getItem('userEmail')));
    const [inlineDropdownMode, setInlineDropdownMode] = useState(() => loadInlineDropdownModeFromPreference(localStorage.getItem('userEmail')));
    const validationLayoutKey = 'bridgeOrderValidation.layoutView.v1';
    const [layoutView, setLayoutViewState] = useState(() => readLayoutView(validationLayoutKey));
    const setLayoutView = (next) => {
        const value = next === LAYOUT_LINE ? LAYOUT_LINE : LAYOUT_HEADER;
        setLayoutViewState(value);
        saveLayoutView(validationLayoutKey, value);
    };

    const handleSelectView = useCallback((mode) => {
        setInlineDropdownMode(isInlineViewMode(mode));
    }, []);

    const handleSetViewModeDefault = useCallback((mode) => {
        const userEmail = localStorage.getItem('userEmail');
        saveViewModeDefault(userEmail, mode);
        setDefaultViewMode(mode);
        setInlineDropdownMode(isInlineViewMode(mode));
    }, []);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedSourceLine, setSelectedSourceLine] = useState(null);
    const [historyModalOrder, setHistoryModalOrder] = useState(null);
    const [showAllOrdersHistory, setShowAllOrdersHistory] = useState(false);
    const [orderDetailsData, setOrderDetailsData] = useState({});
    const [expandedInlineOrders, setExpandedInlineOrders] = useState({});
    const [inlineActiveView, setInlineActiveView] = useState({});
    const [expandedInlineBomRows, setExpandedInlineBomRows] = useState({});
    const [orderAccessLinks, setOrderAccessLinks] = useState(() => {
        const savedLinks = localStorage.getItem('orderAccessLinks');
        return savedLinks ? JSON.parse(savedLinks) : {};
    });
    const [exports, setExports] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [overallTotalRecords, setOverallTotalRecords] = useState(0);
    
    // Archived line item IDs (used to hide only specific lines in inline tables)
    const [archivedOrderIds, setArchivedOrderIds] = useState(() => {
        const savedLineArchived = localStorage.getItem('archivedLineItemIds');
        if (savedLineArchived) {
            return new Set(JSON.parse(savedLineArchived));
        }
        const legacyArchived = localStorage.getItem('archivedOrderIds');
        return legacyArchived ? new Set(JSON.parse(legacyArchived)) : new Set();
    });

    // Archived order numbers (used to hide whole order rows)
    const [archivedOrderNums, setArchivedOrderNums] = useState(() => {
        const savedOrderNums = localStorage.getItem('archivedOrderNums');
        return savedOrderNums ? new Set(JSON.parse(savedOrderNums)) : new Set();
    });
    
    // State for archived orders data (stored as array of full order line objects)
    const [archivedOrdersData, setArchivedOrdersData] = useState(() => {
        const savedArchivedData = localStorage.getItem('archivedOrdersData');
        return savedArchivedData ? JSON.parse(savedArchivedData) : [];
    });
    
    // Toast notification state
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    
    // Order Verification state
    const [reviewToggles, setReviewToggles] = useState({});
    const [confirmCheckboxes, setConfirmCheckboxes] = useState({});
    const [readyToggles, setReadyToggles] = useState({});
    
    // Comments state - NOT persisted to localStorage to avoid stale cached data
    const [commentsMap, setCommentsMap] = useState({});
    
    // Order Status dropdown state
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [orderStatusSelections, setOrderStatusSelections] = useState({});
    const [submittedOrderStatuses, setSubmittedOrderStatuses] = useState({});
    const [tagsToDeselect, setTagsToDeselect] = useState({});
    // Line whose notes popup (HoverPO) is open, so presence can claim that cell.
    const [openNotesLineIds, setOpenNotesLineIds] = useState(() => new Set());

    // Line Status state
    const [lineStatusSelections, setLineStatusSelections] = useState({});
    const [submittedLineStatuses, setSubmittedLineStatuses] = useState({});
    const [lineTagsToDeselect, setLineTagsToDeselect] = useState({});
    const [lineStatusDropdownOpen, setLineStatusDropdownOpen] = useState({});
    const [pendingLineRemoval, setPendingLineRemoval] = useState({});

    // Bulk mode state for inline order view - per order
    const [orderBulkModeEnabled, setOrderBulkModeEnabled] = useState({});
    const [orderHasBulkChanges, setOrderHasBulkChanges] = useState({});
    const [, setIsSubmittingBulk] = useState(false);
    
    // Filter state variables
    const [useEffectOrder, setUseEffectOrder] = useState('');
    const [orderSearch, setOrderSearch] = useState([]);
    const [orderValue, setOrderValue] = useState(ordersFilterHydration.orderValue);
    const [useEffectPO, setUseEffectPO] = useState('');
    const [POSearch, setPOSearch] = useState([]);
    const [POValue, setPOValue] = useState(ordersFilterHydration.POValue);
    const [useEffectShippingMethod, setUseEffectShippingMethod] = useState('');
    const [ShippingMethodSearch, setShippingMethodSearch] = useState([]);
    const [ShippingMethodValue, setShippingMethodValue] = useState(ordersFilterHydration.ShippingMethodValue);
    const [useEffectCountry, setUseEffectCountry] = useState('');
    const [CountrySearch, setCountrySearch] = useState([]);
    const [CountryValue, setCountryValue] = useState(ordersFilterHydration.CountryValue);
    const [useEffectCustomer, setUseEffectCustomer] = useState('');
    const [CustomerSearch, setCustomerSearch] = useState([]);
    const [CustomerValue, setCustomerValue] = useState(ordersFilterHydration.CustomerValue);
    const [CustomerId, setCustomerId] = useState(ordersFilterHydration.customerId || '');
    const [selectBoxAppend, setSelectBoxAppend] = useState([]);
    const [statusValue, setStatusValue] = useState(ordersFilterHydration.statusValue);
    const [viewBy, setViewBy] = useState(ordersFilterHydration.viewBy);
    const [startDate, setStartDate] = useState(ordersFilterHydration.startDate);
    const [endDate, setEndDate] = useState(ordersFilterHydration.endDate);
    
    // Order Access Modal state
    const [showOrderAccessModal, setShowOrderAccessModal] = useState(false);
    const [selectedOrderForAccess, setSelectedOrderForAccess] = useState(null);
    const [newLinkUrl, setNewLinkUrl] = useState('');
    const [newLinkUrlName, setNewLinkUrlName] = useState('');
    const [copiedField, setCopiedField] = useState(null);
    const copyTimeoutRef = useRef(null);

    // ---- Draggable column reorder + column groups (Order Validation page) ----
    const [columnOrder, setColumnOrder] = useState(() => {
        const base = loadOrderValidationColumnOrder();
        const groups = loadColumnGroupsState(localStorage.getItem('userEmail'));
        return getEffectiveColumnOrder(base, groups.activeGroupId, groups.customGroups);
    });
    const [draggedColumnId, setDraggedColumnId] = useState(null);

    const [showColumnGroupsModal, setShowColumnGroupsModal] = useState(false);
    const [columnGroupsState, setColumnGroupsState] = useState(() => {
        const userEmail = localStorage.getItem('userEmail');
        return loadColumnGroupsState(userEmail);
    });
    const { activeGroupId, defaultGroupId, customGroups } = columnGroupsState;

    const effectiveColumnOrder = useMemo(
        () => getEffectiveColumnOrder(columnOrder, activeGroupId, customGroups),
        [columnOrder, activeGroupId, customGroups]
    );

    // ---- Column show/hide (inline +/- controls + modal share one state) ----
    const [columnVisibility, setColumnVisibility] = useState(() => loadColumnVisibility(localStorage.getItem('userEmail')));
    const [showColumnVisibilityModal, setShowColumnVisibilityModal] = useState(false);

    const handleColumnVisibilityChange = useCallback((newVisibility) => {
        const next = newVisibility || {};
        setColumnVisibility(next);
        saveColumnVisibility(localStorage.getItem('userEmail'), next);
    }, []);

    const toggleColumnVisibility = useCallback((columnId) => {
        setColumnVisibility((prev) => {
            const currentlyVisible = prev[columnId] !== false;
            // Never hide the last visible column.
            if (currentlyVisible) {
                const visibleIds = effectiveColumnOrder.filter((id) => prev[id] !== false);
                if (visibleIds.length <= 1) return prev;
            }
            const next = { ...prev, [columnId]: currentlyVisible ? false : true };
            saveColumnVisibility(localStorage.getItem('userEmail'), next);
            return next;
        });
    }, [effectiveColumnOrder]);

    const handleShowAllColumns = useCallback(() => {
        handleColumnVisibilityChange({});
    }, [handleColumnVisibilityChange]);

    // Label lookup for the modal (handles merged group columns too).
    const getColumnLabel = useCallback((id) => {
        if (OV_COLUMN_LABELS[id]) return OV_COLUMN_LABELS[id];
        if (isMergedColumnId(id)) {
            const group = customGroups.find((g) => getMergedIdForGroup(g.id) === id);
            return group?.name || 'Group';
        }
        return id;
    }, [customGroups]);

    const columnVisibilityList = useMemo(
        () => effectiveColumnOrder.map((id) => ({ key: id, label: getColumnLabel(id) })),
        [effectiveColumnOrder, getColumnLabel]
    );

    const hiddenColumnCount = useMemo(
        () => effectiveColumnOrder.filter((id) => columnVisibility[id] === false).length,
        [effectiveColumnOrder, columnVisibility]
    );

    const inlineDropdownTrailingCells = 3;

    useEffect(() => {
        const userEmail = localStorage.getItem('userEmail');
        saveColumnGroupsState(userEmail, columnGroupsState);
    }, [columnGroupsState]);

    const handleColumnGroupsApply = ({ activeGroupId: nextActiveId, defaultGroupId: nextDefaultId }) => {
        setColumnGroupsState((prev) => {
            setColumnOrder((prevOrder) => {
                const baseOrder = sanitizeColumnOrder(prevOrder.filter((id) => !isMergedColumnId(id)));
                if (!nextActiveId || nextActiveId === NORMAL_GROUP_ID) {
                    const prevMerged = getMergedIdForGroup(prev.activeGroupId);
                    const prevSources = getSourceIdsForGroup(prev.activeGroupId, prev.customGroups);
                    if (prevMerged && prevSources.length) {
                        return expandMergedColumnOrder(prevOrder, prevMerged, prevSources);
                    }
                    return baseOrder;
                }
                return buildEffectiveColumnOrder(baseOrder, nextActiveId, prev.customGroups);
            });
            return {
                ...prev,
                activeGroupId: nextActiveId,
                defaultGroupId: nextDefaultId,
            };
        });
    };

    const handleSaveCustomGroup = (group) => {
        const safeColumnIds = (group.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id));
        setColumnGroupsState((prev) => {
            const payload = { ...group, columnIds: safeColumnIds };
            const existing = prev.customGroups.findIndex((g) => g.id === payload.id);
            const nextGroups = [...prev.customGroups];
            if (existing >= 0) nextGroups[existing] = payload;
            else nextGroups.push(payload);
            return { ...prev, customGroups: nextGroups };
        });
    };

    const handleDeleteCustomGroup = (groupId) => {
        setColumnGroupsState((prev) => {
            const nextActive = prev.activeGroupId === groupId ? NORMAL_GROUP_ID : prev.activeGroupId;
            const nextDefault = prev.defaultGroupId === groupId ? NORMAL_GROUP_ID : prev.defaultGroupId;
            return {
                ...prev,
                customGroups: prev.customGroups.filter((g) => g.id !== groupId),
                activeGroupId: nextActive,
                defaultGroupId: nextDefault,
            };
        });
        if (activeGroupId === groupId) {
            setColumnOrder((prev) => {
                const mergedId = getMergedIdForGroup(groupId);
                const sources = getSourceIdsForGroup(groupId, customGroups);
                return expandMergedColumnOrder(prev, mergedId, sources);
            });
        }
    };

    useEffect(() => {
        try {
            localStorage.setItem(ORDER_VALIDATION_COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
        } catch (e) { /* ignore storage write errors */ }
    }, [columnOrder]);

    const handleColumnDragStart = (e, id) => {
        setDraggedColumnId(id);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* noop */ }
        }
    };
    const handleColumnDragOver = (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    };
    const handleColumnDrop = (e, targetId) => {
        e.preventDefault();
        const sourceId = draggedColumnId || (e.dataTransfer && e.dataTransfer.getData('text/plain'));
        if (!sourceId || sourceId === targetId) { setDraggedColumnId(null); return; }
        setColumnOrder((prev) => {
            const next = [...prev];
            const from = next.indexOf(sourceId);
            const to = next.indexOf(targetId);
            if (from === -1 || to === -1) return prev;
            next.splice(from, 1);
            next.splice(to, 0, sourceId);
            return next;
        });
        setDraggedColumnId(null);
    };
    const handleColumnDragEnd = () => setDraggedColumnId(null);
    const resetColumnOrder = () => {
        const nextOrder = activeGroupId && activeGroupId !== NORMAL_GROUP_ID
            ? buildEffectiveColumnOrder([...DEFAULT_ORDER_VALIDATION_COLUMN_ORDER], activeGroupId, customGroups)
            : [...DEFAULT_ORDER_VALIDATION_COLUMN_ORDER];
        setColumnOrder(nextOrder);
        try { localStorage.removeItem(ORDER_VALIDATION_COLUMN_ORDER_STORAGE_KEY); } catch (e) { /* ignore */ }
    };
    const orderColumnDragHandlers = {
        onDragStart: handleColumnDragStart,
        onDragOver: handleColumnDragOver,
        onDrop: handleColumnDrop,
        onDragEnd: handleColumnDragEnd,
    };

    const getCetecOrderNumber = (line) => {
        const orderNum = line?.order?.ordernum || line?.ordernum || '';
        const partialName = line?.name || line?.order?.name || '';
        if  (orderNum && partialName) {
            return `${orderNum}(${partialName})`;
        }
        return orderNum || partialName || 'N/A';
    }
    
    const selectedOrderNumber = selectedOrderForAccess?.order?.ordernum ?? '';
    const selectedCustomerName = selectedOrderForAccess?.order?.customer?.name ?? '';

    useEffect(() => {
        fetchNewOrders().catch(() => {});
        fetchStatusData();
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(KPI_SLOT_STORAGE_KEY, JSON.stringify(kpiSlotConfig));
        } catch {
            /* ignore */
        }
    }, [kpiSlotConfig]);

    useEffect(() => {
        try {
            localStorage.setItem(
                EXCLUDED_BOM_PARTS_STORAGE_KEY,
                JSON.stringify(excludedAvailabilityPartsByOrder || {})
            );
        } catch {
            /* ignore */
        }
    }, [excludedAvailabilityPartsByOrder]);

    // Keep Order Validation filter selections in localStorage so they survive refresh / remount.
    useEffect(() => {
        const nextState = {
            weekFilter,
            monthFilter,
            assembliesFilter,
            kpiQuickFilter,
            searchTerm,
            startDate,
            endDate,
            orderValue,
            POValue,
            ShippingMethodValue,
            CountryValue,
            CustomerValue,
            customerId: CustomerId || '',
            statusValue,
            viewBy,
        };

        if (hasActiveOrdersFilter(nextState)) {
            saveOrdersFilterState(nextState, ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY);
        } else {
            clearOrdersFilterState(ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY);
        }
    }, [
        weekFilter,
        monthFilter,
        assembliesFilter,
        kpiQuickFilter,
        searchTerm,
        startDate,
        endDate,
        orderValue,
        POValue,
        ShippingMethodValue,
        CountryValue,
        CustomerValue,
        CustomerId,
        statusValue,
        viewBy,
    ]);

    useEffect(() => {
        if (kpiTagPickerSlot == null) return;
        const onDown = (e) => {
            if (e.target.closest('.orders-kpi-tag-picker') || e.target.closest('.orders-kpi-filter-trigger')) return;
            setKpiTagPickerSlot(null);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [kpiTagPickerSlot]);

    const syncOrderValidationTableHead = useCallback(() => {
        const shell = pageShellRef.current;
        const scrollEl = tableScrollRef.current;
        if (!shell || !scrollEl) return;

        const containerTop = scrollEl.getBoundingClientRect().top;
        let stickyTheadTop = 0;

        const captionEl = scrollEl.querySelector('.order-validation-table-caption');
        if (captionEl) {
            const captionRect = captionEl.getBoundingClientRect();
            if (captionRect.bottom > containerTop + 1) {
                stickyTheadTop = Math.ceil(captionRect.bottom - containerTop);
            }
        }

        shell.style.setProperty('--ov-sticky-thead-top', `${stickyTheadTop}px`);

        // Collapse the page title while the table is scrolled down; restore it at the
        // top. Hysteresis (24px hide / 4px show) prevents flicker at the boundary.
        const titleEl = titleWrapRef.current;
        if (titleEl) {
            const scrolled = scrollEl.scrollTop;
            if (scrolled > 24) {
                titleEl.classList.add('ov-title-collapsed');
            } else if (scrolled < 4) {
                titleEl.classList.remove('ov-title-collapsed');
            }
        }
    }, []);

    useEffect(() => {
        const shell = pageShellRef.current;
        if (!shell) return;

        syncOrderValidationTableHead();

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => syncOrderValidationTableHead())
            : null;

        if (resizeObserver) {
            if (stickyBandRef.current) resizeObserver.observe(stickyBandRef.current);
            if (tableScrollRef.current) resizeObserver.observe(tableScrollRef.current);
        }

        const scrollEl = tableScrollRef.current;
        scrollEl?.addEventListener('scroll', syncOrderValidationTableHead, { passive: true });

        return () => {
            resizeObserver?.disconnect();
            scrollEl?.removeEventListener('scroll', syncOrderValidationTableHead);
        };
    }, [
        syncOrderValidationTableHead,
        loading,
        filterTabOpen,
        kpiQuickFilter,
        totalOrdersStatExpanded,
        kpiTagPickerSlot,
    ]);

    // Save orderAccessLinks to localStorage
    useEffect(() => {
        localStorage.setItem('orderAccessLinks', JSON.stringify(orderAccessLinks));
    }, [orderAccessLinks]);

    // Save archived line item IDs to localStorage
    useEffect(() => {
        localStorage.setItem('archivedLineItemIds', JSON.stringify(Array.from(archivedOrderIds)));
    }, [archivedOrderIds]);

    // Save archived order numbers to localStorage
    useEffect(() => {
        localStorage.setItem('archivedOrderNums', JSON.stringify(Array.from(archivedOrderNums)));
    }, [archivedOrderNums]);

    // Save archivedOrdersData to localStorage
    useEffect(() => {
        localStorage.setItem('archivedOrdersData', JSON.stringify(archivedOrdersData));
    }, [archivedOrdersData]);

    // Initialize submittedOrderStatuses from manualUpdateStatus in orderData
    useEffect(() => {
        if (newOrdersData && Array.isArray(newOrdersData)) {
            newOrdersData.forEach((line) => {
                if (line?.manualUpdateStatus) {
                    try {
                        const orderLineStatuss = JSON.parse(line.manualUpdateStatus);
                        setSubmittedOrderStatuses((prev) => ({
                            ...prev,
                            [line.id]: orderLineStatuss,
                        }));
                    } catch (e) {
                        console.error("Invalid JSON in manualUpdateStatus:", e);
                    }
                } else if (Array.isArray(line?.comment)) {
                    const statusComments = line.comment
                        .filter(c => c.status)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    if (statusComments.length > 0) {
                        try {
                            const tags = JSON.parse(statusComments[0].status);
                            if (Array.isArray(tags)) {
                                setSubmittedOrderStatuses((prev) => ({
                                    ...prev,
                                    [line.id]: tags,
                                }));
                            }
                        } catch (e) {
                            console.error("Invalid JSON in comment.status:", e);
                        }
                    }
                }
            });
        }
    }, [newOrdersData]);

    const loadSubmittedLineStatusesFromRows = useCallback((rows) => {
        if (!Array.isArray(rows)) return;
        rows.forEach((line) => {
            if (line?.lineItemStatus) {
                try {
                    const orderLineStatus = JSON.parse(line.lineItemStatus);
                    setSubmittedLineStatuses((prev) => ({
                        ...prev,
                        [line.id]: orderLineStatus,
                    }));
                } catch (e) {
                    console.error("Invalid JSON in lineItemStatus:", e);
                }
            }
        });
    }, []);

    // Load line status from list rows and inline order details
    useEffect(() => {
        loadSubmittedLineStatusesFromRows(newOrdersData);
    }, [newOrdersData, loadSubmittedLineStatusesFromRows]);

    useEffect(() => {
        if (orderDetailsData && Object.keys(orderDetailsData).length > 0) {
            Object.values(orderDetailsData).forEach((orderLines) => {
                loadSubmittedLineStatusesFromRows(orderLines);
            });
        }
    }, [orderDetailsData, loadSubmittedLineStatusesFromRows]);

    // Handle clicking outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Find the dropdown element that was portaled to document.body
            const dropdownEl = document.querySelector(".order-status-dropdown");

            // If dropdown is open and the click is outside the dropdown element
            if (dropdownOpenId && dropdownEl && !dropdownEl.contains(event.target)) {
                setDropdownOpenId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownOpenId]);

    // Close Order Status portal dropdown on any page/table scroll so it cannot detach
    // from its trigger. Keep open when scrolling inside the portaled menu list.
    useEffect(() => {
        if (!dropdownOpenId) return undefined;

        const handleScroll = (event) => {
            const target = event?.target;
            if (target instanceof Element && target.closest(".order-status-dropdown")) {
                return;
            }
            setDropdownOpenId(null);
        };

        window.addEventListener("scroll", handleScroll, true);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [dropdownOpenId]);

    // Close line status dropdowns when clicking outside trigger/menu
    useEffect(() => {
        const handleLineStatusOutsideClick = (event) => {
            const isLineStatusDropdown = event.target.closest('.line-status-dropdown');
            const isLineStatusDropdownMenu = event.target.closest('.line-status-dropdown-menu');

            if (!isLineStatusDropdown && !isLineStatusDropdownMenu) {
                setLineStatusDropdownOpen({});
            }
        };

        document.addEventListener("mousedown", handleLineStatusOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleLineStatusOutsideClick);
        };
    }, []);

    // Close Line Status portal dropdowns on any page/table scroll so the fixed-position
    // menu cannot detach from its trigger. Closing here (single idempotent state reset)
    // instead of per-row toggles avoids re-opening the menu. Scrolling inside the menu
    // list itself keeps it open.
    useEffect(() => {
        const hasOpenLineStatusDropdown = Object.values(lineStatusDropdownOpen).some(Boolean);
        if (!hasOpenLineStatusDropdown) return undefined;

        const handleScroll = (event) => {
            const target = event?.target;
            if (target instanceof Element && target.closest('.line-status-dropdown-menu')) {
                return;
            }
            setLineStatusDropdownOpen({});
        };

        window.addEventListener("scroll", handleScroll, true);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [lineStatusDropdownOpen]);

    const fetchNewOrders = async () => {
        setLoading(true);
        try {
            const neword = await fetchAllNotAcceptedOrders('0', '-1', '', null, null, null, startDate, endDate, orderValue, POValue, '', statusValue, ShippingMethodValue, CustomerId, CountryValue, viewBy, false, null, null);
            if (neword && neword.order) {
                // Ensure order is an array before using it
                const orderData = Array.isArray(neword.order) ? neword.order : [];
                setNewOrdersData(orderData);
                
                // Pre-fetch order details for missing parts calculation
                if (orderData && orderData.length > 0) {
                    prefetchOrderDetails(orderData);
                }
                
                // Update totalRecords from API response
                const currentTotal = typeof neword.totalRecords === 'number'
                    ? neword.totalRecords
                    : orderData.length;
                setTotalRecords(currentTotal);
                // Always update overallTotalRecords to preserve the maximum total
                if (currentTotal > 0) {
                    setOverallTotalRecords((prevTotal) => {
                        // If no previous total or current is larger, use current
                        if (prevTotal === 0 || currentTotal > prevTotal) {
                            return currentTotal;
                        }
                        // Otherwise keep the larger previous total
                        return prevTotal;
                    });
                }
                
                // Initialize review toggles and confirm checkboxes based on response data
                if (Array.isArray(orderData) && orderData.length > 0) {
                    const newReviewToggles = {};
                    const newConfirmCheckboxes = {};
                    const newReadyToggles = {};
                    const newLinks = {};

                    orderData.forEach(line => {
                        // Set review toggle based on orderReviewed value from backend
                        newReviewToggles[line.id] = line.order.orderReviewed === true;

                        // Set confirm checkbox based on acceptedNewOrder value from backend
                        newConfirmCheckboxes[line.id] = line.order.acceptedNewOrder === true;

                        // Set ready toggle based on orderReady value from backend
                        newReadyToggles[line.id] = line.order.orderReady === true;

                        // Update orderAccessLinks for order data
                        if (line.folderUrl) {
                            newLinks[line.id] = [{
                                id: Date.now() + Math.random(),
                                url: line.folderUrl,
                                dateAdded: new Date().toLocaleDateString()
                            }];
                        }
                    });

                    // Replace states entirely with backend data (not merge)
                    setReviewToggles(newReviewToggles);
                    setConfirmCheckboxes(newConfirmCheckboxes);
                    setReadyToggles(newReadyToggles);
                    setOrderAccessLinks(prev => ({ ...prev, ...newLinks }));
                }
            } else {
                // If no order data, set empty array
                setNewOrdersData([]);
            }
        } catch (error) {
            console.error('Error fetching new orders:', error);
            setNewOrdersData([]);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Pre-fetch order details for all visible orders to populate missing parts data
    const prefetchOrderDetails = async (orders) => {
        if (!orders || orders.length === 0) return;
        
        // Get unique order numbers from the orders
        const uniqueOrderNums = [...new Set(orders.map(line => line.order?.ordernum).filter(Boolean))];
        
        // Fetch details for orders we don't have yet
        const detailsToFetch = uniqueOrderNums.filter(orderNum => !orderDetailsData[orderNum]);
        
        if (detailsToFetch.length === 0) return;
        
        // Fetch in batches to avoid overwhelming the API
        const batchSize = 5;
        for (let i = 0; i < detailsToFetch.length; i += batchSize) {
            const batch = detailsToFetch.slice(i, i + batchSize);
            
            // Fetch all orders in this batch in parallel
            const promises = batch.map(orderNum => 
                fetchOrderValidationData(0, 10, "", "lineitem", "ascending", null, "", "", "", "", "", "", "", "", "", true, true, "", orderNum)
                    .catch(err => {
                        console.error(`Failed to fetch details for order ${orderNum}:`, err);
                        return null;
                    })
            );
            
            const results = await Promise.all(promises);
            
            // Update orderDetailsData with the fetched results
            const newDetailsData = {};
            results.forEach((result, index) => {
                if (result && result.order) {
                    newDetailsData[batch[index]] = stripArchivedBomParts(result.order);
                }
            });
            
            if (Object.keys(newDetailsData).length > 0) {
                setOrderDetailsData(prev => ({
                    ...prev,
                    ...newDetailsData
                }));
            }
        }
    };

    const UPS_SHIPPING_LINK = 'https://www.ups.com/ship/basic/shipment-info?tx=20678219171053728&loc=en_US&promoCodeAlias=sPArk&WT.srch=1';
    const FEDEX_SHIPPING_LINK = 'https://www.fedex.com/secure-login/en-us/#/credentials';

    const renderShippingMethodCell = (methodText) => {
        const text = methodText || 'N/A';
        const lower = String(text).toLowerCase();
        if (lower.includes('fedex')) {
            return <a href={FEDEX_SHIPPING_LINK} target="_blank" rel="noopener noreferrer">{text}</a>;
        }
        if (lower.includes('ups')) {
            return <a href={UPS_SHIPPING_LINK} target="_blank" rel="noopener noreferrer">{text}</a>;
        }
        return text;
    };

    const getLineReportPercentage = (report) => {
        const parsedPercentage = Number(report?.percentage);
        const count = parseInt(report?.count, 10) || 0;
        const totalCount = parseInt(report?.totalCount, 10) || 0;

        if (!Number.isNaN(parsedPercentage) && Number.isFinite(parsedPercentage)) {
            return parsedPercentage;
        }

        if (totalCount > 0) {
            return Math.round((count / totalCount) * 100);
        }

        return 0;
    };

    const normalizeLineStatusKey = (status) => {
        const key = String(status || '').trim().toLowerCase();
        return key === 'stagging cart' ? 'staging cart' : key;
    };

    const formatLineStatusDisplayLabel = (status) => {
        if (normalizeLineStatusKey(status) === 'staging cart') return 'Staging Cart';
        return String(status || '').trim();
    };

    const collectLineStatusLabelsForReport = (line) => {
        const ordered = [];
        const seen = new Set();

        const add = (raw) => {
            const label =
                typeof raw === 'string'
                    ? raw.trim()
                    : String(raw?.label || '').trim();
            if (!label) return;
            const key = normalizeLineStatusKey(label);
            if (seen.has(key)) return;
            seen.add(key);
            ordered.push(formatLineStatusDisplayLabel(label));
        };

        (line?.lineStatuses || []).forEach((s) =>
            add(typeof s === 'string' ? s : s?.label ?? s)
        );

        if (line?.lineItemStatus) {
            try {
                const parsed = JSON.parse(line.lineItemStatus);
                if (Array.isArray(parsed)) {
                    parsed.forEach((s) =>
                        add(typeof s === 'string' ? s : s?.label ?? s)
                    );
                }
            } catch {
                /* ignore invalid JSON */
            }
        }

        (submittedLineStatuses[line.id] || []).forEach((t) =>
            add(typeof t === 'string' ? t : t?.label ?? t)
        );

        (lineStatusSelections[line.id] || []).forEach((t) =>
            add(typeof t === 'string' ? t : t?.label ?? t)
        );

        return ordered;
    };

    const getMergedLineStatusReportRows = (line) => {
        const base = Array.isArray(line?.orderReport) ? [...line.orderReport] : [];
        const existing = new Set(base.map((r) => normalizeLineStatusKey(r?.status)));

        collectLineStatusLabelsForReport(line).forEach((label) => {
            const key = normalizeLineStatusKey(label);
            if (!existing.has(key)) {
                existing.add(key);
                base.push({
                    status: label,
                    count: 1,
                    totalCount: 1
                });
            }
        });

        return base;
    };

    const getOrderLinesForReport = (orderNum) => {
        if (!orderNum) return [];
        const fromDetails = orderDetailsData[orderNum];
        if (Array.isArray(fromDetails) && fromDetails.length > 0) {
            return fromDetails;
        }
        return (newOrdersData || []).filter((l) => String(l?.order?.ordernum) === String(orderNum));
    };

    // Earliest promisedate across all lines for the same order (matches Orders page + inline dropdown).
    const getEarliestPromisedDateForOrder = (line) => {
        const orderNum = line?.order?.ordernum;
        const fallbackDate = line?.promisedate || '';

        if (!orderNum) return fallbackDate;

        const inlineOrderLines = Object.values(orderDetailsData || {}).reduce((acc, value) => {
            if (Array.isArray(value)) {
                return acc.concat(value);
            }
            return acc;
        }, []);

        const isArchived = (itemId) => archivedOrderIds.has(itemId) || archivedOrderIds.has(String(itemId));

        const allLinesForOrder = [
            ...(Array.isArray(newOrdersData) ? newOrdersData : []),
            ...inlineOrderLines
        ].filter(item => item?.order?.ordernum === orderNum);

        const linesForOrder = allLinesForOrder.filter(item => !isArchived(item.id));

        const promisedDates = linesForOrder
            .map(item => String(item?.promisedate || '').trim())
            .filter(Boolean);

        if (fallbackDate && !isArchived(line?.id)) {
            promisedDates.push(String(fallbackDate).trim());
        }

        if (promisedDates.length === 0) return '';

        return promisedDates.reduce((earliest, current) => {
            const earliestDate = new Date(earliest);
            const currentDate = new Date(current);

            if (isNaN(earliestDate.getTime())) return current;
            if (isNaN(currentDate.getTime())) return earliest;

            return currentDate < earliestDate ? current : earliest;
        });
    };

    const parseAsLocalCalendarDate = (dateValue) => {
        if (!dateValue) return null;
        const raw = String(dateValue).trim();
        if (!raw) return null;

        const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            return new Date(Number(year), Number(month) - 1, Number(day));
        }

        const parsed = new Date(raw);
        if (isNaN(parsed.getTime())) return null;
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    };

    const lineHasStatusTag = (ln, statusLabel) => {
        const key = normalizeLineStatusKey(statusLabel);
        return collectLineStatusLabelsForReport(ln).some(
            (lab) => normalizeLineStatusKey(lab) === key
        );
    };

    const getOrderAggregatedLineStatusReportRows = (orderNum) => {
        const lines = getOrderLinesForReport(orderNum);
        if (lines.length === 0) return null;

        const orderKeys = [];
        const keyToDisplayLabel = new Map();

        const register = (displayLabel) => {
            const canonical = formatLineStatusDisplayLabel(displayLabel);
            const k = normalizeLineStatusKey(canonical);
            if (!k) return;
            if (!keyToDisplayLabel.has(k)) {
                keyToDisplayLabel.set(k, canonical);
                orderKeys.push(k);
            }
        };

        lines.forEach((ln) => {
            (ln.orderReport || []).forEach((r) => {
                if (r?.status) register(r.status);
            });
        });

        lines.forEach((ln) => {
            collectLineStatusLabelsForReport(ln).forEach(register);
        });

        const total = lines.length;
        const rows = orderKeys.map((k) => {
            const display = keyToDisplayLabel.get(k);
            const count = lines.filter((ln) => lineHasStatusTag(ln, display)).length;
            const pct = total > 0 ? Math.round((count / total) * 10000) / 100 : 0;
            return {
                status: display,
                count,
                totalCount: total,
                percentage: pct
            };
        });

        return rows.filter((r) => (parseInt(r.count, 10) || 0) > 0);
    };

    /** Multi-line orders: count = lines that have the tag. Single-line: merged row logic with independent per-tag percentages. */
    const getDisplayLineStatusReportRows = (line) => {
        const orderNum = line?.order?.ordernum;
        const lines = getOrderLinesForReport(orderNum);
        if (lines.length > 1) {
            const agg = getOrderAggregatedLineStatusReportRows(orderNum);
            if (agg && agg.length > 0) {
                return agg;
            }
        }
        return getMergedLineStatusReportRows(line);
    };

    const fetchStatusData = async () => {
        const status = await getStatus();
        if (status && status.length > 0) {
            setSelectBoxAppend(status);
        }
    };

    // Toast notification helper
    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: 'info' });
        }, 3000);
    };

    // Live presence: who else is on this page / in which cell.
    const presence = usePresence(PRESENCE_PAGE, {
        onLockDenied: (evt) =>
            showToast(`${evt?.owner?.name || 'Someone else'} is editing this — read-only`, 'error'),
        onRemoteSave: (evt) => applyRemoteSave(evt),
    });

    const lineStatusCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'lineStatus'),
        []
    );

    // Order Status and Notes live in the collapsed grid row, so their presence
    // has to work without anyone expanding the order.
    const orderStatusCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'orderStatus'),
        []
    );

    const notesCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'comment'),
        []
    );

    // Destructured so these identities only change when the roster does, not on
    // every render of this (very large) grid.
    const {
        usersInCell: presenceUsersInCell,
        lockOwner: presenceLockOwner,
        reportFocus: presenceFocus,
        reportBlur: presenceBlur,
    } = presence;

    const notificationsCtx = useNotifications();
    const commentDock = useCommentDock();
    const stickyNotesRemoteRef = useRef(() => {});

    const lineStatusLockOwner = useCallback(
        (lineId) => presenceLockOwner(lineStatusCell(lineId)),
        [presenceLockOwner, lineStatusCell]
    );

    const orderStatusLockOwner = useCallback(
        (lineId) => presenceLockOwner(orderStatusCell(lineId)),
        [presenceLockOwner, orderStatusCell]
    );

    const renderLinePresence = useCallback(
        (lineId) => {
            const key = lineStatusCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    owner={presenceLockOwner(key)}
                    compact
                    activity="on this line status"
                />
            );
        },
        [presenceUsersInCell, presenceLockOwner, lineStatusCell]
    );

    const renderOrderStatusPresence = useCallback(
        (lineId) => {
            const key = orderStatusCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    owner={presenceLockOwner(key)}
                    size={16}
                    compact
                    activity="changing this order status"
                />
            );
        },
        [presenceUsersInCell, presenceLockOwner, orderStatusCell]
    );

    const renderNotesPresence = useCallback(
        (lineId) => {
            const key = notesCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    size={16}
                    compact
                    activity="in the notes"
                />
            );
        },
        [presenceUsersInCell, notesCell]
    );

    // Rows this user currently has open: expanded inline, or showing in the popup.
    // Announced to everyone else so the grid can show "X is also on this order".
    const openRowIds = useMemo(() => {
        const ids = Object.keys(expandedInlineOrders).filter((num) => expandedInlineOrders[num]);
        const popupOrder = showPopup
            ? selectedSourceLine?.order?.ordernum || selectedSourceLine?.ordernum
            : null;
        if (popupOrder && !ids.includes(String(popupOrder))) ids.push(String(popupOrder));
        return ids;
    }, [expandedInlineOrders, showPopup, selectedSourceLine]);

    useRowPresence(PRESENCE_PAGE, openRowIds, presence);

    const orderRowCell = useCallback(
        (ordernum) => cellKey(PRESENCE_PAGE, 'order', ordernum, 'row'),
        []
    );
/** Avatars of everyone else who has this order open, for the row itself. */
    const renderRowPresence = useCallback(
        (ordernum) => {
            if (!ordernum) return null;
            const users = presenceUsersInCell(orderRowCell(ordernum));
            if (!users.length) return null;
            return <CellPresence users={users} size={16} compact activity="also on this order" />;
        },
        [presenceUsersInCell, orderRowCell]
    );
    /** Everyone active anywhere on this row, shown next to the order number.
     *  Editors of the Order Status / Notes cells are included so their activity
     *  is visible without the order being expanded - the cell badges alone are
     *  easy to miss in a wide grid. */
    const renderRowActivity = useCallback(
        (line) => {
            const ordernum = line?.order?.ordernum || line?.ordernum;
            const users = mergeRowPresence([
                {
                    users: presenceUsersInCell(orderStatusCell(line?.id)),
                    activity: 'changing this order status',
                },
                {
                    users: presenceUsersInCell(notesCell(line?.id)),
                    activity: 'in the notes',
                },
                {
                    users: ordernum ? presenceUsersInCell(orderRowCell(ordernum)) : [],
                    activity: 'also on this order',
                },
            ]);
            if (!users.length) return null;
            return <CellPresence users={users} size={16} compact />;
        },
        [presenceUsersInCell, orderRowCell, orderStatusCell, notesCell]
    );

    // Announce the open line-status dropdown as our focused cell and hold a soft
    // lock on it. One effect covers every open/close path (toggle, outside click,
    // scroll-away, submit) because they all funnel through lineStatusDropdownOpen.
    const openLineStatusId = useMemo(
        () => Object.keys(lineStatusDropdownOpen).find((id) => lineStatusDropdownOpen[id]) || null,
        [lineStatusDropdownOpen]
    );

    useEffect(() => {
        if (!openLineStatusId) return undefined;
        const key = lineStatusCell(openLineStatusId);
        presenceFocus(key);
        return () => presenceBlur(key);
    }, [openLineStatusId, lineStatusCell, presenceFocus, presenceBlur]);

    // Order Status: dropdownOpenId is the single source of truth for "which row
    // has the tag picker open", so one focused cell covers every open/close path.
    useFocusedCell(
        dropdownOpenId ? orderStatusCell(dropdownOpenId) : null,
        Boolean(dropdownOpenId),
        presence,
        { lock: true }
    );

    // A live face shows only while that line's chat window is open. Minimizing or
    // closing the window drops it, even if the row that opened it has scrolled away.
    const openCommentLineKey = useMemo(() => {
        const ids = [];
        (commentDock?.threads || []).forEach((thread) => {
            if (thread.minimized || thread.quoteId || thread.poLineId) return;
            if (thread.orderLineId == null || thread.orderLineId === '') return;
            ids.push(String(thread.orderLineId));
        });
        ids.sort();
        return ids.join('|');
    }, [commentDock?.threads]);

    useEffect(() => {
        const keys = (openCommentLineKey ? openCommentLineKey.split('|') : []).map((id) => notesCell(id));
        keys.forEach((key) => presenceFocus(key, { lock: false }));
        return () => {
            keys.forEach((key) => presenceBlur(key));
        };
    }, [openCommentLineKey, notesCell, presenceFocus, presenceBlur]);

    /** Wire one row's notes popup to presence: claim that cell while it is open. */
    const handleNotesVisibility = useCallback((lineId, isVisible) => {
        const id = String(lineId);
        setOpenNotesLineIds((prev) => {
            if (isVisible) {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
            }
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    /** Review / Ready / Confirm are one-click writes with no editing window, so they
     *  get no lock - just a broadcast so everyone else's toggle flips too. */
    const broadcastToggle = (field, lineId, value) => {
        presence.broadcastSave({
            cell: cellKey(PRESENCE_PAGE, 'line', lineId, field),
            kind: 'toggle',
            field,
            lineId: String(lineId),
            value,
        });
    };

    // Handle Review toggle change with API call
    const handleReviewToggle = async (lineId, orderNum, newValue) => {
        try {
            // Optimistic update
            setReviewToggles(prev => ({
                ...prev,
                [lineId]: newValue
            }));

            // Call backend API
            const result = await updateOrderReview(orderNum, newValue);
            
            if (result.success) {
                broadcastToggle('review', lineId, newValue);
                showToast(`Review status updated to ${newValue ? 'Reviewed' : 'Not Reviewed'}`, 'success');
            } else {
                // Revert optimistic update if API fails
                setReviewToggles(prev => ({
                    ...prev,
                    [lineId]: !newValue
                }));
                showToast(result.message, 'error');
            }
        } catch (error) {
            console.error('Error updating review:', error);
            // Revert optimistic update
            setReviewToggles(prev => ({
                ...prev,
                [lineId]: !newValue
            }));
            showToast('Error updating review status', 'error');
        }
    };

    // Handle Ready toggle change with API call
    const handleReadyToggle = async (lineId, orderNum, newValue) => {
        try {
            // Optimistic update
            setReadyToggles(prev => ({
                ...prev,
                [lineId]: newValue
            }));

            // Call backend API
            const result = await updateOrderReady(orderNum, newValue);
            
            if (result.success) {
                broadcastToggle('ready', lineId, newValue);
                showToast(`Ready status updated to ${newValue ? 'Ready' : 'Not Ready'}`, 'success');
            } else {
                // Revert optimistic update if API fails
                setReadyToggles(prev => ({
                    ...prev,
                    [lineId]: !newValue
                }));
                showToast(result.message, 'error');
            }
        } catch (error) {
            console.error('Error updating ready status:', error);
            // Revert optimistic update
            setReadyToggles(prev => ({
                ...prev,
                [lineId]: !newValue
            }));
            showToast('Error updating ready status', 'error');
        }
    };

    // Handle Confirm & Move toggle change with API call
    const handleConfirmToggle = async (lineId, orderNum, newValue) => {
        // Review AND Ready must both be on before an order can be confirmed.
        // Un-confirming is always allowed, so turning either one off afterwards
        // cannot strand a confirmed order with no way to undo it.
        if (newValue) {
            const missing = [];
            if (!reviewToggles[lineId]) missing.push('reviewed');
            if (!readyToggles[lineId]) missing.push('ready');
            if (missing.length) {
                showToast(
                    `Order must be marked ${missing.join(' and ')} before confirming and moving`,
                    'error'
                );
                return;
            }
        }

        try {
            // Optimistic update
            setConfirmCheckboxes(prev => ({
                ...prev,
                [lineId]: newValue
            }));

            // Call backend API
            const result = await updateOrderAccept(orderNum, newValue);
            
            if (result.success) {
                broadcastToggle('confirm', lineId, newValue);
                showToast(`Order ${newValue ? 'accepted' : 'unaccepted'} successfully`, 'success');
                // Play sound if confirming
                if (newValue) {
                    playConfirmSound();
                }
            } else {
                // Revert optimistic update if API fails
                setConfirmCheckboxes(prev => ({
                    ...prev,
                    [lineId]: !newValue
                }));
                showToast(result.message, 'error');
            }
        } catch (error) {
            console.error('Error updating accept status:', error);
            // Revert optimistic update
            setConfirmCheckboxes(prev => ({
                ...prev,
                [lineId]: !newValue
            }));
            showToast('Error updating order acceptance', 'error');
        }
    };

    const fetchData = async () => {
        if (!useEffectOrder || useEffectOrder === '' || useEffectOrder.length === 0) {
            setOrderSearch([]);
            setOrderValue(useEffectOrder);
            return;
        }
        const orders = await orderFetch('0', '10', useEffectOrder, sortConfig.key, sortConfig.direction, null);
        if (orders === null) {
            return;
        }
        // Ensure order is an array before using it
        const orderData = Array.isArray(orders.order) ? orders.order : [];
        setOrderSearch(orderData);
        setOrderValue(useEffectOrder);
    };

    const fetchPOData = async () => {
        if (!useEffectPO || useEffectPO === '' || useEffectPO.length === 0) {
            setPOSearch([]);
            setPOValue(useEffectPO);
            return;
        }
        const orders = await orderFetch('0', '10', useEffectPO, sortConfig.key, sortConfig.direction, null);
        if (orders === null) {
            return;
        }
        // Ensure order is an array before using it
        const orderData = Array.isArray(orders.order) ? orders.order : [];
        setPOSearch(orderData);
        setPOValue(useEffectPO);
    };

    const fetchShipmentMethodData = async () => {
        if (!useEffectShippingMethod || useEffectShippingMethod === '' || useEffectShippingMethod.length === 0) {
            setShippingMethodSearch([]);
            setShippingMethodValue(useEffectShippingMethod);
            return;
        }
        const orders = await getShipmentMethods(useEffectShippingMethod);
        setShippingMethodSearch(orders);
        setShippingMethodValue(useEffectShippingMethod);
    };

    const fetchCountryData = async () => {
        if (!useEffectCountry || useEffectCountry === '' || useEffectCountry.length === 0) {
            setCountrySearch([]);
            setCountryValue(useEffectCountry);
            return;
        }
        const orders = await getCountries(useEffectCountry);
        setCountrySearch(orders);
        setCountryValue(useEffectCountry);
    };

    const fetchCustomer = async () => {
        if (!useEffectCustomer || useEffectCustomer === '' || useEffectCustomer.length === 0) {
            setCustomerSearch([]);
            setCustomerValue(useEffectCustomer);
            return;
        }
        const orders = await getCustomer(useEffectCustomer);
        setCustomerSearch(orders);
        setCustomerValue(useEffectCustomer);
    };

    useEffect(() => {
        fetchData();
    }, [useEffectOrder]);

    useEffect(() => {
        fetchPOData();
    }, [useEffectPO]);

    useEffect(() => {
        fetchShipmentMethodData();
    }, [useEffectShippingMethod]);

    useEffect(() => {
        fetchCustomer();
    }, [useEffectCustomer]);

    useEffect(() => {
        fetchCountryData();
    }, [useEffectCountry]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? '▲' : '▼';
    };

    const mergedHeaderCells = useMemo(() => {
        const cells = {};
        if (activeGroupId === ORDER_SET_GROUP_ID) {
            cells[ORDER_SET_MERGED_ID] = (
                <th
                    className="text-center"
                    style={{ minWidth: '280px', maxWidth: '320px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={() => handleSort('ordernum')}
                >
                    Order Set {getSortIcon('ordernum')}
                </th>
            );
        }
        customGroups.forEach((group) => {
            const mergedId = getMergedIdForGroup(group.id);
            if (activeGroupId === group.id) {
                cells[mergedId] = (
                    <th className="text-center" style={{ minWidth: '240px', maxWidth: '300px' }}>
                        {group.name}
                    </th>
                );
            }
        });
        return cells;
    }, [activeGroupId, customGroups, sortConfig]);

    const getRowStyle = (date) => {
        if (!date) return {};
        
        // Parse the shipping date (format: yyyy-MM-dd)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        // Parse date string (yyyy-MM-dd format) to avoid timezone issues
        const dateParts = String(date).split('-');
        const targetDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        targetDate.setHours(0, 0, 0, 0); // Reset time to start of day
        
        const timeDiff = targetDate - today;
        const diffDays = Math.round(timeDiff / (1000 * 60 * 60 * 24));

        // Only apply colors for today and future dates (diffDays >= 0), not past dates
        if (diffDays < 0) {
            return {}; // Past dates - no color
        }

        // Red: Today (diffDays === 0) and tomorrow (diffDays === 1) - the 2 days before shipping date
        // If today is Jan 13, then Jan 13 (today) and Jan 14 (tomorrow) should show red
        if (diffDays === 0 || diffDays === 1) {
            return { style: { backgroundColor: '#f8d7da' } }; // Light red - today and tomorrow
        } else if (diffDays <= 7) {
            return { style: { backgroundColor: '#fff3cd' } }; // Light yellow - 1 week before shipping date (2-7 days)
        } else if (diffDays <= 30) {
            return { style: { backgroundColor: '#d1ecf1' } }; // Light cyan - 1 month before shipping date (8-30 days)
        }
        return {}; // More than 30 days - no color
    };

    // Helper function to check if an order has assembly parts (ASY or SOC)
    const hasAssemblyParts = (line) => {
        // Check if the main part is an assembly
        const mainPart = String(line.prcpart || '');
        if (mainPart.startsWith('ASY') || mainPart.startsWith('SOC')) {
            return true;
        }

        // Check BOM parts if they exist
        const bomParts = line?.bom?.bomParts || line?.bomParts || [];
        return bomParts.some(bomPart => {
            const partNumber = String(bomPart.prcpart || '');
            return partNumber.startsWith('ASY') || partNumber.startsWith('SOC');
        });
    };

    // Helper function to check if a date falls within the selected date range filter
    const isWithinDateRange = (dateString) => {
        if (!weekFilter && !monthFilter) {
            return true; // No date filter applied
        }

        if (!dateString) return false;

        const orderDate = new Date(dateString);
        if (isNaN(orderDate.getTime())) return false;

        const today = new Date();
        const targetDate = new Date(dateString);
        const timeDiff = targetDate - today;
        const diffDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        if (weekFilter) {
            return diffDays >= 0 && diffDays <= 7;
        }

        if (monthFilter) {
            return diffDays >= 0 && diffDays <= 30;
        }

        return true;
    };

    // Filter new orders data based on shipment date filters and assemblies filter
    const getFilteredNewOrdersData = () => {
        let filteredData = newOrdersData;
        
        // Filter out archived whole orders
        filteredData = filteredData.filter(line => !archivedOrderNums.has(String(line?.order?.ordernum || '')));

        // Apply search filter
        if (searchTerm) {
            filteredData = filteredData.filter(line => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    (line.order?.ordernum || '').toLowerCase().includes(searchLower) ||
                    (line.order?.customer?.name || '').toLowerCase().includes(searchLower) ||
                    (line.order?.custponum || '').toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply date range filter
        if (weekFilter || monthFilter) {
            filteredData = filteredData.filter(line => {
                const shipDate = getEarliestPromisedDateForOrder(line) || line.shipdate || line.orig_shipdate || '';
                return isWithinDateRange(shipDate);
            });
        }

        // Apply assemblies filter
        if (assembliesFilter) {
            filteredData = filteredData.filter(line => hasAssemblyParts(line));
        }

        if (kpiQuickFilter === 'due') {
            filteredData = filteredData.filter((line) => {
                const ds = getEarliestPromisedDateForOrder(line);
                if (!ds) return false;
                const orderDay = parseAsLocalCalendarDate(ds);
                if (!orderDay) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return orderDay.getTime() <= today.getTime();
            });
        } else if (kpiQuickFilter === 'missing') {
            const missingMap = buildOrderLineStatusReportMapFromDetails(orderDetailsData, excludedAvailabilityPartsByOrder);
            filteredData = filteredData.filter((line) => {
                const r = missingMap[String(line?.order?.ordernum)];
                return r && (r.missingPartsCount || 0) > 0;
            });
        } else if (kpiQuickFilter === 'ready') {
            filteredData = filteredData.filter((line) => isReadyToShipLine(line));
        } else if (kpiQuickFilter && typeof kpiQuickFilter === 'object' && kpiQuickFilter.kind === 'order') {
            filteredData = filteredData.filter((line) => lineHasOrderStatusLabel(line, kpiQuickFilter.label));
        } else if (kpiQuickFilter && typeof kpiQuickFilter === 'object' && kpiQuickFilter.kind === 'line') {
            filteredData = filteredData.filter((line) => lineHasLineStatusLabel(line, kpiQuickFilter.label));
        }

        return filteredData;
    };

    // Auto-refresh: silently reloads the Order Validation list with the current filters,
    // without the sync/permission flow used by the manual Refresh button.
    const handleAutoRefresh = async () => {
        await fetchNewOrders();
    };

    // Shared auto-refresh countdown (10 minutes) - same hook as the Orders page
    const { timerFormatted, restartTimer } = useAutoRefreshTimer(handleAutoRefresh, AUTO_REFRESH_INTERVAL_SECONDS);

    const handleManualPageRefresh = async () => {
        setIsManualRefreshing(true);
        try {
            await handleAutoRefresh();
            // Restart the countdown after manual refresh.
            restartTimer();
        } catch (error) {
            console.error('Manual refresh error:', error);
            throw error;
        } finally {
            setIsManualRefreshing(false);
        }
    };

    const handleRefreshOrders = async () => {
        const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');

        if (!userEmail) {
            alert('User session not found. Please login again.');
            return;
        }

        try {
            const result = await getUsersByEmail(userEmail);
            const hasAccess = result.success && result.multifactorEnabled;

            if (!hasAccess) {
                alert('You don\'t have access to refresh the orders');
                return;
            }
        } catch (error) {
            console.error('Error checking user permissions:', error);
            alert('Error checking permissions. Please try again.');
            return;
        }

        setIsRefreshing(true);
        try {
            await syncOrders({ userName: userEmail });
            await fetchNewOrders();
            setLastRefreshData({
                timestamp: new Date(),
                user: userEmail,
                success: true
            });
            // Data was just refreshed, so restart the auto-refresh countdown.
            restartTimer();
        } catch (error) {
            console.error('Refresh failed:', error);
            setLastRefreshData({
                timestamp: new Date(),
                user: userEmail,
                success: false,
                error: error.message
            });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setTempSearchTerm('');
        setCurrentPage(1);
        setOrderValue('');
        setPOValue('');
        setShippingMethodValue('');
        setCountryValue('');
        setCustomerValue('');
        setCustomerId('');
        setStatusValue('');
        setStartDate('');
        setEndDate('');
        setViewBy(false);
        setWeekFilter(false);
        setMonthFilter(false);
        setAssembliesFilter(false);
        setFilterTab(false);
        setKpiQuickFilter(null);
        clearOrdersFilterState(ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY);
        setSortConfig({ key: 'orig_shipdate', direction: 'ascending' });
        setItemsPerPage(-1);
        setExports(false);
        fetchNewOrders().catch(() => {});
    };

    // Play electric/boom sound effect when Confirm & Move checkbox is checked
    const playConfirmSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const duration = 0.3; // 300ms
            const sampleRate = audioContext.sampleRate;
            const numSamples = Math.floor(duration * sampleRate);
            const buffer = audioContext.createBuffer(1, numSamples, sampleRate);
            const data = buffer.getChannelData(0);

            // Create an electric/boom-like sound with multiple frequencies
            for (let i = 0; i < numSamples; i++) {
                const t = i / sampleRate;
                // Combine multiple frequencies for electric/boom effect
                const freq1 = 60; // Low boom frequency
                const freq2 = 120; // Mid frequency
                const freq3 = 240; // Higher electric frequency
                
                // Exponential decay envelope for boom effect
                const envelope = Math.exp(-t * 8);
                
                // Mix frequencies with different phases - increased amplitudes for louder sound
                const wave1 = Math.sin(2 * Math.PI * freq1 * t) * 0.65;
                const wave2 = Math.sin(2 * Math.PI * freq2 * t) * 0.55;
                const wave3 = Math.sin(2 * Math.PI * freq3 * t) * 0.45;
                
                // Add some noise for electric crackle effect - increased for more impact
                const noise = (Math.random() - 0.5) * 0.2;
                
                // Combine all elements
                data[i] = (wave1 + wave2 + wave3 + noise) * envelope;
            }

            const source = audioContext.createBufferSource();
            const gainNode = audioContext.createGain();
            
            // Set volume to 1.2 (120%) - extra loud for maximum impact
            gainNode.gain.value = 1.2;
            
            source.buffer = buffer;
            source.connect(gainNode);
            gainNode.connect(audioContext.destination);
            source.start(0);
        } catch (error) {
            console.error('Error playing sound:', error);
        }
    };

    const handleExport = () => {
        // Get the filtered data that matches what's displayed in the table
        const filteredData = getFilteredNewOrdersData();
        
        if (!filteredData || filteredData.length === 0) {
            alert('No data to export');
            return;
        }
        
        const headers = [
            ["Line Item", "Order Number", "Ordered On Date", "WO#", "Customer", "Customer PO", "Order Qty", "Status", "Ship Date", "Shipment Method", "Country"]
        ];

        // Extract table data from filtered results
        const rows = filteredData.map((line) => [
            line.negativeCountStr || line.lineitem || "N/A",
            line.order?.ordernum || "N/A",
            getOrderedOnDate(line),
            line.order?.woNum || "N/A",
            line.order?.customer?.name || "N/A",
            line.order?.custponum || "N/A",
            line.balancedue ?? "N/A",
            line.statusObj?.status || "N/A",
            getEarliestPromisedDateForOrder(line) || line.shipdate || line.orig_shipdate || "N/A",
            line.order?.shipmentMethod?.methodOfShipment || "N/A",
            line.order?.country?.country_Name || "N/A"
        ]);

        // Combine headers and rows
        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);

        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Order Validation");

        // Export the Excel file
        XLSX.writeFile(workbook, "order_validation.xlsx");
    };

    const showOrderPOP = async (line) => {
        const orderNumber = line?.order?.ordernum || line?.ordernum;
        if (!orderNumber) {
            return;
        }

        setSelectedSourceLine(line);

        const triggerUrl = line?.folderUrl?.trim();
        if (triggerUrl && line?.id) {
            setOrderAccessLinks((prev) => {
                if (prev[line.id]?.length > 0) {
                    return prev;
                }
                return {
                    ...prev,
                    [line.id]: [{
                        id: `trigger-${line.id}`,
                        url: triggerUrl,
                        dateAdded: new Date().toLocaleDateString(),
                    }],
                };
            });
        }

        const orders = await fetchOrderValidationData(0, 10, "", "lineitem", "ascending", null, "", "", "", "", "", "", "", "", "", true, true, "", orderNumber);
        if (orders === null) {
            return;
        }

        // Ensure order is an array before using it
        const orderData = stripArchivedBomParts(Array.isArray(orders.order) ? orders.order : []);
        setSelectedOrder(orderData);
        cacheOrderDetailsData(orderNumber, orderData);

        if (orderData.length > 0) {
            const newLinks = {};
            const timestamp = Date.now();
            const dateLabel = new Date().toLocaleDateString();

            orderData.forEach((orderLine) => {
                const url = orderLine.folderUrl?.trim();
                if (url) {
                    newLinks[orderLine.id] = [{
                        id: `${timestamp}-${orderLine.id}`,
                        url,
                        dateAdded: dateLabel
                    }];
                }
            });

            if (Object.keys(newLinks).length > 0) {
                setOrderAccessLinks(prev => ({ ...prev, ...newLinks }));
            }
        }

        setShowPopup(true);
    };

    const toggleInlineOrderDetails = async (line) => {
        const orderNum = line.order?.ordernum || line.ordernum;
        const isExpanded = expandedInlineOrders[orderNum];

        if (isExpanded) {
            setExpandedInlineOrders(prev => {
                const newState = { ...prev };
                delete newState[orderNum];
                return newState;
            });
        } else {
            if (!orderDetailsData[orderNum]) {
                const orders = await fetchOrderValidationData(0, 10, "", "lineitem", "ascending", null, "", "", "", "", "", "", "", "", "", true, true, "", orderNum);
                if (orders !== null) {
                    // Ensure order is an array before using it
                    const orderData = stripArchivedBomParts(Array.isArray(orders.order) ? orders.order : []);
                    setOrderDetailsData(prev => ({
                        ...prev,
                        [orderNum]: orderData
                    }));
                }
            }
            setExpandedInlineOrders(prev => ({
                ...prev,
                [orderNum]: true
            }));

            setInlineActiveView(prev => ({
                ...prev,
                [orderNum]: 'orderLines'
            }));
        }
    };

    const handleEyeIconClick = async (line) => {
        if (inlineDropdownMode) {
            await toggleInlineOrderDetails(line);
        } else {
            await showOrderPOP(line);
        }
    };

    const handleHistoryIconClick = (line) => {
        const orderNumber = line?.order?.ordernum || line?.ordernum;
        if (orderNumber) {
            setHistoryModalOrder(orderNumber);
        }
    };

    const getOrderFolderLink = useCallback((orderNum) => {
        const lines = orderDetailsData[orderNum];
        if (Array.isArray(lines)) {
            for (const orderLine of lines) {
                const linksForLine = orderAccessLinks?.[orderLine.id];
                if (Array.isArray(linksForLine) && linksForLine.length > 0) {
                    const latestLink = linksForLine[linksForLine.length - 1]?.url?.trim();
                    if (latestLink) {
                        return latestLink;
                    }
                }

                const fallbackUrl = orderLine.folderUrl?.trim();
                if (fallbackUrl) {
                    return fallbackUrl;
                }
            }
        }
        return null;
    }, [orderDetailsData, orderAccessLinks]);

    const resolveOrderFolderLinkForLine = useCallback((line) => {
        if (!line) return null;

        const orderNum = line?.order?.ordernum;
        const cachedLink = orderNum ? getOrderFolderLink(orderNum) : null;
        const rowLinks = orderAccessLinks?.[line.id];
        const rowLinkUrl = Array.isArray(rowLinks) && rowLinks.length > 0
            ? rowLinks[rowLinks.length - 1]?.url?.trim()
            : null;
        const namedUrls = line?.order?.orderUrls;
        const firstOrderUrl = Array.isArray(namedUrls) && namedUrls.length > 0
            ? namedUrls[0]?.url?.trim()
            : null;
        const fallbackUrl = typeof line.folderUrl === 'string' ? line.folderUrl.trim() : null;

        return cachedLink || rowLinkUrl || firstOrderUrl || fallbackUrl || null;
    }, [getOrderFolderLink, orderAccessLinks]);

    const renderOrderFolderIconForLine = useCallback((line, isOrangeRow) => {
        const folderLink = resolveOrderFolderLinkForLine(line);
        const folderTitle = folderLink ? `Open folder: ${folderLink}` : 'No folder link saved yet';
        return (
            <a
                href={folderLink || '#'}
                onClick={(event) => { if (!folderLink) event.preventDefault(); }}
                target={folderLink ? '_blank' : undefined}
                rel={folderLink ? 'noopener noreferrer' : undefined}
                style={{ cursor: folderLink ? 'pointer' : 'not-allowed', textDecoration: 'none' }}
                title={folderTitle}
            >
                <i
                    className="fa fa-folder"
                    aria-hidden="true"
                    style={{ fontSize: '16px', color: folderLink ? (isOrangeRow ? '#cc7a00' : '#ff8c00') : '#cccccc' }}
                />
            </a>
        );
    }, [resolveOrderFolderLinkForLine]);

    const getSimpleColumnLine = useCallback((colId, line, displayShipDate, isOrangeRow, isPoReviewRow = false) => {
        switch (colId) {
            case 'ordernum':
                return {
                    label: 'Order #',
                    show: !!(line.order?.ordernum || line.ordernum),
                    content: (
                        <span className="d-inline-flex align-items-center gap-2">
                            <a
                                href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.ordernum}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            >
                                {getCetecOrderNumber(line)}
                            </a>
                            {renderOrderFolderIconForLine(line, isOrangeRow)}
                        </span>
                    ),
                };
            case 'orderedondate':
                return {
                    label: 'Ordered On Date',
                    show: getOrderedOnDate(line) !== '-',
                    content: getOrderedOnDate(line),
                };
            case 'wo':
                return {
                    label: 'WO #',
                    show: !!(line.order?.woNum && line.order.woNum !== '-'),
                    content: (
                        <span className="d-inline-flex align-items-center justify-content-center gap-1">
                            <a
                                href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.woNum}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none' }}
                            >
                                {line.order?.woNum}
                            </a>
                            {line.order?.woNum && line.order.woNum !== '-' && line.order?.woUrl && (
                                <a
                                    href={line.order.woUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`Open WO folder: ${line.order.woUrl}`}
                                >
                                    <i
                                        className="fa fa-folder"
                                        aria-hidden="true"
                                        style={{ fontSize: '16px', color: isOrangeRow ? '#cc7a00' : '#ff8c00' }}
                                    />
                                </a>
                            )}
                        </span>
                    ),
                };
            case 'name':
                return {
                    label: 'Customer',
                    show: !!(line.order?.customer?.name),
                    content: line.order?.customer?.id ? (
                        <a
                            href={`https://gigaflightinc.cetecerp.com/customer/${line.order.customer.id}/view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            {line.order.customer.name}
                        </a>
                    ) : (line.order?.customer?.name || ''),
                };
            case 'custponum':
                return { label: 'PO #', content: line.order?.custponum, show: !!(line.order?.custponum) };
            case 'shipdate':
                return { label: 'Ship Date', content: displayShipDate || 'N/A', show: true };
            case 'lineitem':
                return {
                    label: 'Line Item',
                    content: line.negativeCountStr,
                    show: !!(line.negativeCountStr),
                    valueStyle: isPoReviewRow ? undefined : getLineItemMismatchStyle(line.negativeCountStr),
                };
            case 'shippingmethod': {
                const methodText = line.order?.shipmentMethod?.methodOfShipment;
                return {
                    label: 'Shipping Method',
                    content: renderShippingMethodCell(methodText),
                    show: !!(methodText),
                };
            }
            case 'country': {
                const countryName = line.order?.country?.country_Name;
                const isIntl = countryName && countryName !== 'United States';
                return {
                    label: 'Country',
                    content: countryName,
                    show: !!countryName,
                    valueClassName: (!isPoReviewRow && isIntl) ? 'bg-dark text-light px-1 rounded country-cell-intl' : ((!isPoReviewRow && countryName === 'United States') ? 'px-1 rounded country-cell-us' : ''),
                };
            }
            default:
                return null;
        }
    }, [renderOrderFolderIconForLine, renderShippingMethodCell]);

    const renderOrderSetBodyCell = useCallback((line, isOrangeRow) => (
        <td className="text-center orders-column-group-td" style={{ minWidth: '280px', maxWidth: '320px' }}>
            <CombinedColumnCell
                lines={[
                    getSimpleColumnLine('ordernum', line, null, isOrangeRow),
                    getSimpleColumnLine('wo', line, null, isOrangeRow),
                    getSimpleColumnLine('name', line, null, isOrangeRow),
                    getSimpleColumnLine('custponum', line, null, isOrangeRow),
                ].filter(Boolean)}
            />
        </td>
    ), [getSimpleColumnLine]);

    const renderCustomGroupBodyCell = useCallback((line, mergedId, displayShipDate, isOrangeRow, isPoReviewRow = false) => {
        const groupId = mergedId.replace(/^group-/, '');
        const group = customGroups.find((g) => g.id === groupId);
        const columnIds = group?.columnIds || [];
        const lines = columnIds
            .map((colId) => getSimpleColumnLine(colId, line, displayShipDate, isOrangeRow, isPoReviewRow))
            .filter(Boolean);
        return (
            <td className="text-center orders-column-group-td" style={{ minWidth: '240px', maxWidth: '300px' }}>
                <CombinedColumnCell lines={lines} />
            </td>
        );
    }, [customGroups, getSimpleColumnLine]);

    const getMergedBodyCells = useCallback((line, displayShipDate, isOrangeRow, isPoReviewRow = false) => {
        const cells = {};
        if (activeGroupId === ORDER_SET_GROUP_ID) {
            cells[ORDER_SET_MERGED_ID] = renderOrderSetBodyCell(line, isOrangeRow);
        }
        customGroups.forEach((group) => {
            if (activeGroupId === group.id) {
                cells[getMergedIdForGroup(group.id)] = renderCustomGroupBodyCell(
                    line,
                    getMergedIdForGroup(group.id),
                    displayShipDate,
                    isOrangeRow,
                    isPoReviewRow
                );
            }
        });
        return cells;
    }, [activeGroupId, customGroups, renderOrderSetBodyCell, renderCustomGroupBodyCell]);

    const renderInlineOrderHeader = useCallback((line, cachedOrderFolderLink) => {
        const inlineFolderLink = cachedOrderFolderLink || null;
        const rowLinks = orderAccessLinks?.[line.id];
        const rowLinkUrl = Array.isArray(rowLinks) && rowLinks.length > 0
            ? rowLinks[rowLinks.length - 1]?.url?.trim()
            : null;
        const fallbackUrl = typeof line.folderUrl === 'string' ? line.folderUrl.trim() : null;
        const folderLink = inlineFolderLink || rowLinkUrl || fallbackUrl || null;
        const folderTitle = folderLink ? `Open folder: ${folderLink}` : 'No folder link saved yet';

        return (
            <div className="col-12 d-flex align-items-center justify-content-between">
                <div><b>Order Num</b>: {line.order.ordernum}</div>
                <div><b>PO Num</b>: {line.order.custponum}</div>
                <a
                    href={folderLink || '#'}
                    onClick={(event) => {
                        if (!folderLink) {
                            event.preventDefault();
                        }
                    }}
                    target={folderLink ? '_blank' : undefined}
                    rel={folderLink ? 'noreferrer' : undefined}
                    className={`btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 ${folderLink ? 'text-primary' : 'text-muted'}`}
                    style={{ color: folderLink ? '#0078d4' : '#6c757d', cursor: folderLink ? 'pointer' : 'not-allowed' }}
                    title={folderTitle}
                >
                    <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: 'orange' }}></i>
                    <span style={{ fontSize: '14px' }}>Order Folder</span>
                </a>
                <div><b>Customer Name</b>: {line.order.customer?.name || 'N/A'}</div>
            </div>
        );
    }, [orderAccessLinks]);

    const handleInlineViewChange = (orderNum, view) => {
        setInlineActiveView(prev => ({
            ...prev,
            [orderNum]: view
        }));
    };

    const toggleInlineBomRow = (orderNum, lineIndex) => {
        const key = `${orderNum}-${lineIndex}`;
        setExpandedInlineBomRows(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleInlineSwitchView = (orderNum) => {
        const currentView = inlineActiveView[orderNum] || 'orderLines';
        const newView = currentView === 'orderLines' ? 'bomParts' : 'orderLines';
        setInlineActiveView(prev => ({
            ...prev,
            [orderNum]: newView
        }));
    };

    const handleInlineDualView = (orderNum) => {
        setInlineActiveView(prev => ({
            ...prev,
            [orderNum]: 'both'
        }));
    };

    const lineRowHasTriggerTags = (row, triggerTags = []) => {
        if (!row) return false;

        const matchesLabel = (raw) => {
            const label = typeof raw === 'string' ? raw : raw?.label || raw;
            return triggerTags.some((tag) => normalizeLineStatusKey(label) === normalizeLineStatusKey(tag));
        };

        if ((submittedLineStatuses[row.id] || submittedLineStatuses[String(row.id)] || []).some(matchesLabel)) return true;
        if ((lineStatusSelections[row.id] || lineStatusSelections[String(row.id)] || []).some((tag) => triggerTags.includes(tag.label))) return true;

        const lineStatuses = row.lineStatuses || [];
        if (lineStatuses.some(matchesLabel)) return true;

        if (row.lineItemStatus) {
            try {
                const parsedStatuses = JSON.parse(row.lineItemStatus);
                if (Array.isArray(parsedStatuses) && parsedStatuses.some(matchesLabel)) {
                    return true;
                }
            } catch (e) {
                // Ignore parse errors
            }
        }

        if (row.orderReport && Array.isArray(row.orderReport)) {
            if (row.orderReport.some((report) => matchesLabel(report?.status))) {
                return true;
            }
        }

        return false;
    };

    const orderHasAnyLineStatusTag = (line, triggerTags = []) => {
        const ordernum = line?.order?.ordernum;
        if (!ordernum) return false;

        if (lineRowHasTriggerTags(line, triggerTags)) return true;

        const inlineLines = orderDetailsData?.[ordernum];
        const linesForOrder = Array.isArray(inlineLines) && inlineLines.length > 0
            ? inlineLines
            : newOrdersData.filter(l => l?.order?.ordernum === ordernum);

        if (!linesForOrder.length) return false;

        return linesForOrder.some((l) => lineRowHasTriggerTags(l, triggerTags));
    };

    // Helper to determine if row should be highlighted brown (RMA or Waiting On Components)
    const shouldRowBeBrown = (line) =>
        orderHasAnyLineStatusTag(line, ['RMA', 'Waiting On Components', 'Waiting for Components']);

    const shouldRowBeOnHoldCart = (line) =>
        orderHasAnyLineStatusTag(line, ['On Hold Cart']);

    const shouldRowBeStaggingCart = (line) =>
        orderHasAnyLineStatusTag(line, ['Staging Cart']);

    const getLineStatusReportHighlight = (line) => {
        if (orderHasAnyLineStatusTag(line, ['ENG Review'])) {
            return { backgroundColor: '#625C5C', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (shouldRowBeStaggingCart(line)) {
            return { backgroundColor: '#1DA1DB', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (shouldRowBeOnHoldCart(line)) {
            return { backgroundColor: '#C56A00', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (shouldRowBeBrown(line)) {
            return { backgroundColor: '#A85A58', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        return { backgroundColor: 'transparent', textColor: 'inherit', secondaryTextColor: '#6c757d' };
    };

    // These line-status colors stay on the report cell even when the row has another highlight
    // (purple CC/Wire+PPA, Hot Order, PO Review, shipped green, or ship-date colors).
    const getPriorityLineStatusHighlight = (line) => {
        if (orderHasAnyLineStatusTag(line, ['ENG Review'])) {
            return { backgroundColor: '#625C5C', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (shouldRowBeStaggingCart(line)) {
            return { backgroundColor: '#1DA1DB', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (shouldRowBeOnHoldCart(line)) {
            return { backgroundColor: '#C56A00', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        if (orderHasAnyLineStatusTag(line, ['Waiting On Components', 'Waiting for Components'])) {
            return { backgroundColor: '#A85A58', textColor: 'white', secondaryTextColor: 'rgba(255, 255, 255, 0.9)' };
        }
        return null;
    };

    const isHotOrderOrAogLabel = (raw) => {
        const s = String(typeof raw === 'string' ? raw : raw?.label || '').toLowerCase().trim();
        return s === 'hot order' || s === 'hot order/aog' || s === 'aog';
    };

    const lineHasHotOrderStatus = (l) => {
        if (!l?.id) return false;
        const hasTag = (arr) => Array.isArray(arr) && arr.some((tag) => isHotOrderOrAogLabel(tag));
        return hasTag(submittedOrderStatuses[l.id])
            || hasTag(submittedOrderStatuses[String(l.id)])
            || hasTag(orderStatusSelections[l.id])
            || hasTag(orderStatusSelections[String(l.id)]);
    };

    // Entire order turns orange when Hot Order or AOG is selected (pending or submitted).
    const shouldRowBeOrange = (line) => {
        if (lineHasHotOrderStatus(line)) return true;
        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return false;

        const seen = new Set();
        const check = (ln) => {
            if (!ln?.id || seen.has(ln.id)) return false;
            seen.add(ln.id);
            return lineHasHotOrderStatus(ln);
        };

        const detailLines =
            orderDetailsData?.[orderNum] ??
            orderDetailsData?.[String(orderNum)] ??
            orderDetailsData?.[Number(orderNum)];
        if (Array.isArray(detailLines) && detailLines.some(check)) return true;

        return (newOrdersData || []).some(
            (ln) => String(ln?.order?.ordernum || '') === orderNum && check(ln)
        );
    };

    const lineHasCcHoldAndPpa = (l) => {
        if (!l?.id) return false;
        const labels = new Set();
        const add = (arr) => {
            if (!Array.isArray(arr)) return;
            arr.forEach((tag) => {
                const s = String(typeof tag === 'string' ? tag : tag?.label || '').toLowerCase().trim();
                if (s) labels.add(s);
            });
        };
        add(submittedOrderStatuses[l.id]);
        add(submittedOrderStatuses[String(l.id)]);
        add(orderStatusSelections[l.id]);
        add(orderStatusSelections[String(l.id)]);
        const hasPpa = labels.has('ppa');
        const hasCcOrWireHold = labels.has('cc hold') || labels.has('wire hold');
        return hasPpa && hasCcOrWireHold;
    };

    // Entire order turns purple when PPA is selected together with CC Hold or Wire Hold.
    // Paid on the order removes that purple highlight.
    const shouldRowBePurple = (line) => {
        if (orderHasOrderStatusPaid(line)) return false;
        if (lineHasCcHoldAndPpa(line)) return true;
        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return false;

        const seen = new Set();
        const check = (ln) => {
            if (!ln?.id || seen.has(ln.id)) return false;
            seen.add(ln.id);
            return lineHasCcHoldAndPpa(ln);
        };

        const detailLines =
            orderDetailsData?.[orderNum] ??
            orderDetailsData?.[String(orderNum)] ??
            orderDetailsData?.[Number(orderNum)];
        if (Array.isArray(detailLines) && detailLines.some(check)) return true;

        return (newOrdersData || []).some(
            (ln) => String(ln?.order?.ordernum || '') === orderNum && check(ln)
        );
    };

    const isPoReviewStatusLabel = (label) =>
        String(label || '').toLowerCase().trim() === 'po review';

    const lineHasPoReviewStatus = (l) => {
        if (!l?.id) return false;
        const hasTag = (arr) =>
            Array.isArray(arr) && arr.some((tag) => isPoReviewStatusLabel(typeof tag === 'string' ? tag : tag?.label));
        return hasTag(submittedOrderStatuses[l.id])
            || hasTag(submittedOrderStatuses[String(l.id)])
            || hasTag(orderStatusSelections[l.id])
            || hasTag(orderStatusSelections[String(l.id)]);
    };

    const shouldRowBePoReview = (line) => {
        if (lineHasPoReviewStatus(line)) return true;

        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return false;

        const seen = new Set();
        const check = (ln) => {
            if (!ln?.id || seen.has(ln.id)) return false;
            seen.add(ln.id);
            return lineHasPoReviewStatus(ln);
        };

        const detailLines =
            orderDetailsData?.[orderNum] ??
            orderDetailsData?.[String(orderNum)] ??
            orderDetailsData?.[Number(orderNum)];
        if (Array.isArray(detailLines) && detailLines.some(check)) return true;

        return (newOrdersData || []).some(
            (ln) => String(ln?.order?.ordernum || '') === orderNum && check(ln)
        );
    };

    const isInShippingOrShippedLabel = (raw) => {
        const s = String(typeof raw === 'string' ? raw : raw?.label || '').trim().toLowerCase();
        return s === 'in shipping' || s === 'ready to ship' || s === 'shipped';
    };

    const getOrderDetailLineById = (l) => {
        const on = l?.order?.ordernum;
        const arr = on && orderDetailsData[on];
        if (!Array.isArray(arr) || !l?.id) return null;
        return arr.find((d) => String(d.id) === String(l.id)) || null;
    };

    const lineHasInShippingOrShippedOnRow = (row) => {
        if (!row) return false;

        const reports = Array.isArray(row.orderReport) ? row.orderReport : [];
        if (reports.some((r) => isInShippingOrShippedLabel(r?.status))) return true;

        if ((row.lineStatuses || []).some((x) => isInShippingOrShippedLabel(typeof x === 'string' ? x : x?.label))) {
            return true;
        }

        if (row.lineItemStatus) {
            try {
                const parsed = JSON.parse(row.lineItemStatus);
                if (Array.isArray(parsed) && parsed.some((t) => isInShippingOrShippedLabel(typeof t === 'string' ? t : t?.label))) {
                    return true;
                }
            } catch {
                /* ignore */
            }
        }

        return false;
    };

    const lineHasInShippingOrShippedStatus = (line) => {
        if (!line?.id) return false;

        const reportRowsShowInShipping = (rows) =>
            Array.isArray(rows) &&
            rows.some((r) => {
                const status = r?.status ? String(r.status).toLowerCase() : '';
                return status === 'in shipping' || status === 'ready to ship' || status === 'shipped';
            });

        const checkLineObj = (ln) => {
            if (!ln?.id) return false;
            if (lineHasInShippingOrShippedOnRow(ln)) return true;
            if (reportRowsShowInShipping(getMergedLineStatusReportRows(ln))) return true;
            if ((submittedLineStatuses[ln.id] || []).some((t) => isInShippingOrShippedLabel(t))) return true;
            if ((lineStatusSelections[ln.id] || []).some((t) => isInShippingOrShippedLabel(t))) return true;
            return false;
        };

        if (checkLineObj(line)) return true;

        const detail = getOrderDetailLineById(line);
        if (detail && checkLineObj(detail)) return true;

        const orderNum = String(line?.order?.ordernum || '');
        if (orderNum) {
            const detailLinesForOrder = orderDetailsData?.[orderNum];
            if (Array.isArray(detailLinesForOrder) && detailLinesForOrder.some(checkLineObj)) {
                return true;
            }

            const siblingListRows = (newOrdersData || []).filter(
                (ln) => String(ln?.order?.ordernum || '') === orderNum
            );
            if (siblingListRows.some(checkLineObj)) return true;
        }

        if (reportRowsShowInShipping(getDisplayLineStatusReportRows(line))) return true;

        return false;
    };

    const lineHasOrderStatusHold = (l) => {
        if (!l?.id) return false;
        const holdStatuses = ['cc hold', 'wire hold', 'credit hold', 'ppa'];
        return (
            (submittedOrderStatuses[l.id] || []).some((tag) => holdStatuses.includes(String(tag?.label || '').toLowerCase())) ||
            (orderStatusSelections[l.id] || []).some((tag) => holdStatuses.includes(String(tag?.label || '').toLowerCase()))
        );
    };

    const lineHasOrderStatusPaid = (l) => {
        if (!l?.id) return false;
        const hasPaid = (arr) =>
            Array.isArray(arr) && arr.some((tag) => {
                const s = String(typeof tag === 'string' ? tag : tag?.label || '').trim().toLowerCase();
                return s === 'paid';
            });
        return hasPaid(submittedOrderStatuses[l.id])
            || hasPaid(submittedOrderStatuses[String(l.id)])
            || hasPaid(orderStatusSelections[l.id])
            || hasPaid(orderStatusSelections[String(l.id)]);
    };

    const orderHasOrderStatusPaid = (line) => {
        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return lineHasOrderStatusPaid(line);

        const seen = new Set();
        const check = (ln) => {
            if (!ln?.id || seen.has(ln.id)) return false;
            seen.add(ln.id);
            return lineHasOrderStatusPaid(ln);
        };

        if (check(line)) return true;

        const detailLines = orderDetailsData?.[orderNum];
        if (Array.isArray(detailLines) && detailLines.some(check)) return true;

        return newOrdersData.some(
            (ln) => String(ln?.order?.ordernum || '') === orderNum && check(ln)
        );
    };

    const orderHasOrderStatusHold = (line) => {
        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return lineHasOrderStatusHold(line);

        const seen = new Set();
        const check = (ln) => {
            if (!ln?.id || seen.has(ln.id)) return false;
            seen.add(ln.id);
            return lineHasOrderStatusHold(ln);
        };

        if (check(line)) return true;

        const detailLines = orderDetailsData?.[orderNum];
        if (Array.isArray(detailLines) && detailLines.some(check)) return true;

        return newOrdersData.some(
            (ln) => String(ln?.order?.ordernum || '') === orderNum && check(ln)
        );
    };

    /** Cart tags that mean ready-to-ship (In Shipping KPI + green-eligible like Shipped). */
    const hasReadyToShipCartTag = (line) => {
        if (lineHasLineStatusLabel(line, 'Staging Cart')) return true;
        if (lineHasLineStatusLabel(line, 'On Hold Cart')) return true;
        const stagingKey = normalizeLineStatusKey('Staging Cart');
        const onHoldCartKey = normalizeLineStatusKey('On Hold Cart');
        return getDisplayLineStatusReportRows(line).some((r) => {
            const key = normalizeLineStatusKey(r?.status);
            return key === stagingKey || key === onHoldCartKey;
        });
    };

    /** In shipping KPI / filter: in shipping path or Staging/On Hold Cart (holds do not block KPI). */
    const isReadyToShipLine = (line) => {
        if (hasReadyToShipCartTag(line)) return true;

        const ordernum = line?.order?.ordernum;
        if (!ordernum) return false;

        if (lineHasInShippingOrShippedStatus(line)) return true;

        const inlineLines = orderDetailsData?.[ordernum];
        const linesForOrder = Array.isArray(inlineLines) && inlineLines.length > 0
            ? inlineLines
            : newOrdersData.filter((ln) => ln?.order?.ordernum === ordernum);

        if (!linesForOrder.length) return false;

        return linesForOrder.every((ln) => {
            const reports = getMergedLineStatusReportRows(ln);

            return reports.some((r) => {
                const status = r?.status ? String(r.status).toLowerCase() : '';
                const count = parseInt(r?.count, 10) || 0;
                const totalCount = parseInt(r?.totalCount, 10) || 0;

                return (status === 'shipped' || status === 'in shipping' || status === 'ready to ship') && count === totalCount && totalCount > 0;
            });
        });
    };

    /** Light green row: Shipped / cart tags green unless unpaid hold; Paid allows green with holds. */
    const shouldRowBeGreen = (line) => {
        if (orderHasOrderStatusHold(line) && !orderHasOrderStatusPaid(line)) return false;
        return isReadyToShipLine(line);
    };

    const normLineStatusLabel = (x) => {
        if (x == null) return '';
        if (typeof x === 'string') return x.trim();
        return String(x.label ?? x.name ?? x).trim();
    };

    const lineHasLineStatusLabel = (line, wantLabel) => {
        const w = String(wantLabel || '').trim().toLowerCase();
        if (!w || !line?.id) return false;

        const checkArr = (arr) =>
            Array.isArray(arr) && arr.some((x) => normalizeLineStatusKey(normLineStatusLabel(x)) === normalizeLineStatusKey(w));

        const checkLineObj = (ln) => {
            if (!ln) return false;
            if (checkArr(ln.lineStatuses)) return true;
            if (checkArr(submittedLineStatuses[ln.id] || submittedLineStatuses[String(ln.id)])) return true;
            if (checkArr(lineStatusSelections[ln.id] || lineStatusSelections[String(ln.id)])) return true;
            if (ln.lineItemStatus) {
                try {
                    const parsed = JSON.parse(ln.lineItemStatus);
                    if (checkArr(parsed)) return true;
                } catch {
                    /* ignore */
                }
            }
            return false;
        };

        if (checkLineObj(line)) return true;
        const detail = getOrderDetailLineById(line);
        if (detail && checkLineObj(detail)) return true;

        const orderNum = String(line?.order?.ordernum || '');
        if (!orderNum) return false;

        const detailLinesForOrder = orderDetailsData?.[orderNum];
        if (Array.isArray(detailLinesForOrder) && detailLinesForOrder.some((ln) => checkLineObj(ln))) {
            return true;
        }

        const siblingListRows = (newOrdersData || []).filter(
            (ln) => String(ln?.order?.ordernum || '') === orderNum
        );
        if (siblingListRows.some((ln) => checkLineObj(ln))) {
            return true;
        }

        return false;
    };

    const isStaggingCartLine = (line) => {
        if (lineHasLineStatusLabel(line, 'Staging Cart')) return true;
        if (shouldRowBeStaggingCart(line)) return true;

        const stagingKey = normalizeLineStatusKey('Staging Cart');
        return getDisplayLineStatusReportRows(line).some(
            (r) => normalizeLineStatusKey(r?.status) === stagingKey
        );
    };

    const lineHasOrderStatusLabel = (line, wantLabel) => {
        const w = String(wantLabel || '').trim().toLowerCase();
        if (!w || !line?.id) return false;
        const checkArr = (arr) =>
            Array.isArray(arr) && arr.some((t) => String(t?.label ?? '').trim().toLowerCase() === w);
        return checkArr(submittedOrderStatuses[line.id]) || checkArr(orderStatusSelections[line.id]);
    };

    const cacheOrderDetailsData = useCallback((orderNumber, orderLines) => {
        if (!orderNumber || !Array.isArray(orderLines) || orderLines.length === 0) return;
        const orderKey = String(orderNumber);
        setOrderDetailsData((prev) => ({
            ...prev,
            [orderKey]: orderLines,
            [orderNumber]: orderLines,
        }));
    }, []);

    const handleToggleExcludedAvailabilityPart = useCallback((orderNum, partNumber, shouldExclude) => {
        if (!orderNum || !partNumber) return;
        setExcludedAvailabilityPartsByOrder((prev) => {
            const orderKey = String(orderNum);
            const prevOrderMap = prev[orderKey] || {};
            const nextOrderMap = { ...prevOrderMap };

            if (shouldExclude) {
                nextOrderMap[partNumber] = true;
            } else {
                delete nextOrderMap[partNumber];
            }

            if (Object.keys(nextOrderMap).length === 0) {
                const { [orderKey]: removedOrder, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [orderKey]: nextOrderMap,
            };
        });
    }, []);

    const closePopup = () => {
        setShowPopup(false);
        setSelectedSourceLine(null);
    };

    const resolveAddedOrderUrlItem = (response, url, urlName) => {
        const payload = (response && typeof response === 'object' && response.data && typeof response.data === 'object' && !Array.isArray(response.data))
            ? response.data
            : response;
        return {
            id: payload?.id ?? payload?.urlId ?? Date.now(),
            url: payload?.url || url,
            urlName: payload?.urlName || urlName,
        };
    };

    const patchOrderUrlsForOrder = (ordernum, updater) => {
        const patchList = (list) => {
            if (!Array.isArray(list)) return list;
            return list.map((line) => {
                if (String(line?.order?.ordernum) !== String(ordernum)) return line;
                const current = Array.isArray(line.order?.orderUrls) ? line.order.orderUrls : [];
                return {
                    ...line,
                    order: {
                        ...line.order,
                        orderUrls: updater(current),
                    },
                };
            });
        };
        setNewOrdersData((prev) => patchList(prev));
        setSelectedOrder((prev) => patchList(prev));
        setSelectedSourceLine((prev) => {
            if (!prev || String(prev?.order?.ordernum) !== String(ordernum)) return prev;
            const current = Array.isArray(prev.order?.orderUrls) ? prev.order.orderUrls : [];
            return {
                ...prev,
                order: {
                    ...prev.order,
                    orderUrls: updater(current),
                },
            };
        });
        setSelectedOrderForAccess((prev) => {
            if (!prev || String(prev?.order?.ordernum) !== String(ordernum)) return prev;
            const current = Array.isArray(prev.order?.orderUrls) ? prev.order.orderUrls : [];
            return {
                ...prev,
                order: {
                    ...prev.order,
                    orderUrls: updater(current),
                },
            };
        });
    };

    // Order Access functions
    const handleOrderAccessClick = (line) => {
        setSelectedOrderForAccess(line);
        setShowOrderAccessModal(true);
        setNewLinkUrl('');
        setNewLinkUrlName('');
    };

    const handleAddOrderAccessLink = async () => {
        if (!newLinkUrlName.trim()) {
            alert('Please enter a URL name');
            return;
        }

        if (!newLinkUrl.trim()) {
            alert('Please enter a URL');
            return;
        }

        try {
            new URL(newLinkUrl);
        } catch {
            alert('Please enter a valid URL (include http:// or https://)');
            return;
        }

        try {
            const ordernum = selectedOrderForAccess?.order?.ordernum
                || selectedOrderForAccess?.ordernum
                || '';
            const urlName = newLinkUrlName.trim();
            const changedBy = localStorage.getItem('userEmail')
                || localStorage.getItem('email')
                || localStorage.getItem('userName')
                || '';
            const requestData = {
                action: 'add',
                ordernum,
                url: newLinkUrl.trim(),
                urlName,
                orderLineId: selectedOrderForAccess.id,
                lastEditedBy: changedBy,
            };

            const response = await manageOrderUrl(requestData);

            if (response) {
                const existingUrls = selectedOrderForAccess?.order?.orderUrls;
                const previousUrlName = Array.isArray(existingUrls) && existingUrls.length > 0
                    ? String(existingUrls[existingUrls.length - 1]?.urlName || '').trim()
                    : '';
                recordOrderAccessHistoryHint({
                    ordernum,
                    action: 'add',
                    urlName,
                    previousUrlName,
                    changedBy,
                    orderLineId: selectedOrderForAccess.id,
                });
                setNewLinkUrl('');
                setNewLinkUrlName('');
                notifyOrderHistoryChanged(ordernum);
                const addedItem = resolveAddedOrderUrlItem(response, newLinkUrl.trim(), urlName);
                patchOrderUrlsForOrder(ordernum, (urls) => {
                    if (urls.some((item) => String(item.id) === String(addedItem.id))) return urls;
                    return [...urls, addedItem];
                });
                alert('URL added successfully');
                await fetchNewOrders();
            } else {
                alert('Failed to save URL. Please try again.');
            }
        } catch (error) {
            console.error('Error saving URL:', error);
            alert('Error saving URL. Please try again.');
        }
    };

    const handleDeleteOrderAccessLink = async (lineId, linkId, ordernum = null, urlName = '') => {
        try {
            const order = ordernum || selectedOrderForAccess?.order?.ordernum || selectedOrderForAccess?.ordernum;

            if (!order) {
                alert('Order number not found. Please try again.');
                return;
            }

            const changedBy = localStorage.getItem('userEmail')
                || localStorage.getItem('email')
                || localStorage.getItem('userName')
                || '';

            const requestData = {
                action: 'delete',
                ordernum: order,
                id: linkId,
                orderLineId: lineId,
                lastEditedBy: changedBy,
            };

            const response = await manageOrderUrl(requestData);

            if (response) {
                recordOrderAccessHistoryHint({
                    ordernum: order,
                    action: 'delete',
                    urlName: String(urlName || '').trim(),
                    previousUrlName: String(urlName || '').trim(),
                    changedBy,
                    orderLineId: lineId,
                });
                notifyOrderHistoryChanged(order);
                patchOrderUrlsForOrder(order, (urls) => urls.filter((item) => String(item.id) !== String(linkId)));
                alert('URL deleted successfully');
                await fetchNewOrders();
            } else {
                alert('Failed to delete URL. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting URL:', error);
            alert('Error deleting URL. Please try again.');
        }
    };

    const handleCloseOrderAccessModal = () => {
        setShowOrderAccessModal(false);
        setSelectedOrderForAccess(null);
        setNewLinkUrl('');
        setNewLinkUrlName('');
        setCopiedField(null);
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = null;
        }
    };

    // Archive functions
    const handleArchiveOrder = async (line) => {
        const orderNumber = line?.order?.ordernum;
        if (!orderNumber) {
            console.error('Missing order number for archive');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to archive order ${orderNumber}?`);
        if (!confirmed) {
            return;
        }

        try {
            const result = await updateArchiveOrder(orderNumber, true);
            if (result?.success) {
                // Keep local archive state in sync for filtering/UI until data reloads
                setArchivedOrderNums(prev => {
                    const newSet = new Set(prev);
                    newSet.add(String(orderNumber));
                    return newSet;
                });

                setArchivedOrdersData(prev => {
                    const existingIndex = prev.findIndex(archived => archived.id === line.id);
                    if (existingIndex === -1) {
                        return [...prev, line];
                    }
                    const updated = [...prev];
                    updated[existingIndex] = line;
                    return updated;
                });

                // Refresh the orders data
                await fetchNewOrders();
            } else {
                alert(result?.message || 'Failed to archive order.');
            }
        } catch (err) {
            console.error('Error archiving order:', err);
            alert('Error archiving order. Please try again.');
        }
    };

    const handleArchiveOrderLineItem = async (line) => {
        const orderLineItemId = line?.id;
        const orderNumber = line?.order?.ordernum || line?.ordernum;

        if (!orderLineItemId) {
            console.error('Missing order line item ID for archive');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to archive order ${orderNumber} line ${line?.lineitem || ''}?`);
        if (!confirmed) {
            return;
        }

        try {
            const result = await updateArchiveOrderLineItem(orderLineItemId, true);
            if (result?.success) {
                setArchivedOrderIds(prev => {
                    const newSet = new Set(prev);
                    newSet.add(orderLineItemId);
                    return newSet;
                });

                setArchivedOrdersData(prev => {
                    const existingIndex = prev.findIndex(archived => archived.id === orderLineItemId);
                    if (existingIndex === -1) {
                        return [...prev, line];
                    }
                    const updated = [...prev];
                    updated[existingIndex] = line;
                    return updated;
                });

                await fetchNewOrders();
            } else {
                alert(result?.message || 'Failed to archive order line item.');
            }
        } catch (err) {
            console.error('Error archiving order line item:', err);
            alert('Error archiving order line item. Please try again.');
        }
    };

    const handleArchiveBomPart = async (bomPartId, archived, orderNum) => {
        if (!bomPartId) {
            console.error('Missing BOM part ID for archive');
            return;
        }

        const action = archived ? 'archive' : 'unarchive';
        const confirmed = window.confirm(`Are you sure you want to ${action} this BOM part line?`);
        if (!confirmed) {
            return;
        }

        try {
            const result = await updateArchiveBomPart(bomPartId, archived);
            if (result?.success) {
                const applyArchiveToLines = (lines) => {
                    if (!Array.isArray(lines)) return lines;
                    return lines.map((item) => {
                        const bomParts = item?.bom?.bomParts;
                        if (!Array.isArray(bomParts)) return item;
                        let changed = false;
                        const nextBomParts = bomParts.map((bom) => {
                            if (bom?.id === bomPartId) {
                                changed = true;
                                return { ...bom, archived };
                            }
                            return bom;
                        });
                        if (!changed) return item;
                        return { ...item, bom: { ...item.bom, bomParts: nextBomParts } };
                    });
                };

                if (orderNum) {
                    setOrderDetailsData(prev => {
                        const existing = prev?.[orderNum];
                        if (!existing) return prev;
                        return { ...prev, [orderNum]: applyArchiveToLines(existing) };
                    });
                }

                setSelectedOrder(prev => (Array.isArray(prev) ? applyArchiveToLines(prev) : prev));
            } else {
                alert(result?.message || 'Failed to update BOM part archive status.');
            }
        } catch (err) {
            console.error('Error updating BOM part archive status:', err);
            alert('Error updating BOM part archive status. Please try again.');
        }
    };

    const handleCopyToClipboard = (value, field) => {
        const text = value !== null && value !== undefined ? value.toString().trim() : '';
        if (!text) {
            return;
        }

        const finalizeCopy = () => {
            setCopiedField(field);
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
            copyTimeoutRef.current = setTimeout(() => {
                setCopiedField(null);
            }, 2000);
        };

        const fallbackCopyText = (textToCopy) => {
            const textArea = document.createElement('textarea');
            textArea.value = textToCopy;
            textArea.style.position = 'fixed';
            textArea.style.top = '-9999px';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            if (!successful) {
                throw new Error('Fallback copy command was unsuccessful');
            }
        };

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text)
                .then(finalizeCopy)
                .catch(() => {
                    try {
                        fallbackCopyText(text);
                        finalizeCopy();
                    } catch (err) {
                        console.error('Failed to copy text:', err);
                    }
                });
        } else {
            try {
                fallbackCopyText(text);
                finalizeCopy();
            } catch (err) {
                console.error('Failed to copy text:', err);
            }
        }
    };

    // Comment management functions - synchronized with All Orders
    const handleDeleteComment = async (lineId, commentId) => {
        // First, try to delete from backend if this is a saved comment
        try {
            await deleteComment(commentId, lineId);
        } catch (error) {
            console.error('Error deleting comment from backend:', error);
            // Continue to update local state even if backend call fails
        }
        
        // Update local commentsMap state to remove the comment
        setCommentsMap(prev => {
            const currentComments = prev[lineId] || [];
            const updatedComments = currentComments.filter((comment) => {
                if (!comment || typeof comment !== 'object') return false;
                const existingId = comment.id;
                return String(existingId) !== String(commentId);
            });
            return {
                ...prev,
                [lineId]: updatedComments
            };
        });
        
        // Update newOrdersData to remove the comment from line.comment array
        setNewOrdersData(prevOrders => {
            if (!Array.isArray(prevOrders)) return prevOrders;
            return prevOrders.map(line => {
                if (line.id === lineId && Array.isArray(line.comment)) {
                    return {
                        ...line,
                        comment: line.comment.filter(c => String(c.id) !== String(commentId))
                    };
                }
                return line;
            });
        });
    };

    const handleResolveThread = (lineId, commentId = null) => {
        setCommentsMap(prev => {
            const currentComments = prev[lineId] || [];
            let updatedComments;

            if (commentId !== null) {
                // Resolve specific comment by id or index
                updatedComments = currentComments.map((comment, index) => {
                    const commentIdentifier = comment.id || comment.commentId || index;
                    if (commentIdentifier === commentId) {
                        return { ...comment, resolved: true };
                    }
                    return comment;
                });
            } else {
                // Resolve all comments in the thread
                updatedComments = currentComments.map(comment => ({ ...comment, resolved: true }));
            }

            return {
                ...prev,
                [lineId]: updatedComments
            };
        });
    };

    const handleAddComment = (lineId, newComment, status = "pending") => {
        const currentUser = localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'Current User';
        const newCommentObj = toStoredComment(newComment, status, { lastEditedBy: currentUser });

        applyCommentPatch(lineId, newCommentObj);

        const line = findOrderLineById(lineId, [newOrdersData], orderDetailsData);
        // Everyone else, on any page, gets a minimized blue chat for this note.
        presence.broadcastSave({
            cell: notesCell(lineId),
            kind: 'comment',
            lineId: String(lineId),
            orderNumber: line?.order?.ordernum || line?.ordernum || '',
            customerName: line?.order?.customer?.name || '',
            value: newCommentObj,
        });
    };

    // Order Status handler functions
    const toggleDropdown = (id, event) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition(orderStatusMenuPosition(rect));
        setDropdownOpenId(id);
    };

    const handleSelectTagsClick = (event, line) => {
        // Close any other open dropdowns first
        if (dropdownOpenId && dropdownOpenId !== line.id) {
            setDropdownOpenId(null);
        }

        // Toggle the current dropdown
        if (dropdownOpenId === line.id) {
            setDropdownOpenId(null);
            return;
        }

        // Someone else is already in this order status - don't open a second picker.
        const statusLockedBy = orderStatusLockOwner(line.id);
        if (statusLockedBy) {
            showToast(`${statusLockedBy.name} is changing this order status`, 'error');
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition(orderStatusMenuPosition(rect));
        setDropdownOpenId(line.id);
    };

    // Handle Order Status tag selection
    const handleOrderStatusToggle = (lineId, tag) => {
        setOrderStatusSelections(prev => {
            const currentSelections = prev[lineId] || [];
            const isSelected = currentSelections.some(selection => selection.label === tag.label);

            if (isSelected) {
                // Remove tag if already selected
                return {
                    ...prev,
                    [lineId]: currentSelections.filter(selection => selection.label !== tag.label)
                };
            } else {
                // Add tag if not selected
                return {
                    ...prev,
                    [lineId]: [...currentSelections, tag]
                };
            }
        });
    };

    // Line Status functions
    const toggleLineStatusDropdown = (lineId) => {
        setLineStatusDropdownOpen(prev => {
            const hasOtherOpen = Object.keys(prev).some(id => id !== lineId && prev[id]);

            if (prev[lineId]) {
                return {};
            }

            if (hasOtherOpen) {
                return { [lineId]: true };
            }

            return { [lineId]: true };
        });
    };

    const handleLineStatusToggle = useCallback((lineId, tag, orderNum) => {
        const isBulkMode = !!(orderNum && orderBulkModeEnabled && orderBulkModeEnabled[orderNum]);
        const lineIdKey = String(lineId);
        const orderDetailsArray = orderNum && orderDetailsData ? orderDetailsData[orderNum] : null;
        const targetIds = isBulkMode && Array.isArray(orderDetailsArray) && orderDetailsArray.length > 0
            ? orderDetailsArray.map((line) => String(line.id))
            : [lineIdKey];

        const isEffectivelySelected =
            (lineStatusSelections[lineIdKey] || []).some((s) => s.label === tag.label) ||
            (
                (submittedLineStatuses[lineIdKey] || []).some((s) => s.label === tag.label) &&
                !(lineTagsToDeselect[lineIdKey] || []).some((t) => t.label === tag.label)
            );
        const isAdding = !isEffectivelySelected;

        setLineStatusSelections((prev) => {
            const next = { ...prev };
            targetIds.forEach((itemId) => {
                const current = next[itemId] || [];
                const inSel = current.some((s) => s.label === tag.label);
                const inSub = (submittedLineStatuses[itemId] || []).some((s) => s.label === tag.label);

                if (isAdding) {
                    if (!inSel && !inSub) {
                        next[itemId] = [...current, tag];
                    }
                } else if (inSel) {
                    next[itemId] = current.filter((s) => s.label !== tag.label);
                }
            });
            return next;
        });

        setLineTagsToDeselect((prev) => {
            const next = { ...prev };
            targetIds.forEach((itemId) => {
                const current = next[itemId] || [];
                const marked = current.some((t) => t.label === tag.label);
                const inSub = (submittedLineStatuses[itemId] || []).some((s) => s.label === tag.label);

                if (isAdding) {
                    if (marked) {
                        next[itemId] = current.filter((t) => t.label !== tag.label);
                    }
                } else if (inSub && !marked) {
                    next[itemId] = [...current, tag];
                }
            });
            return next;
        });

        setPendingLineRemoval((prev) => {
            const next = { ...prev };
            targetIds.forEach((itemId) => {
                if (isAdding) {
                    next[itemId] = false;
                    return;
                }
                const remainingSelections = (lineStatusSelections[itemId] || []).filter(
                    (s) => s.label !== tag.label
                ).length;
                const remainingSubmitted = (submittedLineStatuses[itemId] || []).filter((s) => {
                    if (s.label === tag.label) return false;
                    return !(lineTagsToDeselect[itemId] || []).some((t) => t.label === s.label);
                }).length;
                next[itemId] = remainingSelections === 0 && remainingSubmitted === 0;
            });
            return next;
        });

        if (isBulkMode && orderNum) {
            setOrderHasBulkChanges((prev) => ({ ...prev, [orderNum]: true }));
        }
    }, [orderBulkModeEnabled, orderDetailsData, submittedLineStatuses, lineStatusSelections, lineTagsToDeselect]);

    const handleLineTagDeselect = (lineId, tag) => {
        const lineIdKey = String(lineId);
        setLineTagsToDeselect(prev => {
            const currentDeselections = prev[lineIdKey] || [];
            const isAlreadyMarked = currentDeselections.some(t => t.label === tag.label);
            const submittedTags = submittedLineStatuses[lineIdKey] || [];

            if (isAlreadyMarked) {
                const newDeselections = currentDeselections.filter(t => t.label !== tag.label);

                if (newDeselections.length === 0) {
                    setPendingLineRemoval(prevPending => ({
                        ...prevPending,
                        [lineIdKey]: false
                    }));
                }

                return {
                    ...prev,
                    [lineIdKey]: newDeselections
                };
            }

            const newDeselections = [...currentDeselections, tag];

            if (newDeselections.length === submittedTags.length && (lineStatusSelections[lineIdKey] || []).length === 0) {
                setPendingLineRemoval(prevPending => ({
                    ...prevPending,
                    [lineIdKey]: true
                }));
            }

            return {
                ...prev,
                [lineIdKey]: newDeselections
            };
        });
    };

    const handleBulkLineTagDeselect = useCallback((lineId, tag, orderNum) => {
        const isBulkMode = orderNum && orderBulkModeEnabled && orderBulkModeEnabled[orderNum];

        if (!isBulkMode) {
            handleLineTagDeselect(lineId, tag);
            return;
        }

        const orderDetailsArray = orderDetailsData && orderDetailsData[orderNum];
        if (!Array.isArray(orderDetailsArray) || orderDetailsArray.length === 0) {
            return;
        }

        const clickedId = String(lineId);
        const shouldMark = !(lineTagsToDeselect[clickedId] || []).some((t) => t.label === tag.label);

        setLineTagsToDeselect((prev) => {
            const newDeselections = { ...prev };
            let hasChanges = false;

            orderDetailsArray.forEach((line) => {
                const itemId = String(line.id);
                const submittedTags = submittedLineStatuses[itemId] || [];
                if (!submittedTags.some((t) => t.label === tag.label)) return;

                const currentDeselections = newDeselections[itemId] || [];
                const isAlreadyMarked = currentDeselections.some((t) => t.label === tag.label);

                if (shouldMark && !isAlreadyMarked) {
                    newDeselections[itemId] = [...currentDeselections, tag];
                    hasChanges = true;
                } else if (!shouldMark && isAlreadyMarked) {
                    newDeselections[itemId] = currentDeselections.filter((t) => t.label !== tag.label);
                    hasChanges = true;
                }
            });

            if (hasChanges && orderNum) {
                setOrderHasBulkChanges((prevBulk) => ({ ...prevBulk, [orderNum]: true }));
            }

            return newDeselections;
        });

        setPendingLineRemoval((prev) => {
            const next = { ...prev };
            orderDetailsArray.forEach((line) => {
                const itemId = String(line.id);
                const submittedTags = submittedLineStatuses[itemId] || [];
                if (!submittedTags.some((t) => t.label === tag.label)) return;

                if (shouldMark) {
                    const remainingSelections = (lineStatusSelections[itemId] || []).length;
                    const remainingSubmitted = submittedTags.filter((t) => t.label !== tag.label).length;
                    next[itemId] = remainingSelections === 0 && remainingSubmitted === 0;
                } else {
                    next[itemId] = false;
                }
            });
            return next;
        });
    }, [orderBulkModeEnabled, orderDetailsData, submittedLineStatuses, lineStatusSelections, lineTagsToDeselect]);

    const hasLineChangesToSubmit = useCallback((lineId) => {
        const currentSelections = lineStatusSelections[lineId] || [];
        const tagsToRemove = lineTagsToDeselect[lineId] || [];

        return currentSelections.length > 0 || tagsToRemove.length > 0 || pendingLineRemoval[lineId];
    }, [lineStatusSelections, lineTagsToDeselect, pendingLineRemoval]);

    /** Push a committed line status into every piece of grid state that renders it.
     *  Shared by the local submit path and by remote saves arriving over presence. */
    const applyLineStatusPatch = useCallback((lineId, finalSelections) => {
        const lineJson = JSON.stringify(finalSelections);

        setSubmittedLineStatuses(prev => ({ ...prev, [lineId]: finalSelections }));
        setLineStatusSelections(prev => ({ ...prev, [lineId]: [] }));
        setLineTagsToDeselect(prev => ({ ...prev, [lineId]: [] }));
        setPendingLineRemoval(prev => ({ ...prev, [lineId]: false }));

        setOrderDetailsData(prev => {
            const updated = { ...prev };
            const lineIdNum = typeof lineId === 'string' ? parseInt(lineId, 10) : lineId;
            Object.keys(updated).forEach(orderNum => {
                if (Array.isArray(updated[orderNum])) {
                    updated[orderNum] = updated[orderNum].map(line => {
                        if (line.id === lineIdNum || String(line.id) === String(lineId)) {
                            return { ...line, lineItemStatus: lineJson };
                        }
                        return line;
                    });
                }
            });
            return updated;
        });

        setNewOrdersData((prev) =>
            Array.isArray(prev)
                ? prev.map((row) =>
                      String(row.id) === String(lineId) ? { ...row, lineItemStatus: lineJson } : row
                  )
                : prev
        );
    }, []);

    /** Push a committed order status into the grid state. Shared by the local
     *  submit path and by remote saves arriving over presence. */
    const applyOrderStatusPatch = useCallback((lineId, newSubmitted) => {
        const statusJson = JSON.stringify(newSubmitted);

        setSubmittedOrderStatuses(prev => ({ ...prev, [lineId]: newSubmitted }));
        setOrderStatusSelections(prev => ({ ...prev, [lineId]: [] }));
        setTagsToDeselect(prev => ({ ...prev, [lineId]: [] }));

        setNewOrdersData(prevOrders => {
            if (!Array.isArray(prevOrders)) return prevOrders;
            return prevOrders.map(o => String(o.id) === String(lineId)
                ? { ...o, manualUpdateStatus: statusJson }
                : o);
        });
    }, []);

    /** Merge one saved comment into commentsMap. Shared by the local add path and
     *  by remote adds arriving over presence. */
    const applyCommentPatch = useCallback((lineId, commentToStore) => {
        if (!commentToStore) return;
        setCommentsMap(prev => {
            const currentComments = prev[lineId] || [];
            const filtered = currentComments.filter(c => String(c?.id) !== String(commentToStore.id));
            return {
                ...prev,
                [lineId]: [...filtered, commentToStore]
            };
        });
    }, []);

    /** Someone else saved on this page - patch that one cell, no reload. */
    const applyRemoteSave = useCallback((evt) => {
        if (!evt || !evt.lineId) return;

        if (evt.kind === 'lineStatus') {
            applyLineStatusPatch(evt.lineId, evt.value || []);
            showToast(`${evt.by?.name || 'Someone'} updated a line status`, 'info');
            return;
        }

        if (evt.kind === 'orderStatus') {
            applyOrderStatusPatch(evt.lineId, evt.value || []);
            showToast(`${evt.by?.name || 'Someone'} updated an order status`, 'info');
            return;
        }

        if (evt.kind === 'comment') {
            if (evt.quoteId || evt.poLineId) return;
            applyCommentPatch(evt.lineId, evt.value);
            if (!evt.page || evt.page === PRESENCE_PAGE) {
                showToast(`${evt.by?.name || 'Someone'} added a note`, 'info');
            }
            return;
        }

        if (evt.kind === 'stickyNotes') {
            stickyNotesRemoteRef.current(evt.lineId, evt.value || []);
        }

        if (evt.kind === 'toggle') {
            const setter = {
                review: setReviewToggles,
                ready: setReadyToggles,
                confirm: setConfirmCheckboxes,
            }[evt.field];
            if (!setter) return;
            setter((prev) => ({ ...prev, [evt.lineId]: evt.value }));
            showToast(`${evt.by?.name || 'Someone'} updated ${evt.field}`, 'info');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyLineStatusPatch]);

    const handleLineStatusSubmit = async (lineId) => {
        const lockedBy = lineStatusLockOwner(lineId);
        if (lockedBy) {
            showToast(`${lockedBy.name} is editing this line status — try again in a moment`, 'error');
            return;
        }

        const currentSelections = lineStatusSelections[lineId] || [];
        const submittedSelections = submittedLineStatuses[lineId] || [];
        const tagsToRemove = lineTagsToDeselect[lineId] || [];

        const finalSelections = [
            ...currentSelections,
            ...submittedSelections.filter(
                tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
            )
        ].filter((tag, index, array) =>
            array.findIndex(t => t.label === tag.label) === index
        );

        const submitData = {
            orderLineId: lineId,
            status: JSON.stringify(finalSelections),
        };

        try {
            await saveLineStatus(submitData);
            notifyOrderHistoryChanged(
                (Array.isArray(newOrdersData) ? newOrdersData : [])
                    .find((l) => String(l?.id) === String(lineId))?.order?.ordernum
            );

            setLineStatusDropdownOpen(prev => {
                const newState = { ...prev };
                delete newState[lineId];
                return newState;
            });

            applyLineStatusPatch(lineId, finalSelections);

            // Let everyone else on this page patch the same cell without a refresh.
            presence.broadcastSave({
                cell: lineStatusCell(lineId),
                kind: 'lineStatus',
                lineId: String(lineId),
                value: finalSelections,
            });

            showToast("Line status updated successfully", "success");
        } catch (error) {
            console.error("Error submitting line status:", error);
            showToast("Error submitting line status. Please try again.", "error");
        }
    };

    const handleBulkLineStatusSubmit = async (orderNum) => {
        if (!orderNum || !orderHasBulkChanges[orderNum]) return;

        setIsSubmittingBulk(true);

        try {
            const orderDetailsArray = orderDetailsData && orderDetailsData[orderNum];
            if (!Array.isArray(orderDetailsArray) || orderDetailsArray.length === 0) {
                showToast("No order details found", "error");
                setIsSubmittingBulk(false);
                return;
            }

            const linesToSubmit = [];
            orderDetailsArray.forEach((line) => {
                const lineId = String(line.id);
                if (hasLineChangesToSubmit(lineId)) {
                    linesToSubmit.push(lineId);
                }
            });

            if (linesToSubmit.length === 0) {
                showToast("No changes to submit", "info");
                setIsSubmittingBulk(false);
                return;
            }

            for (const lineId of linesToSubmit) {
                const currentSelections = lineStatusSelections[lineId] || [];
                const submittedSelections = submittedLineStatuses[lineId] || [];
                const tagsToRemove = lineTagsToDeselect[lineId] || [];

                const finalSelections = [
                    ...currentSelections,
                    ...submittedSelections.filter(
                        tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
                    )
                ].filter((tag, index, array) =>
                    array.findIndex(t => t.label === tag.label) === index
                );

                const submitData = {
                    orderLineId: lineId,
                    status: JSON.stringify(finalSelections),
                };

                await saveLineStatus(submitData);
                notifyOrderHistoryChanged(
                    (Array.isArray(newOrdersData) ? newOrdersData : [])
                        .find((l) => String(l?.id) === String(lineId))?.order?.ordernum
                );

                setSubmittedLineStatuses(prev => ({
                    ...prev,
                    [lineId]: finalSelections
                }));

                setLineStatusSelections(prev => ({
                    ...prev,
                    [lineId]: []
                }));

                setLineTagsToDeselect(prev => ({
                    ...prev,
                    [lineId]: []
                }));

                setPendingLineRemoval(prev => ({
                    ...prev,
                    [lineId]: false
                }));

                setOrderDetailsData(prev => {
                    const updated = { ...prev };
                    const lineIdNum = typeof lineId === 'string' ? parseInt(lineId, 10) : lineId;
                    Object.keys(updated).forEach(orderNumKey => {
                        if (Array.isArray(updated[orderNumKey])) {
                            updated[orderNumKey] = updated[orderNumKey].map(line => {
                                if (line.id === lineIdNum || String(line.id) === String(lineId)) {
                                    return {
                                        ...line,
                                        lineItemStatus: JSON.stringify(finalSelections)
                                    };
                                }
                                return line;
                            });
                        }
                    });
                    return updated;
                });

                const lineJson = JSON.stringify(finalSelections);
                setNewOrdersData((prev) =>
                    Array.isArray(prev)
                        ? prev.map((row) =>
                              String(row.id) === String(lineId) ? { ...row, lineItemStatus: lineJson } : row
                          )
                        : prev
                );
            }

            if (orderNum) {
                setOrderHasBulkChanges(prevBulk => ({ ...prevBulk, [orderNum]: false }));
            }
            showToast(`Successfully updated ${linesToSubmit.length} order lines`, "success");
        } catch (error) {
            console.error("Error submitting bulk line status:", error);
            showToast("Error submitting bulk line status. Please try again.", "error");
        } finally {
            setIsSubmittingBulk(false);
        }
    };

    // Handle individual tag deselection by clicking on the tag
    const handleTagDeselect = (lineId, tag) => {
        setTagsToDeselect(prev => {
            const currentDeselections = prev[lineId] || [];
            const isMarkedForDeselection = currentDeselections.some(t => t.label === tag.label);

            if (isMarkedForDeselection) {
                // Remove from deselection list
                return {
                    ...prev,
                    [lineId]: currentDeselections.filter(t => t.label !== tag.label)
                };
            } else {
                // Add to deselection list
                return {
                    ...prev,
                    [lineId]: [...currentDeselections, tag]
                };
            }
        });
    };

    // Check if there are changes to submit
    const hasChangesToSubmit = (lineId) => {
        const currentSelections = orderStatusSelections[lineId] || [];
        const submittedSelections = submittedOrderStatuses[lineId] || [];
        const tagsToRemove = tagsToDeselect[lineId] || [];

        // Check if there are new selections to submit
        const hasNewSelections = currentSelections.length > 0;

        // Check if there are tags marked for deselection
        const hasTagsToDeselect = tagsToRemove.length > 0;

        return hasNewSelections || hasTagsToDeselect;
    };

    // Handle Order Status submission/deselection
    const handleOrderStatusSubmit = async (lineId) => {
        const statusLockedBy = orderStatusLockOwner(lineId);
        if (statusLockedBy) {
            showToast(`${statusLockedBy.name} is changing this order status`, 'error');
            return;
        }

        // Save selected statuses to backend but do NOT trigger a full refresh.
        const currentSelections = orderStatusSelections[lineId] || [];
        const submittedSelections = submittedOrderStatuses[lineId] || [];
        const tagsToRemove = tagsToDeselect[lineId] || [];

        try {
            // Start with existing submitted selections (or empty)
            let newSubmitted = Array.isArray(submittedSelections) ? [...submittedSelections] : [];

            // Remove tags that are marked for deselection
            if (tagsToRemove.length > 0) {
                newSubmitted = newSubmitted.filter(submittedTag =>
                    !tagsToRemove.some(tagToRemove => tagToRemove.label === submittedTag.label)
                );
            }

            // Add new selections that aren't already in the submitted list
            if (currentSelections.length > 0) {
                currentSelections.forEach(sel => {
                    const alreadyExists = newSubmitted.some(sub => sub.label === sel.label);
                    if (!alreadyExists) {
                        newSubmitted.push(sel);
                    }
                });
            }

            // Prepare payload using the merged resulting statuses so backend receives the final state
            const payload = {
                status: JSON.stringify(newSubmitted),
                orderLineId: lineId,
                // Names the user on the order history entry and on the PO Review
                // notification raised for Customer Service and the Warehouse.
                lastEditedBy: localStorage.getItem('userEmail')
                    || localStorage.getItem('email')
                    || localStorage.getItem('userName')
                    || ''
            };

            // Persist to backend (await so we know result) but avoid any global refresh
            await saveComment(payload);
            notifyOrderHistoryChanged(
                (Array.isArray(newOrdersData) ? newOrdersData : [])
                    .find((l) => String(l?.id) === String(lineId))?.order?.ordernum
            );

            // Close dropdown after successful save
            setDropdownOpenId(null);

            // Update submitted statuses and the local orderData, so rendering that
            // relies on line.manualUpdateStatus reflects the merged selections
            // until a manual refresh/fetch from backend occurs.
            applyOrderStatusPatch(lineId, newSubmitted);

            // Let everyone else on this page patch the same cell without a refresh.
            presence.broadcastSave({
                cell: orderStatusCell(lineId),
                kind: 'orderStatus',
                lineId: String(lineId),
                value: newSubmitted,
            });

        } catch (err) {
            console.error('Failed to save order status:', err);
            // Optionally provide user feedback here (toast/alert) instead of refreshing
        }
    };

    const fetchOrdersSearch = async (isExport = false) => {
        setLoading(true);
        const exportFlag = isExport || exports;
        try {
            const orders = await orderFetch('0', '-1', searchTerm, sortConfig.key, sortConfig.direction, null, startDate, endDate, orderValue, POValue, '', statusValue, ShippingMethodValue, CustomerId, CountryValue, viewBy, exportFlag, null, null, true);
            if (orders === null) {
                return;
            }
            
            if (exportFlag === true) {
                setExports(false);
                if (!orders.order || orders.order.length === 0) {
                    alert('No data to export');
                    setLoading(false);
                    return;
                }
                const headers = [
                    ["Line Item", "Order Number", "Ordered On Date", "WO#", "Customer", "Customer PO", "Order Qty", "Status", "Ship Date", "Shipment Method", "Country"]
                ];

                // Extract table data
                let data = orders.order;
                const rows = data.map((line) => [
                    line.negativeCountStr || line.lineitem || "N/A",
                    line.order?.ordernum || "N/A",
                    getOrderedOnDate(line),
                    line.order?.woNum || "N/A",
                    line.order?.customer?.name || "N/A",
                    line.order?.custponum || "N/A",
                    line.oorderqty || "N/A",
                    line.statusObj?.status || "N/A",
                    getEarliestPromisedDateForOrder(line) || line.shipdate || line.orig_shipdate || "N/A",
                    line.order?.shipmentMethod?.methodOfShipment || "N/A",
                    line.order?.country?.country_Name || "N/A"
                ]);

                // Combine headers and rows
                const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);

                // Create a new workbook and append the worksheet
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Order Validation");

                // Export the Excel file
                XLSX.writeFile(workbook, "order_validation.xlsx");
            } else {
                if (orders && orders.order) {
                    // Ensure order is an array before using it
                    const orderData = Array.isArray(orders.order) ? orders.order : [];
                    setNewOrdersData(orderData);
                    
                    // Update totalRecords from API response
                    const currentTotal = typeof orders.totalRecords === 'number'
                        ? orders.totalRecords
                        : orderData.length;
                    setTotalRecords(currentTotal);
                    // Always update overallTotalRecords to preserve the maximum total
                    if (currentTotal > 0) {
                        setOverallTotalRecords((prevTotal) => {
                            // If no previous total or current is larger, use current
                            if (prevTotal === 0 || currentTotal > prevTotal) {
                                return currentTotal;
                            }
                            // Otherwise keep the larger previous total
                            return prevTotal;
                        });
                    }
                    
                    // Update orderAccessLinks for order data
                    if (Array.isArray(orderData) && orderData.length > 0) {
                        const newLinks = {};
                        orderData.forEach(line => {
                            if (line.folderUrl) {
                                newLinks[line.id] = [{
                                    id: Date.now() + Math.random(),
                                    url: line.folderUrl,
                                    dateAdded: new Date().toLocaleDateString()
                                }];
                            }
                        });
                        setOrderAccessLinks(prev => ({ ...prev, ...newLinks }));
                    }
                } else {
                    // If no order data, set empty array
                    setNewOrdersData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            if (!exportFlag) {
                setNewOrdersData([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const searchOrder = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            searchOrder(tempSearchTerm);
        }
    };

    const filteredData = getFilteredNewOrdersData();
    const filteredRecordsCount = filteredData.length;
    const totalPages = itemsPerPage === -1 ? 1 : Math.max(1, Math.ceil(filteredRecordsCount / itemsPerPage));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedData = itemsPerPage === -1
        ? filteredData
        : filteredData.slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage);

    const lineRows = useMemo(
        () => flattenOrdersToLines(paginatedData, orderDetailsData, archivedOrderIds),
        [paginatedData, orderDetailsData, archivedOrderIds]
    );
    const lineDetailsPending = useMemo(
        () => headerLinesMissingDetails(paginatedData, orderDetailsData),
        [paginatedData, orderDetailsData]
    );

    const hasPaginatedRecords = paginatedData.length > 0;
    const startRecord = hasPaginatedRecords ? (itemsPerPage === -1 ? 1 : (currentPage - 1) * itemsPerPage + 1) : 0;
    const endRecord = hasPaginatedRecords ? startRecord + paginatedData.length - 1 : 0;
    // Use the original data length as total (before client-side filtering)
    // This ensures we show the correct total even when client-side filters are applied
    const originalDataCount = newOrdersData.length;
    const validationHistoryOrderNums = useMemo(() => {
        const nums = new Set();
        (newOrdersData || []).forEach((line) => {
            const ordernum = line?.order?.ordernum || line?.ordernum;
            if (ordernum) nums.add(String(ordernum));
        });
        return Array.from(nums);
    }, [newOrdersData]);
    const { getUnreadCount, markViewed } = useOrderHistoryUnread(validationHistoryOrderNums);

    const stickyNoteOrderKey = (() => {
        const nums = new Set();
        (paginatedData || []).forEach((line) => {
            const ordernum = line?.order?.ordernum || line?.ordernum;
            if (ordernum) nums.add(String(ordernum));
        });
        return Array.from(nums).sort().join('|');
    })();
    const stickyNoteOrderNums = useMemo(
        () => (stickyNoteOrderKey ? stickyNoteOrderKey.split('|') : []),
        [stickyNoteOrderKey]
    );
    // Cetec shipping instructions, read-only, pinned on top of each row's sticky notes.
    const shippingInstructions = useShippingInstructions(
        (paginatedData || []).map((line) => line?.order || line)
    );

    const stickyNotes = useStickyNotes(stickyNoteOrderNums, {
        onChange: (orderNumber, notes) => presence.broadcastSave({
            cell: cellKey(PRESENCE_PAGE, 'order', orderNumber, 'stickyNotes'),
            kind: 'stickyNotes',
            lineId: String(orderNumber),
            value: notes,
        }),
    });
    stickyNotesRemoteRef.current = stickyNotes.applyRemote;
    const displayTotalRecords = overallTotalRecords > 0 && overallTotalRecords >= originalDataCount 
        ? overallTotalRecords 
        : (originalDataCount > 0 ? originalDataCount : (totalRecords > 0 ? totalRecords : 0));

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const sortedData = [...paginatedData].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue, bValue;
        switch (sortConfig.key) {
            case 'ordernum':
                aValue = a.order?.ordernum || '';
                bValue = b.order?.ordernum || '';
                break;
            case 'orderedondate':
                aValue = getOrderedOnDate(a) === '-' ? '' : getOrderedOnDate(a);
                bValue = getOrderedOnDate(b) === '-' ? '' : getOrderedOnDate(b);
                break;
            case 'name':
                aValue = a.order?.customer?.name || '';
                bValue = b.order?.customer?.name || '';
                break;
            case 'custponum':
                aValue = a.order?.custponum || '';
                bValue = b.order?.custponum || '';
                break;
            case 'orig_shipdate':
                {
                    const dateA = getEarliestPromisedDateForOrder(a) || a.shipdate || a.orig_shipdate || '';
                    const dateB = getEarliestPromisedDateForOrder(b) || b.shipdate || b.orig_shipdate || '';
                    
                    // Handle empty dates
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    
                    // Convert dates to proper Date objects for comparison
                    const parseDate = (dateStr) => {
                        if (!dateStr) return new Date(0);
                        // Handle M/D/YY format
                        const parts = dateStr.split('/');
                        if (parts.length === 3) {
                            const month = parseInt(parts[0]) - 1; // Month is 0-indexed
                            const day = parseInt(parts[1]);
                            let year = parseInt(parts[2]);
                            // Handle 2-digit years (assume 20xx for years < 50, 19xx for years >= 50)
                            if (year < 50) year += 2000;
                            else if (year < 100) year += 1900;
                            return new Date(year, month, day);
                        }
                        return new Date(dateStr);
                    };
                    
                    const dateObjA = parseDate(dateA);
                    const dateObjB = parseDate(dateB);
                    
                    if (sortConfig.direction === 'ascending') {
                        return dateObjA - dateObjB; // Earliest dates first
                    } else {
                        return dateObjB - dateObjA; // Latest dates first
                    }
                }
            case 'method_of_shipment':
                aValue = a.order?.shipmentMethod?.methodOfShipment || '';
                bValue = b.order?.shipmentMethod?.methodOfShipment || '';
                break;
            case 'country_name':
                aValue = a.order?.country?.country_Name || '';
                bValue = b.order?.country?.country_Name || '';
                break;
            default:
                return 0;
        }

        if (sortConfig.direction === 'ascending') {
            return aValue.localeCompare(bValue);
        } else {
            return bValue.localeCompare(aValue);
        }
    });

    const orderLineStatusReportMap = useMemo(
        () => buildOrderLineStatusReportMapFromDetails(orderDetailsData, excludedAvailabilityPartsByOrder),
        [orderDetailsData, excludedAvailabilityPartsByOrder]
    );

    const mergedKpiLines = useMemo(() => {
        const map = new Map();
        (newOrdersData || []).forEach((line) => {
            if (!line?.id) return;
            if (archivedOrderNums.has(String(line?.order?.ordernum || ''))) return;
            map.set(line.id, line);
        });
        return Array.from(map.values());
    }, [newOrdersData, archivedOrderNums]);

    const dueTodayKpiLines = useMemo(() =>
        mergedKpiLines.filter((line) => {
            const ds = getEarliestPromisedDateForOrder(line);
            if (!ds) return false;
            const orderDay = parseAsLocalCalendarDate(ds);
            if (!orderDay) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return orderDay.getTime() === today.getTime();
        }), [mergedKpiLines, orderDetailsData, newOrdersData, archivedOrderIds, archivedOrderNums]);

    const pastDueKpiLines = useMemo(() =>
        mergedKpiLines.filter((line) => {
            const ds = getEarliestPromisedDateForOrder(line);
            if (!ds) return false;
            const orderDay = parseAsLocalCalendarDate(ds);
            if (!orderDay) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return orderDay.getTime() < today.getTime();
        }), [mergedKpiLines, orderDetailsData, newOrdersData, archivedOrderIds, archivedOrderNums]);

    const dueKpiLines = useMemo(
        () => [...dueTodayKpiLines, ...pastDueKpiLines],
        [dueTodayKpiLines, pastDueKpiLines]
    );

    const missingKpiLines = useMemo(() => {
        const nums = new Set(
            Object.entries(orderLineStatusReportMap || {})
                .filter(([, v]) => (v?.missingPartsCount || 0) > 0)
                .map(([k]) => String(k))
        );
        return mergedKpiLines.filter((line) => nums.has(String(line?.order?.ordernum || '')));
    }, [mergedKpiLines, orderLineStatusReportMap]);

    const uniqueKpiOrders = useMemo(
        () => new Set(mergedKpiLines.map((l) => l?.order?.ordernum).filter(Boolean)).size,
        [mergedKpiLines]
    );

    const missingKpiOrderCount = useMemo(
        () => new Set(missingKpiLines.map((l) => l?.order?.ordernum).filter(Boolean)).size,
        [missingKpiLines]
    );

    const missingKpiTotalBomCount = useMemo(() => {
        const orderNums = Array.from(new Set(missingKpiLines.map((l) => String(l?.order?.ordernum || '')).filter(Boolean)));
        return orderNums.reduce((total, orderNum) => {
            const report = orderLineStatusReportMap?.[orderNum];
            return total + (report?.missingPartsCount || 0);
        }, 0);
    }, [missingKpiLines, orderLineStatusReportMap]);

    const dueTodayKpiOrderCount = useMemo(
        () => new Set(dueTodayKpiLines.map((l) => l?.order?.ordernum).filter(Boolean)).size,
        [dueTodayKpiLines]
    );

    const pastDueKpiOrderCount = useMemo(
        () => new Set(pastDueKpiLines.map((l) => l?.order?.ordernum).filter(Boolean)).size,
        [pastDueKpiLines]
    );

    const newOrdersWeekStats = useMemo(() => {
        const weekBounds = (weekOffsetFromCurrent) => {
            const now = new Date();
            const day = now.getDay();
            const mondayThisWeek = new Date(now);
            mondayThisWeek.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
            mondayThisWeek.setHours(0, 0, 0, 0);
            const weekStart = new Date(mondayThisWeek);
            weekStart.setDate(mondayThisWeek.getDate() - weekOffsetFromCurrent * 7);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            weekEnd.setHours(23, 59, 59, 999);
            return { weekStart, weekEnd };
        };

        const lines = (newOrdersData || []).filter(
            (l) => !archivedOrderNums.has(String(l?.order?.ordernum || ''))
        );

        const countInWeek = (weekOffset) => {
            const { weekStart, weekEnd } = weekBounds(weekOffset);
            return lines.filter((line) => {
                const d = getOrderLineEntryDate(line);
                if (!d) return false;
                return d >= weekStart && d <= weekEnd;
            }).length;
        };

        const thisWeekCount = countInWeek(0);
        const lastWeekCount = countInWeek(1);
        const withEntryDate = lines.filter((l) => getOrderLineEntryDate(l)).length;

        let pctVsLastWeek = null;
        let changeText = '';
        let trendShort = '';
        let trendDir = 'neutral';

        if (lastWeekCount === 0 && thisWeekCount === 0) {
            changeText =
                withEntryDate === 0
                    ? 'Add entry dates on lines to compare weeks, or expand date range'
                    : 'No new-order lines dated this week or last week';
            trendShort = '';
            trendDir = 'neutral';
        } else if (lastWeekCount === 0 && thisWeekCount > 0) {
            changeText = `New activity this week (${thisWeekCount} vs none last week)`;
            pctVsLastWeek = null;
            trendShort = 'New this week';
            trendDir = 'up';
        } else if (lastWeekCount > 0) {
            pctVsLastWeek = Math.round(((thisWeekCount - lastWeekCount) / lastWeekCount) * 1000) / 10;
            const delta = thisWeekCount - lastWeekCount;
            const dSign = delta >= 0 ? '+' : '';
            const pSign = pctVsLastWeek >= 0 ? '+' : '';
            changeText = `${dSign}${delta} vs last week (${pSign}${pctVsLastWeek}%)`;
            trendShort = `${pSign}${pctVsLastWeek}% from last week`;
            if (pctVsLastWeek > 0) trendDir = 'up';
            else if (pctVsLastWeek < 0) trendDir = 'down';
            else trendDir = 'flat';
        }

        return {
            thisWeekCount,
            lastWeekCount,
            changeText,
            pctVsLastWeek,
            linesWithDate: withEntryDate,
            trendShort,
            trendDir,
        };
    }, [newOrdersData, archivedOrderNums]);

    const readyKpiLines = mergedKpiLines.filter((line) => isReadyToShipLine(line));
    const readyKpiOrderCount = new Set(readyKpiLines.map((l) => l?.order?.ordernum).filter(Boolean)).size;
    const kpiTotalLinesDisplay = mergedKpiLines.length;

    const kpiQuickFiltersEqual = (a, b) => {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (typeof a === 'string' && typeof b === 'string') return a === b;
        if (typeof a === 'object' && typeof b === 'object' && a && b)
            return a.kind === b.kind && String(a.label) === String(b.label);
        return false;
    };

    const filterValueForSlot = (slot) => {
        if (!slot || slot.preset === 'total') return null;
        if (slot.preset === 'due') return 'due';
        if (slot.preset === 'missing') return 'missing';
        if (slot.preset === 'ready') return 'ready';
        if (slot.preset === 'order') return { kind: 'order', label: slot.label };
        if (slot.preset === 'line') return { kind: 'line', label: slot.label };
        return null;
    };

    const kpiStatToneClass = ['orders-stat-card--blue', 'orders-stat-card--amber', 'orders-stat-card--rose', 'orders-stat-card--green'];

    const getKpiSlotDisplay = (slot) => {
        if (slot.preset === 'total') {
            return {
                variant: 'total',
                title: 'Total orders',
                value: uniqueKpiOrders,
                meta: null,
                iconClass: 'fa fa-clipboard',
            };
        }
        if (slot.preset === 'due') {
            return {
                variant: 'metric',
                title: 'Orders Due',
                value: dueKpiLines.length,
                meta: `Due today: ${dueTodayKpiOrderCount} · Past Orders Due: ${pastDueKpiOrderCount}`,
                iconClass: 'fa fa-calendar',
            };
        }
        if (slot.preset === 'missing') {
            return {
                variant: 'metric',
                title: 'Missing parts',
                value: missingKpiOrderCount,
                meta: `Total orders: ${missingKpiOrderCount} · Total no. of BOM: ${missingKpiTotalBomCount}`,
                iconClass: 'fa fa-exclamation-triangle',
            };
        }
        if (slot.preset === 'ready') {
            return {
                variant: 'metric',
                title: 'In shipping',
                value: readyKpiLines.length,
                meta: `${readyKpiOrderCount} orders · in shipping pipeline · click to filter lists`,
                iconClass: 'fa fa-check-circle',
            };
        }
        if (slot.preset === 'order') {
            const lines = mergedKpiLines.filter((l) => lineHasOrderStatusLabel(l, slot.label));
            const orderCnt = new Set(lines.map((l) => l?.order?.ordernum).filter(Boolean)).size;
            return {
                variant: 'metric',
                title: slot.label,
                value: lines.length,
                meta: `${orderCnt} orders · order status tag · click to filter lists`,
                iconClass: 'fa fa-tag',
            };
        }
        if (slot.preset === 'line') {
            const lines = mergedKpiLines.filter((l) => lineHasLineStatusLabel(l, slot.label));
            const orderCnt = new Set(lines.map((l) => l?.order?.ordernum).filter(Boolean)).size;
            return {
                variant: 'metric',
                title: slot.label,
                value: lines.length,
                meta: `${orderCnt} orders · line status tag · click to filter lists`,
                iconClass: 'fa fa-list',
            };
        }
        return {
            variant: 'metric',
            title: '—',
            value: 0,
            meta: '',
            iconClass: 'fa fa-circle-o',
        };
    };

    const getLineComments = (line) => {
        const merged = [...(line.comment || []), ...(commentsMap[line.id] || [])].filter(Boolean);
        return merged.sort((a, b) => {
            const timeOf = (comment) => {
                const raw = comment?.createdAt || (comment?.dateStr && comment?.timeStr ? `${comment.dateStr} ${comment.timeStr}` : comment?.date) || 0;
                const time = new Date(raw).getTime();
                return Number.isFinite(time) ? time : 0;
            };
            return timeOf(b) - timeOf(a);
        });
    };

    const isCommentChatOpen = (lineId) => {
        const key = getCommentThreadKey({ orderLineId: lineId });
        return Boolean(commentDock?.threads?.some((thread) => thread.key === key && !thread.minimized));
    };

    const isOrderCommentChatOpen = (orderNumber) => {
        const key = getOrderThreadKey(orderNumber);
        return Boolean(key && commentDock?.threads?.some((thread) => thread.key === key && !thread.minimized));
    };

    const renderInfoComments = (line) => {
        const orderNumber = line.order?.ordernum || line.ordernum;
        const lineComments = collectOrderComments(line, orderDetailsData, commentsMap);
        const commentWatchers = presenceUsersInCell(notesCell(line.id));
        const resolveLineId = (commentId) => findCommentLineId(commentId, line, orderDetailsData, commentsMap);
        return (
            <HoverPO
                hideBadge
                threadKey={getOrderThreadKey(orderNumber)}
                orderLineId={line.id}
                comments={lineComments}
                orderNumber={orderNumber}
                customerName={line.order?.customer?.name}
                customerId={line.order?.customer?.id}
                sourcePage="order-validation"
                threadTitle={orderNumber ? `${orderNumber} comments` : 'Comments'}
                onVisibleChange={(isVisible) => handleNotesVisibility(line.id, isVisible)}
                onDeleteComment={(commentId) => handleDeleteComment(resolveLineId(commentId), commentId)}
                onResolveThread={(commentId) => {
                    if (commentId == null) {
                        orderLinesForHeader(line, orderDetailsData).forEach((row) => handleResolveThread(row.id, null));
                        return;
                    }
                    handleResolveThread(resolveLineId(commentId), commentId);
                }}
                onAddComment={(newComment, status) => handleAddComment(line.id, newComment, status)}
            >
                <OrderCommentsButton
                    total={lineComments.length}
                    unread={notificationsCtx ? notificationsCtx.unreadCountForOrder(orderNumber) : 0}
                    presenceUsers={commentWatchers}
                    active={isOrderCommentChatOpen(orderNumber) || isCommentChatOpen(line.id) || commentWatchers.length > 0}
                />
            </HoverPO>
        );
    };

    const renderLineComments = (line) => {
        const lineNo = line?.lineitem != null && String(line.lineitem) !== '' ? `Line ${line.lineitem}` : '';
        const lineComments = getLineComments(line).map((comment) => ({
            ...comment,
            lineLabel: comment?.lineLabel || lineNo,
        }));
        const commentWatchers = presenceUsersInCell(notesCell(line.id));
        const orderNumber = line.order?.ordernum || line.ordernum;
        return (
            <HoverPO
                hideBadge
                orderLineId={line.id}
                comments={lineComments}
                orderNumber={orderNumber}
                customerName={line.order?.customer?.name}
                customerId={line.order?.customer?.id}
                sourcePage="order-validation"
                lineInfo={lineNo}
                threadTitle={orderNumber && lineNo ? `${orderNumber} · ${lineNo}` : (orderNumber || 'Comments')}
                onVisibleChange={(isVisible) => handleNotesVisibility(line.id, isVisible)}
                onDeleteComment={(commentId) => handleDeleteComment(line.id, commentId)}
                onResolveThread={(commentId) => handleResolveThread(line.id, commentId)}
                onAddComment={(newComment, status) => handleAddComment(line.id, newComment, status)}
            >
                <OrderCommentsButton
                    total={lineComments.length}
                    unread={notificationsCtx
                        ? notificationsCtx.unreadCountForOrder(orderNumber, line.id)
                        : 0}
                    presenceUsers={commentWatchers}
                    active={isCommentChatOpen(line.id) || commentWatchers.length > 0}
                />
            </HoverPO>
        );
    };

    const renderStickyNotes = (line) => {
        const orderNumber = line.order?.ordernum || line.ordernum;
        return (
            <StickyNotesCell
                orderNumber={orderNumber ? String(orderNumber) : ''}
                orderLineId={line.id}
                customerName={line.order?.customer?.name}
                notes={stickyNotes.notesByOrder[String(orderNumber)] || []}
                pinned={shippingInstructions.forOrder(line.order || line)}
                shippingComments={line.order?.shipping_instructions || ''}
                customerId={shippingInstructions.customerIdFor(line.order || line)}
                onAddShipping={shippingInstructions.add}
                onUpdateShipping={shippingInstructions.update}
                onRemoveShipping={shippingInstructions.remove}
                onAdd={stickyNotes.add}
                onUpdate={stickyNotes.update}
                onRemove={stickyNotes.remove}
            />
        );
    };

    const getLineRowProps = (line) => {
        const source = line?._headerLine || line;
        const displayShipDate = getEarliestPromisedDateForOrder(source);
        const rowStyle = getRowStyle(displayShipDate);
        const existingStyle = rowStyle.style || {};
        const isPoReview = shouldRowBePoReview(source);
        const isOrange = shouldRowBeOrange(source);
        const isPurple = shouldRowBePurple(source);
        const rowBackground = isPoReview ? '#232323' : isOrange ? '#FF9D47' : isPurple ? '#BEA9FF' : (shouldRowBeGreen(source) ? '#d4edda' : (existingStyle.backgroundColor || 'transparent'));
        const lightDateRow = !isPoReview && !isOrange && !isPurple && ['#f8d7da', '#fff3cd', '#d1ecf1', '#d4edda'].includes(String(rowBackground).toLowerCase());
        return {
            className: [isPoReview ? 'po-review-row' : '', !isPoReview && isOrange ? 'hot-order-row' : '', !isPoReview && !isOrange && isPurple ? 'cc-hold-ppa-row' : '', lightDateRow ? 'bridge-light-row' : ''].filter(Boolean).join(' ') || undefined,
            style: {
                ...existingStyle,
                backgroundColor: rowBackground,
                color: isPoReview ? '#ffffff' : (lightDateRow ? '#212529' : undefined),
            },
            isOrange,
            isPoReview,
        };
    };

    const renderOrderLineTable = () => (
        <OrderLineView
            lines={lineRows}
            detailsPending={lineDetailsPending}
            getRowProps={getLineRowProps}
            renderComments={renderLineComments}
            onHistory={handleHistoryIconClick}
            getHistoryUnread={getUnreadCount}
            getOrderLabel={getCetecOrderNumber}
            getFolderLink={resolveOrderFolderLinkForLine}
            renderShippingMethod={renderShippingMethodCell}
            renderStickyNotes={renderStickyNotes}
            onOrderAccess={handleOrderAccessClick}
            onDeleteOrderAccess={handleDeleteOrderAccessLink}
            onSelectOrderStatus={handleSelectTagsClick}
            orderStatusSelections={orderStatusSelections}
            submittedOrderStatuses={submittedOrderStatuses}
            tagsToDeselect={tagsToDeselect}
            hasOrderStatusChanges={hasChangesToSubmit}
            onDeselectOrderTag={handleTagDeselect}
            onSubmitOrderStatus={handleOrderStatusSubmit}
            renderOrderStatusPresence={renderOrderStatusPresence}
            lineStatusSelections={lineStatusSelections}
            submittedLineStatuses={submittedLineStatuses}
            lineTagsToDeselect={lineTagsToDeselect}
            lineStatusDropdownOpen={lineStatusDropdownOpen}
            onToggleLineStatusDropdown={toggleLineStatusDropdown}
            onToggleLineStatus={handleLineStatusToggle}
            onDeselectLineTag={handleLineTagDeselect}
            hasLineStatusChanges={hasLineChangesToSubmit}
            onSubmitLineStatus={handleLineStatusSubmit}
            lineStatusLockOwner={lineStatusLockOwner}
            renderLinePresence={renderLinePresence}
            lineStatusTags={LINE_STATUS_TAGS}
            excludedPartsByOrder={excludedAvailabilityPartsByOrder}
            onArchiveBomPart={handleArchiveBomPart}
            onToggleExcludedAvailabilityPart={handleToggleExcludedAvailabilityPart}
        />
    );

    return (
        <div className="order-validation-outer">
            <div ref={titleWrapRef} className="order-validation-page-title-wrap px-3 pt-3 pb-1">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <h1 className="orders-dashboard-page-title table-section-text table-section-text-left mb-0">Order Validation</h1>
                    <HeaderLineTabs value={layoutView} onChange={setLayoutView} />
                </div>
            </div>
        <div ref={pageShellRef} className="box-body border b-white rounded-2 orders-dashboard-shell order-validation-page" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="order-validation-sticky-stack">
            <div className="orders-dashboard-header px-3 pt-2 pb-2">
                <div className="orders-dashboard-header-inner order-validation-dashboard-toolbar">
                    <div className="order-validation-refresh-toolbar d-flex align-items-center flex-wrap gap-2">
                        <OrderRefresh
                            onRefresh={handleRefreshOrders}
                            lastRefreshData={lastRefreshData}
                            isLoading={isRefreshing}
                            title="Order Validation"
                        />
                        <PresenceBar
                            here={presence.here}
                            cells={presence.cells}
                            connected={presence.connected}
                            me={presence.me}
                        />
                    </div>
                    <div className="orders-dashboard-actions">
                        <div className="orders-dashboard-refresh-pill" role="status">
                            <span>
                                Auto-refresh in <strong>{timerFormatted}</strong>
                            </span>
                            <span className="orders-dashboard-refresh-meta">Next refresh in 10 minutes</span>
                        </div>
                        <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2 px-3" onClick={() => { setFilterTab(filterTabOpen === true ? false : true); }}>
                            <img src={filterIcon} style={{ width: '16px', height: '16px' }} alt="" />
                            Filter
                        </button>
                        <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2 px-3" onClick={handleExport}>
                            <img src={excelIcon} style={{ width: '16px', height: '16px' }} alt="" />
                            Export
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary d-flex align-items-center gap-2 px-3"
                            onClick={() => setShowColumnGroupsModal(true)}
                            title="Combine columns into groups"
                        >
                            <i className="fa fa-table-columns" aria-hidden="true"></i>
                            Column Groups
                        </button>
                        <ViewModeToggle
                            inlineDropdownMode={inlineDropdownMode}
                            onSelectView={handleSelectView}
                            defaultViewMode={defaultViewMode}
                            onSetDefault={handleSetViewModeDefault}
                        />
                    </div>
                </div>
            </div>

            <div ref={stickyBandRef} className="order-validation-top-band">
            <div ref={kpiStatsRef} className="orders-dashboard-stats px-3 pb-3">
                {kpiQuickFilter ? (
                    <div className="orders-stat-filter-banner mb-2 d-flex align-items-center flex-wrap gap-2" role="status">
                        <span className="text-muted small">
                            <strong className="text-body">
                                {kpiQuickFilter === 'due' && 'Active due orders'}
                                {kpiQuickFilter === 'missing' && 'Active missing parts'}
                                {kpiQuickFilter === 'ready' && 'Active in shipping'}
                                {typeof kpiQuickFilter === 'object' && kpiQuickFilter?.kind === 'order' && (
                                    <>Active order status: {kpiQuickFilter.label}</>
                                )}
                                {typeof kpiQuickFilter === 'object' && kpiQuickFilter?.kind === 'line' && (
                                    <>Active line status: {kpiQuickFilter.label}</>
                                )}
                            </strong>
                            <span className="ms-1">— Order Validation list</span>
                        </span>
                        <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => setKpiQuickFilter(null)}>
                            Clear
                        </button>
                    </div>
                ) : null}
                <div className="row g-3 align-items-stretch">
                    {[0, 1, 2, 3].map((slotIndex) => {
                        const slot = kpiSlotConfig[slotIndex];
                        const disp = getKpiSlotDisplay(slot);
                        const fv = filterValueForSlot(slot);
                        const isActive = fv != null && kpiQuickFiltersEqual(kpiQuickFilter, fv);
                        const tone = kpiStatToneClass[slotIndex];
                        const toggleListFilter = () => {
                            if (fv == null) return;
                            setKpiQuickFilter((prev) => (kpiQuickFiltersEqual(prev, fv) ? null : fv));
                        };
                        const picker =
                            kpiTagPickerSlot === slotIndex ? (
                                <div
                                    className="orders-kpi-tag-picker shadow border rounded bg-white p-2"
                                    role="group"
                                    aria-label="Choose KPI metric"
                                >
                                    <div className="orders-kpi-picker-header d-flex justify-content-between align-items-center gap-2 mb-2">
                                        <div className="small fw-bold text-muted text-uppercase mb-0">Customize Card</div>
                                        <button
                                            type="button"
                                            className="orders-kpi-picker-restore btn btn-link btn-sm p-0"
                                            onClick={() => {
                                                setKpiSlotConfig((prev) => {
                                                    const next = [...prev];
                                                    next[slotIndex] = { ...DEFAULT_KPI_SLOTS[slotIndex] };
                                                    return next;
                                                });
                                                setKpiTagPickerSlot(null);
                                            }}
                                        >
                                            Restore default
                                        </button>
                                    </div>

                                    <label className="orders-kpi-picker-label small text-muted mb-1" htmlFor={`ov-kpi-order-select-${slotIndex}`}>
                                        Order status tag
                                    </label>
                                    <select
                                        id={`ov-kpi-order-select-${slotIndex}`}
                                        className="form-select form-select-sm orders-kpi-picker-select mb-2"
                                        defaultValue=""
                                        onChange={(e) => {
                                            const label = e.target.value;
                                            if (!label) return;
                                            setKpiSlotConfig((prev) => {
                                                const next = [...prev];
                                                next[slotIndex] = { preset: 'order', label };
                                                return next;
                                            });
                                            setKpiTagPickerSlot(null);
                                        }}
                                    >
                                        <option value="">Select order status...</option>
                                        {ORDER_STATUS_TAGS.map((tag) => (
                                            <option key={`ov-kpi-order-opt-${slotIndex}-${tag.label}`} value={tag.label}>
                                                {tag.label}
                                            </option>
                                        ))}
                                    </select>

                                    <label className="orders-kpi-picker-label small text-muted mb-1" htmlFor={`ov-kpi-line-select-${slotIndex}`}>
                                        Line status tag
                                    </label>
                                    <select
                                        id={`ov-kpi-line-select-${slotIndex}`}
                                        className="form-select form-select-sm orders-kpi-picker-select"
                                        defaultValue=""
                                        onChange={(e) => {
                                            const label = e.target.value;
                                            if (!label) return;
                                            setKpiSlotConfig((prev) => {
                                                const next = [...prev];
                                                next[slotIndex] = { preset: 'line', label };
                                                return next;
                                            });
                                            setKpiTagPickerSlot(null);
                                        }}
                                    >
                                        <option value="">Select line status...</option>
                                        {LINE_STATUS_TAGS.map((tag) => (
                                            <option key={`ov-kpi-line-opt-${slotIndex}-${tag.label}`} value={tag.label}>
                                                {tag.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : null;
                        return (
                            <div key={slotIndex} className="col-12 col-sm-6 col-xl-3">
                                <div className="position-relative h-100 orders-kpi-slot-wrap">
                                    {disp.variant === 'total' ? (
                                        <>
                                            <div className="orders-total-flip-scene w-100 h-100">
                                                <div className={`orders-total-flip-inner ${totalOrdersStatExpanded ? 'is-flipped' : ''}`}>
                                                    <button
                                                        type="button"
                                                        id={`ov-orders-total-stat-trigger-${slotIndex}`}
                                                        aria-expanded={totalOrdersStatExpanded}
                                                        aria-controls={`ov-orders-total-stat-details-${slotIndex}`}
                                                        className="orders-stat-card orders-stat-card--blue orders-stat-card--clickable orders-total-flip-face orders-total-flip-face--front text-start border-0"
                                                        onClick={() => setTotalOrdersStatExpanded(true)}
                                                        aria-label="Total orders summary, flip to details"
                                                    >
                                                        <div className="orders-stat-card-icon">
                                                            <i className="fa fa-clipboard" aria-hidden="true"></i>
                                                        </div>
                                                        <div className="orders-stat-card-body flex-grow-1 min-w-0">
                                                            <div className="orders-stat-card-label mb-0">Total orders</div>
                                                            <div className="orders-stat-card-value">{uniqueKpiOrders}</div>
                                                            <div className={`orders-stat-card-trend orders-stat-card-trend--${newOrdersWeekStats.trendDir}`}>
                                                                {newOrdersWeekStats.trendShort ? (
                                                                    <>
                                                                        {newOrdersWeekStats.trendDir === 'up' && (
                                                                            <i className="fa fa-arrow-up me-1" aria-hidden="true"></i>
                                                                        )}
                                                                        {newOrdersWeekStats.trendDir === 'down' && (
                                                                            <i className="fa fa-arrow-down me-1" aria-hidden="true"></i>
                                                                        )}
                                                                        <span>{newOrdersWeekStats.trendShort}</span>
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        id={`ov-orders-total-stat-details-${slotIndex}`}
                                                        aria-labelledby={`ov-orders-total-stat-trigger-${slotIndex}`}
                                                        className="orders-stat-card orders-stat-card--blue orders-stat-card--clickable orders-total-flip-face orders-total-flip-face--back text-start border-0"
                                                        onClick={() => setTotalOrdersStatExpanded(false)}
                                                        aria-label="Flip back to summary"
                                                    >
                                                        <div className="orders-stat-card-icon orders-total-flip-back-icon">
                                                            <i className="fa fa-file-text-o" aria-hidden="true"></i>
                                                        </div>
                                                        <div className="orders-stat-card-body flex-grow-1 min-w-0">
                                                            <div className="orders-stat-card-label mb-0 mb-2">Details</div>
                                                            <div className="orders-stat-card-meta orders-stat-card-meta--emphasis mb-2 orders-total-flip-detail-text">
                                                                <span>
                                                                    New orders this week: <strong>{newOrdersWeekStats.thisWeekCount}</strong>
                                                                </span>
                                                                <span className="mx-1">·</span>
                                                                <span>
                                                                    Last week: <strong>{newOrdersWeekStats.lastWeekCount}</strong>
                                                                </span>
                                                            </div>
                                                            <div className="orders-stat-card-meta small text-muted mb-2 orders-total-flip-detail-text">
                                                                {newOrdersWeekStats.changeText}
                                                            </div>
                                                            <div className="orders-stat-card-meta small mb-0 orders-total-flip-detail-text">
                                                                <span className="text-body">{kpiTotalLinesDisplay}</span> lines loaded ·{' '}
                                                                <span className="text-body">{uniqueKpiOrders}</span> orders
                                                            </div>
                                                            <div className="orders-total-flip-footnote small text-muted mt-2 mb-0">Tap to flip back</div>
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="orders-kpi-filter-trigger"
                                                title="Choose tag for this card"
                                                aria-label="Choose tag for this card"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setKpiTagPickerSlot((prev) => (prev === slotIndex ? null : slotIndex));
                                                }}
                                            >
                                                <i className="fa fa-filter" aria-hidden="true"></i>
                                            </button>
                                            {picker}
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className={`orders-stat-card ${tone} orders-stat-card--clickable text-start w-100 border-0 ${isActive ? 'orders-stat-card--active' : ''}`}
                                                onClick={toggleListFilter}
                                                aria-pressed={!!isActive}
                                            >
                                                <div className="orders-stat-card-icon">
                                                    <i className={disp.iconClass} aria-hidden="true"></i>
                                                </div>
                                                <div className="orders-stat-card-body">
                                                    <div className="orders-stat-card-label">{disp.title}</div>
                                                    <div className="orders-stat-card-value">{disp.value}</div>
                                                    {disp.meta ? (
                                                        <div className="orders-stat-card-meta orders-stat-card-meta--emphasis">{disp.meta}</div>
                                                    ) : null}
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                className="orders-kpi-filter-trigger"
                                                title="Choose tag for this card"
                                                aria-label="Choose tag for this card"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setKpiTagPickerSlot((prev) => (prev === slotIndex ? null : slotIndex));
                                                }}
                                            >
                                                <i className="fa fa-filter" aria-hidden="true"></i>
                                            </button>
                                            {picker}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {filterTabOpen ?
                <div className="row p-2 m-0" style={{ background: '#d3d3d333', width: '100%' }}>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Order</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={orderValue} onInput={(e) => setUseEffectOrder(e.target.value)} />
                        {orderSearch.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '155px',
                                    zIndex: '10'
                                }}
                            >
                                {orderSearch.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setOrderValue(item.order.ordernum);
                                            setOrderSearch([]);
                                        }}
                                    >
                                        {item.order.ordernum}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>PO NO.</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={POValue} onInput={(e) => setUseEffectPO(e.target.value)} />
                        {POSearch.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '155px',
                                    zIndex: '10'
                                }}
                            >
                                {POSearch
                                    .filter((item, index, arr) => index === 0 || item.order.custponum !== arr[index - 1].order.custponum)
                                    .map((item, index) => (
                                        <li
                                            key={index}
                                            style={{
                                                padding: '10px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #eee',
                                            }}
                                            onClick={() => {
                                                setPOValue(item.order.custponum);
                                                setPOSearch([]);
                                            }}
                                        >
                                            {item.order.custponum}
                                        </li>
                                    ))
                                }
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-3 col-md-3 col-xs-3 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Ship Method</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={ShippingMethodValue} onInput={(e) => setUseEffectShippingMethod(e.target.value)} />
                        {ShippingMethodSearch.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '155px',
                                    zIndex: '10'
                                }}
                            >
                                {ShippingMethodSearch.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setShippingMethodValue(item.MethodOfShipment);
                                            setShippingMethodSearch([]);
                                        }}
                                    >
                                        {item.MethodOfShipment}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-4 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Date</div>
                        <div className='d-flex justify-content-start align-items-center gap-2' style={{ width: '75%' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DatePicker
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control d-inline w-100"
                                    id="startDate"
                                    selected={startDate ? new Date(startDate) : null}
                                    placeholderText='YYYY-MM-DD'
                                    onChange={(e) => {
                                        if (!e) { setStartDate(''); return; }
                                        const y = e.getFullYear();
                                        const m = String(e.getMonth() + 1).padStart(2, '0');
                                        const d = String(e.getDate()).padStart(2, '0');
                                        setStartDate(`${y}-${m}-${d}`);
                                    }}
                                    isClearable
                                    shouldCloseOnSelect
                                    popperPlacement="top-start"
                                    popperProps={{ strategy: 'fixed' }}
                                    autoComplete='off'
                                />
                            </div>
                            <span> - </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DatePicker
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control d-inline w-100"
                                    id="endDate"
                                    selected={endDate ? new Date(endDate) : null}
                                    placeholderText='YYYY-MM-DD'
                                    onChange={(e) => {
                                        if (!e) { setEndDate(''); return; }
                                        const y = e.getFullYear();
                                        const m = String(e.getMonth() + 1).padStart(2, '0');
                                        const d = String(e.getDate()).padStart(2, '0');
                                        setEndDate(`${y}-${m}-${d}`);
                                    }}
                                    isClearable
                                    shouldCloseOnSelect
                                    popperPlacement="top-start"
                                    popperProps={{ strategy: 'fixed' }}
                                    autoComplete='off'
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Status</div>
                        <select className="form-select" style={{ width: '65%' }} onChange={(e) => setStatusValue(e.target.value)}>
                            <option value="">none</option>
                            {selectBoxAppend &&
                                selectBoxAppend
                                    .filter(item => item.status !== 'N/A')
                                    .map((item, index) => (
                                        <option key={index} value={item.status}>{item.status}</option>
                                    ))
                            }
                        </select>
                    </div>
                    <div class="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Cust Name</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={CustomerValue} onInput={(e) => { setUseEffectCustomer(e.target.value); setCustomerId(''); }} />
                        {CustomerSearch.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '200px',
                                    zIndex: '10'
                                }}
                            >
                                {CustomerSearch.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setCustomerValue(item.name);
                                            setCustomerId(item.id);
                                            setCustomerSearch([]);
                                        }}
                                    >
                                        {item.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>View By</div>
                        <select class="form-select" style={{ width: '62%' }} onChange={(e) => { setViewBy(e.target.value); }}>
                            <option value={false} selected={viewBy === true || viewBy === 'true' ? false : true}>Order</option>
                            <option value={true} selected={viewBy === true || viewBy === 'true' ? true : false}>Order Line</option>
                        </select>
                    </div>
                    <div class="col-lg-2 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2' style={{ minWidth: '60px', textAlign: 'right' }}>Country</div>
                        <input type="text" class="form-control" style={{ width: '65%' }} value={CountryValue} onInput={(e) => setUseEffectCountry(e.target.value)} />
                        {CountrySearch.length > 0 && (
                            <ul
                                style={{
                                    listStyleType: 'none',
                                    margin: '10px 0',
                                    padding: '0',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    top: '200px',
                                    zIndex: '10'
                                }}
                            >
                                {CountrySearch.map((item, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            padding: '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                        }}
                                        onClick={() => {
                                            setCountryValue(item.Country_Name);
                                            setCountrySearch([]);
                                        }}
                                    >
                                        {item.Country_Name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div class="col-lg-1 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <input type="button" class="btn btn-primary" value='Submit' onClick={() => { fetchOrdersSearch(); setCurrentPage(1); }} />
                    </div>
                </div> : ''}
            <div
                ref={controlsRowRef}
                className="orders-toolbar-date-row orders-dashboard-controls-row order-validation-controls-row px-3 pt-3 pb-3 mb-0"
                style={{ width: '100%', boxSizing: 'border-box' }}
            >
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex flex-wrap align-items-center orders-dashboard-controls-left" style={{ gap: '10px' }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowAllOrdersHistory(true)}
                            title="View audit history for orders on Order Validation"
                        >
                            <i className="fa fa-history me-1" aria-hidden="true"></i>
                            All Orders History
                        </button>
                        <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                            <span>Rows</span>
                            <select
                                className="px-2 py-1 border rounded-1 form-select form-select-sm orders-dashboard-page-size"
                                style={{ width: 'auto', minWidth: '72px' }}
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="-1">All</option>
                            </select>
                        </label>
                        <div className="orders-dashboard-date-pills d-flex flex-wrap align-items-center">
                            <button
                                type="button"
                                className={`btn orders-date-pill ${weekFilter ? 'orders-date-pill--active orders-date-pill--warning' : 'btn-outline-warning'}`}
                                onClick={() => {
                                    setWeekFilter(!weekFilter);
                                    if (!weekFilter) {
                                        setMonthFilter(false);
                                    }
                                }}
                            >
                                ⏰ Week
                            </button>
                            <button
                                type="button"
                                className={`btn orders-date-pill ${monthFilter ? 'orders-date-pill--active orders-date-pill--success' : 'btn-outline-success'}`}
                                onClick={() => {
                                    setMonthFilter(!monthFilter);
                                    if (!monthFilter) {
                                        setWeekFilter(false);
                                    }
                                }}
                            >
                                📆 Month
                            </button>
                        </div>
                    </div>
                    <div className="orders-dashboard-controls-right d-flex align-items-center flex-shrink-0 justify-content-end">
                        <div
                            className="orders-section-header-controls orders-dashboard-search-group d-flex flex-wrap align-items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                        >
                            <button
                                type="button"
                                className="btn btn-sm reset-column-order-btn"
                                onClick={(e) => { e.stopPropagation(); resetColumnOrder(); }}
                                title="Reset column order to default"
                            >
                                <i className="fa fa-undo" style={{ marginRight: '4px' }}></i>
                                Reset Column Order
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm reset-column-order-btn"
                                onClick={(e) => { e.stopPropagation(); setShowColumnVisibilityModal(true); }}
                                title="Show or hide columns"
                            >
                                <i className="fa fa-columns" style={{ marginRight: '4px' }}></i>
                                Show/Hide Columns
                            </button>
                            {hiddenColumnCount > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-sm reset-column-order-btn"
                                    onClick={(e) => { e.stopPropagation(); handleShowAllColumns(); }}
                                    title="Show all hidden columns"
                                >
                                    <i className="fa fa-eye" style={{ marginRight: '4px' }}></i>
                                    Show all columns ({hiddenColumnCount})
                                </button>
                            )}
                            <button
                                type="button"
                                className={`btn reset-column-order-btn assemblies${assembliesFilter ? ' is-selected' : ''}`}
                                aria-pressed={assembliesFilter}
                                onClick={() => setAssembliesFilter(!assembliesFilter)}
                            >
                                Assemblies
                            </button>
                            <div className="d-flex align-items-center">
                                <input
                                    type="text"
                                    className="form-control"
                                    id='search'
                                    placeholder={messages.search_label}
                                    value={tempSearchTerm}
                                    onChange={(e) => setTempSearchTerm(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    style={{ marginRight: '5px', minWidth: '140px' }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => searchOrder(tempSearchTerm)}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
            </div>
            <div className="order-validation-table-panel px-2 pb-2">
                    {loading ? (
                        <div className="text-center my-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                ref={tableScrollRef}
                                className="order-validation-table-scroll"
                            >
                                    {layoutView === LAYOUT_LINE ? renderOrderLineTable() : (
                                    <table
                                        className="table gf-table table-bordered table-hover mb-0 new-orders-table"
                                        style={{ minWidth: '1000px' }}
                                    >
                                        <thead className="order-validation-sticky-thead">
                                            <tr>
                                                <ReorderableCells order={effectiveColumnOrder} mergedCells={mergedHeaderCells} draggable dragHandlers={orderColumnDragHandlers} draggedColumnId={draggedColumnId} columnVisibility={columnVisibility} onToggleColumn={toggleColumnVisibility} isHeader>
                                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '170px' }}>Info</th>
                                                <th className="text-center" style={{ minWidth: '200px', maxWidth: '200px' }}>Order Verification</th>
                                                <th className="text-center" style={{ minWidth: '190px', maxWidth: '190px', whiteSpace: 'nowrap' }} onClick={() => handleSort('ordernum')}>
                                                    {messages.order_no_label} {getSortIcon('ordernum')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '172px', maxWidth: '172px', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => handleSort('orderedondate')}>
                                                    Ordered On Date {getSortIcon('orderedondate')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '120px', maxWidth: '120px' }}>WO#</th>
                                                <th className="text-center" style={{ minWidth: '200px', maxWidth: '200px' }} onClick={() => handleSort('name')}>
                                                    {messages.customer_name_label} {getSortIcon('name')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '120px', maxWidth: '120px' }} onClick={() => handleSort('custponum')}>
                                                    {messages.po_no_label} {getSortIcon('custponum')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '150px' }} onClick={() => handleSort('orig_shipdate')}>
                                                    {messages.shipping_date_label} {getSortIcon('orig_shipdate')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '100px', maxWidth: '100px' }}>{messages.line_item}</th>
                                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '150px' }}>Order Status</th>
                                                <th className="text-center bom-availability-cell">BOM</th>
                                                <th className="text-center" style={{ minWidth: '200px', maxWidth: '250px' }}>Line Status Report</th>
                                                <th className="text-center" style={{ minWidth: '168px', maxWidth: '168px' }} onClick={() => handleSort('method_of_shipment')}>
                                                    {messages.shipping_method_label} {getSortIcon('method_of_shipment')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '120px', maxWidth: '120px' }} onClick={() => handleSort('country_name')}>
                                                    {messages.country_label} {getSortIcon('country_name')}
                                                </th>
                                                <th className="text-center" style={{ minWidth: '280px', maxWidth: '320px' }}>Sticky Notes</th>
                                                <th className="text-center order-access-column" style={{ width: '152px', minWidth: '152px', maxWidth: '152px' }}>Order Access</th>
                                                <th className="text-center" style={{ minWidth: '96px', maxWidth: '96px' }}>Archive</th>
                                                </ReorderableCells>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={effectiveColumnOrder.length} className="text-center">
                                                        No new orders found
                                                    </td>
                                                </tr>
                                            ) : (
                                                sortedData.map((line, lineIndex) => {
                                                    const isReviewed = reviewToggles[line.id] || false;
                                                    const isReady = readyToggles[line.id] || false;
                                                    const isConfirmed = confirmCheckboxes[line.id] || false;
                                                    // Confirm & Move needs both gates. An already-confirmed order stays
                                                    // clickable so it can always be undone.
                                                    const canConfirm = isReviewed && isReady;
                                                    const confirmEnabled = canConfirm || isConfirmed;
                                                    const confirmBlockedReason = isReviewed
                                                        ? 'Must mark order Ready first'
                                                        : isReady
                                                            ? 'Must review order first'
                                                            : 'Must review and mark order Ready first';
                                                    const displayShipDate = getEarliestPromisedDateForOrder(line) || line.shipdate || line.orig_shipdate || '';
                                                    const rowStyle = getRowStyle(displayShipDate);
                                                    const existingStyle = rowStyle.style || {};
                                                    const isPoReview = shouldRowBePoReview(line);
                                                    const isOrange = shouldRowBeOrange(line);
                                                    const isPurple = shouldRowBePurple(line);
                                                    const isGreen = shouldRowBeGreen(line);
                                                    const rowBackground = isPoReview ? '#232323' : isOrange ? '#FF9D47' : isPurple ? '#BEA9FF' : (isGreen ? '#d4edda' : (existingStyle.backgroundColor || 'transparent'));
                                                    const lightDateRow = !isPoReview && !isOrange && !isPurple && ['#f8d7da', '#fff3cd', '#d1ecf1', '#d4edda'].includes(String(rowBackground).toLowerCase());
                                                    const priorityLineStatusHighlight = getPriorityLineStatusHighlight(line);
                                                    const lineStatusReportHighlight = priorityLineStatusHighlight
                                                        || (isPoReview
                                                            ? { backgroundColor: 'transparent', textColor: '#ffffff', secondaryTextColor: 'rgba(255, 255, 255, 0.85)' }
                                                            : (isOrange || isPurple)
                                                                ? { backgroundColor: 'transparent', textColor: 'inherit', secondaryTextColor: '#212529' }
                                                                : getLineStatusReportHighlight(line));
                                                    const isLineItemMismatch = line.negativeCountStr && line.negativeCountStr.includes('/') &&
                                                        parseInt(line.negativeCountStr.split('/')[0]) !== parseInt(line.negativeCountStr.split('/')[1]);
                                                    // Apply glow style when review toggle is ON (same approach as old code)
                                                    const rowGlowStyle = isReviewed 
                                                        ? { 
                                                            outline: '2px solid #22b814',
                                                            outlineOffset: '-2px',
                                                            boxShadow: '0 0 8px rgba(34, 184, 20, 0.6)',
                                                            position: 'relative',
                                                            zIndex: 1
                                                        }
                                                        : {};
                                                    return (
                                                    <React.Fragment key={`new-order-${lineIndex}`}>
                                                        <tr 
                                                            className={`${isReviewed ? 'review-mode-active' : ''}${canConfirm ? ' review-ready-active' : ''}${isPoReview ? ' po-review-row' : ''}${!isPoReview && isOrange ? ' hot-order-row' : ''}${!isPoReview && !isOrange && isPurple ? ' cc-hold-ppa-row' : ''}${lightDateRow ? ' bridge-light-row' : ''}`.trim()}
                                                            style={{
                                                                ...existingStyle,
                                                                backgroundColor: rowBackground,
                                                                color: isPoReview ? '#ffffff' : (lightDateRow ? '#212529' : undefined),
                                                                ...rowGlowStyle
                                                            }}
                                                        >
                                                            <ReorderableCells order={effectiveColumnOrder} mergedCells={getMergedBodyCells(line, displayShipDate, isOrange, isPoReview)} columnVisibility={columnVisibility}>
                                                            <td className="text-center orders-info-cell">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => { handleEyeIconClick(line) }}
                                                                        title="View order details"
                                                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                                    >
                                                                        <img src={viewIcon} style={{ width: '20px' }} alt="View" />
                                                                    </button>
                                                                    <OrderHistoryIconButton
                                                                        unreadCount={getUnreadCount(line?.order?.ordernum || line?.ordernum)}
                                                                        onClick={() => { handleHistoryIconClick(line) }}
                                                                    />
                                                                    {renderInfoComments(line)}
                                                                    {renderRowPresence(line.order?.ordernum || line.ordernum)}
                                                                </div>
                                                            </td>
                                                            <td className="order-verification-column text-center">
                                                                <div className="order-verification-layout">
                                                                    <div className="order-verification-top-row">
                                                                        <div className="order-verification-control-block">
                                                                            <label className="mb-0 review-label-large">Review</label>
                                                                            <label className="switch switch-medium mb-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={reviewToggles[line.id] || false}
                                                                                    onChange={(e) => {
                                                                                        const orderNum = line.order?.ordernum || line.ordernum;
                                                                                        handleReviewToggle(line.id, orderNum, e.target.checked);
                                                                                    }}
                                                                                />
                                                                                <span className="slider round"></span>
                                                                            </label>
                                                                        </div>
                                                                        <div className="order-verification-control-block order-verification-control-ready">
                                                                            <label className="mb-0 review-label-large">Ready</label>
                                                                            <button
                                                                                type="button"
                                                                                className={`ready-circle-btn ${readyToggles[line.id] ? 'active' : ''}`}
                                                                                aria-label={`Toggle ready status for order ${line.order?.ordernum || line.ordernum}`}
                                                                                aria-pressed={readyToggles[line.id] || false}
                                                                                onClick={() => {
                                                                                    const orderNum = line.order?.ordernum || line.ordernum;
                                                                                    const currentValue = readyToggles[line.id] || false;
                                                                                    handleReadyToggle(line.id, orderNum, !currentValue);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {/* Confirm & Move Checkbox - Label first, then checkbox */}
                                                                    <div className="d-flex align-items-center gap-2 order-verification-confirm-row">
                                                                        <label
                                                                            htmlFor={`confirm-${line.id}`}
                                                                            className="mb-0 confirm-label-large"
                                                                            style={{ cursor: confirmEnabled ? 'pointer' : 'not-allowed', opacity: confirmEnabled ? 1 : 0.5 }}
                                                                            title={confirmEnabled ? undefined : confirmBlockedReason}
                                                                        >
                                                                            Confirm & Move
                                                                        </label>
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`confirm-${line.id}`}
                                                                            className="confirm-checkbox-large"
                                                                            checked={isConfirmed}
                                                                            onChange={(e) => {
                                                                                const orderNum = line.order?.ordernum || line.ordernum;
                                                                                handleConfirmToggle(line.id, orderNum, e.target.checked);
                                                                            }}
                                                                            disabled={!confirmEnabled}
                                                                            style={{ cursor: confirmEnabled ? 'pointer' : 'not-allowed' }}
                                                                            title={
                                                                                isConfirmed
                                                                                    ? 'Uncheck to undo confirm & move'
                                                                                    : canConfirm
                                                                                        ? 'Check to confirm and move order'
                                                                                        : confirmBlockedReason
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center" style={{ minWidth: '190px', maxWidth: '190px', whiteSpace: 'nowrap'}}>
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <a 
                                                                        href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.ordernum}/view`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                                                                    >
                                                                        {getCetecOrderNumber(line)}
                                                                    </a>
                                                                    {renderRowActivity(line)}
                                                                    {(() => {
                                                                        const orderNum = line.order?.ordernum;
                                                                        const folderLink = orderNum ? getOrderFolderLink(orderNum) : null;
                                                                        const rowLinks = orderAccessLinks?.[line.id];
                                                                        const rowLinkUrl = Array.isArray(rowLinks) && rowLinks.length > 0
                                                                            ? rowLinks[rowLinks.length - 1]?.url?.trim()
                                                                            : null;
                                                                        const namedUrls = line?.order?.orderUrls;
                                                                        const firstOrderUrl = Array.isArray(namedUrls) && namedUrls.length > 0
                                                                            ? namedUrls[0]?.url?.trim()
                                                                            : null;
                                                                        const fallbackUrl = typeof line.folderUrl === 'string' ? line.folderUrl.trim() : null;
                                                                        const finalFolderLink = folderLink || rowLinkUrl || firstOrderUrl || fallbackUrl || null;
                                                                        const folderTitle = finalFolderLink ? `Open folder: ${finalFolderLink}` : 'No folder link saved yet';
                                                                        return (
                                                                            <a
                                                                                href={finalFolderLink || '#'}
                                                                                onClick={(event) => {
                                                                                    if (!finalFolderLink) {
                                                                                        event.preventDefault();
                                                                                    }
                                                                                }}
                                                                                target={finalFolderLink ? '_blank' : undefined}
                                                                                rel={finalFolderLink ? 'noopener noreferrer' : undefined}
                                                                                style={{ 
                                                                                    cursor: finalFolderLink ? 'pointer' : 'not-allowed',
                                                                                    textDecoration: 'none'
                                                                                }}
                                                                                title={folderTitle}
                                                                            >
                                                                                <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: finalFolderLink ? (isOrange ? '#cc7a00' : '#ff8c00') : '#cccccc' }}></i>
                                                                            </a>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </td>
                                                            <td className="text-center" style={{ minWidth: '172px', maxWidth: '172px', whiteSpace: 'nowrap' }}>
                                                                {getOrderedOnDate(line)}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <a href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.woNum}/view`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none' }}>
                                                                        {line.order?.woNum || '-'}
                                                                    </a>
                                                                    {line.order?.woNum && line.order.woNum !== '-' && (
                                                                        <a
                                                                            href={line.order?.woUrl || '#'}
                                                                            onClick={(event) => {
                                                                                if (!line.order?.woUrl) {
                                                                                    event.preventDefault();
                                                                                }
                                                                            }}
                                                                            target={line.order?.woUrl ? '_blank' : undefined}
                                                                            rel={line.order?.woUrl ? 'noreferrer' : undefined}
                                                                            style={{ 
                                                                                cursor: line.order?.woUrl ? 'pointer' : 'not-allowed',
                                                                                textDecoration: 'none',
                                                                                marginLeft: '4px'
                                                                            }}
                                                                            title={line.order?.woUrl ? `Open WO folder: ${line.order.woUrl}` : 'No WO folder link available'}
                                                                        >
                                                                            <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: line.order?.woUrl ? (isOrange ? '#cc7a00' : '#ff8c00') : '#cccccc' }}></i>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                {line.order?.customer?.id ? (
                                                                    <a 
                                                                        href={`https://gigaflightinc.cetecerp.com/customer/${line.order.customer.id}/view`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none' }}
                                                                    >
                                                                        {line.order.customer.name || ''}
                                                                    </a>
                                                                ) : (
                                                                    line.order?.customer?.name || ''
                                                                )}
                                                            </td>
                                                            <td className="text-center">{line.order?.custponum || ''}</td>
                                                            <td className="text-center">{displayShipDate}</td>
                                                            <td className="text-center" style={{
                                                                backgroundColor: (isPoReview || isOrange || isPurple) ? 'transparent' : (isLineItemMismatch ? '#e74c3c' : 'transparent'),
                                                                color: isPoReview ? '#ffffff' : ((isLineItemMismatch && !isPurple && !isOrange) ? 'white' : 'inherit'),
                                                                fontWeight: isLineItemMismatch ? 'bold' : 'normal'
                                                            }}>
                                                                {line.negativeCountStr || ''}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {renderOrderStatusPresence(line.id)}
                                                                    <div
                                                                        className="form-control order-status-trigger d-flex align-items-center justify-content-between flex-grow-1"
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            minHeight: "38px",
                                                                            backgroundColor: "white",
                                                                            border: "1px solid #ced4da"
                                                                        }}
                                                                        onClick={(e) => handleSelectTagsClick(e, line)}
                                                                    >
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {(orderStatusSelections[line.id] || []).map((tag, index) => (
                                                                                <span
                                                                                    key={`current-${index}`}
                                                                                    className="badge"
                                                                                    style={{
                                                                                        backgroundColor: tag.color,
                                                                                        color: "white",
                                                                                        fontSize: "0.75rem",
                                                                                        padding: "4px 8px",
                                                                                        borderRadius: "12px",
                                                                                        display: "inline-flex",
                                                                                        alignItems: "center",
                                                                                        gap: "4px"
                                                                                    }}
                                                                                >
                                                                                    {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                                                                    {tag.label}
                                                                                </span>
                                                                            ))}

                                                                            {(submittedOrderStatuses[line.id] || []).map((tag, index) => {
                                                                                const isMarkedForDeselection = (tagsToDeselect[line.id] || []).some(t => t.label === tag.label);
                                                                                return (
                                                                                    <span
                                                                                        key={`submitted-${index}`}
                                                                                        className="badge"
                                                                                        style={{
                                                                                            backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                                                                            color: "white",
                                                                                            fontSize: "0.75rem",
                                                                                            padding: "4px 8px",
                                                                                            borderRadius: "12px",
                                                                                            cursor: "pointer",
                                                                                            opacity: isMarkedForDeselection ? 0.6 : 1,
                                                                                            display: "inline-flex",
                                                                                            alignItems: "center",
                                                                                            gap: "4px"
                                                                                        }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleTagDeselect(line.id, tag);
                                                                                        }}
                                                                                        title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                                                                    >
                                                                                        {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                                                                        {tag.label}
                                                                                    </span>
                                                                                );
                                                                            })}

                                                                            {(orderStatusSelections[line.id] || []).length === 0 && (submittedOrderStatuses[line.id] || []).length === 0 && (
                                                                                <span className="text-muted">Select tags...</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {hasChangesToSubmit(line.id) && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-success btn-sm order-status-submit-btn"
                                                                            style={{
                                                                                minWidth: "20px",
                                                                                height: "20px",
                                                                                padding: "0",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                fontSize: "10px",
                                                                                borderRadius: "2px"
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOrderStatusSubmit(line.id);
                                                                            }}
                                                                            title="Submit changes"
                                                                        >
                                                                            ✓
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center bom-availability-cell">
                                                                {(() => {
                                                                    const report = orderLineStatusReportMap[String(line.order?.ordernum || '')];
                                                                    if (!report) return <span className="text-muted">-</span>;
                                                                    return <BomAvailabilityPill missing={report.missingPartsCount} total={report.totalPartsCount} />;
                                                                })()}
                                                            </td>
                                                            <td
                                                                className={`text-center${priorityLineStatusHighlight ? ' line-status-priority-cell' : ''}`}
                                                                style={{
                                                                minWidth: '200px',
                                                                maxWidth: '250px',
                                                                backgroundColor: lineStatusReportHighlight.backgroundColor,
                                                                color: lineStatusReportHighlight.textColor,
                                                                ...(priorityLineStatusHighlight ? {
                                                                    '--line-status-highlight': priorityLineStatusHighlight.backgroundColor,
                                                                    '--line-status-highlight-text': priorityLineStatusHighlight.textColor,
                                                                    boxShadow: 'none'
                                                                } : {})
                                                            }}>
                                                                {(() => {
                                                                    const reportRows = getDisplayLineStatusReportRows(line);
                                                                    if (reportRows.length === 0) {
                                                                        return <span className="text-muted">-</span>;
                                                                    }
                                                                    return (
                                                                    <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                                                        {reportRows.map((report, idx) => (
                                                                            <div key={`${normalizeLineStatusKey(report.status)}-${idx}`} style={{ paddingBottom: '4px' }}>
                                                                                <strong>{formatLineStatusDisplayLabel(report.status)}</strong> - <span style={{ color: lineStatusReportHighlight.textColor === 'inherit' ? '#28a745' : lineStatusReportHighlight.textColor, fontWeight: '600' }}>{getLineReportPercentage(report)}%</span>
                                                                                <div style={{ fontSize: '0.75rem', color: lineStatusReportHighlight.secondaryTextColor }}>({report.count}/{report.totalCount})</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center">{renderShippingMethodCell(line.order?.shipmentMethod?.methodOfShipment)}</td>
                                                            <td className={!isPoReview && line.order?.country?.country_Name !== 'United States' ? 'bg-dark text-light text-center country-cell-intl' : (line.order?.country?.country_Name === 'United States' ? 'text-center country-cell-us' : 'text-center')}>
                                                                {line.order?.country?.country_Name || 'N/A'}
                                                            </td>
                                                            <td className="notes-column sticky-notes-column">
                                                                {renderStickyNotes(line)}
                                                            </td>
                                                        <td className="order-access-column text-center">
                                                            <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ width: '100%', maxWidth: '118px', margin: '0 auto' }}>
                                                                {line?.order?.orderUrls && line.order.orderUrls.length > 0 && (
                                                                    <div className="d-flex flex-wrap gap-1 justify-content-center" style={{ width: '100%' }}>
                                                                        {line.order.orderUrls.map((urlItem) => (
                                                                            <div
                                                                                key={urlItem.id}
                                                                                style={{
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    backgroundColor: '#f0f0f0',
                                                                                    border: '1px solid #ddd',
                                                                                    borderRadius: '12px',
                                                                                    padding: '3px 6px',
                                                                                    fontSize: '10px',
                                                                                    maxWidth: '112px',
                                                                                    position: 'relative'
                                                                                }}
                                                                            >
                                                                                <a
                                                                                    href={urlItem.url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{
                                                                                        textDecoration: 'none',
                                                                                        color: '#007bff',
                                                                                        fontWeight: '500',
                                                                                        marginRight: '4px',
                                                                                        flexGrow: 1,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap'
                                                                                    }}
                                                                                    title={`${urlItem.urlName}: ${urlItem.url}`}
                                                                                >
                                                                                    {urlItem.urlName}
                                                                                </a>
                                                                                <button
                                                                                    type="button"
                                                                                    className="order-access-remove"
                                                                                    onClick={() => handleDeleteOrderAccessLink(line.id, urlItem.id, line.order?.ordernum, urlItem.urlName)}
                                                                                    title="Delete this URL"
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary"
                                                                    style={{
                                                                        fontSize: '0.7rem',
                                                                        padding: '3px 8px',
                                                                        height: '28px'
                                                                    }}
                                                                    onClick={() => handleOrderAccessClick(line)}
                                                                    title="Add folder access link"
                                                                >
                                                                    <i className="fa fa-plus" style={{ marginRight: '3px' }}></i>
                                                                    Add
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0"
                                                                onClick={() => handleArchiveOrder(line)}
                                                                title="Archive this order"
                                                                style={{ 
                                                                    fontSize: '1.2rem',
                                                                    color: '#6c757d',
                                                                    textDecoration: 'none'
                                                                }}
                                                            >
                                                                <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                                            </button>
                                                        </td>
                                                            </ReorderableCells>
                                                    </tr>
                                                    <InlineOrderDropdown
                                                        line={line}
                                                        inlineDropdownMode={inlineDropdownMode}
                                                        expandedInlineOrders={expandedInlineOrders}
                                                        orderDetailsData={orderDetailsData}
                                                        inlineActiveView={inlineActiveView}
                                                        renderInlineOrderHeader={renderInlineOrderHeader}
                                                        orderFolderLink={resolveOrderFolderLinkForLine(line)}
                                                        onInlineViewChange={handleInlineViewChange}
                                                        onInlineSwitchView={handleInlineSwitchView}
                                                        onInlineDualView={handleInlineDualView}
                                                        expandedInlineBomRows={expandedInlineBomRows}
                                                        onToggleInlineBomRow={toggleInlineBomRow}
                                                        lineStatusSelections={lineStatusSelections}
                                                        submittedLineStatuses={submittedLineStatuses}
                                                        submittedOrderStatuses={submittedOrderStatuses}
                                                        orderStatusSelections={orderStatusSelections}
                                                        lineTagsToDeselect={lineTagsToDeselect}
                                                        lineStatusDropdownOpen={lineStatusDropdownOpen}
                                                        onToggleLineStatusDropdown={toggleLineStatusDropdown}
                                                        onHandleLineStatusToggle={handleLineStatusToggle}
                                                        onHandleLineTagDeselect={handleLineTagDeselect}
                                                        onHandleBulkLineTagDeselect={handleBulkLineTagDeselect}
                                                        hasLineChangesToSubmit={hasLineChangesToSubmit}
                                                        onHandleLineStatusSubmit={handleLineStatusSubmit}
                                                        renderLinePresence={renderLinePresence}
                                                        lineStatusLockOwner={lineStatusLockOwner}
                                                        lineStatusTags={LINE_STATUS_TAGS}
                                                        contentPaddingClass="p-4"
                                                        tableClassName="table table-hover border rounded inline-order-details-table inline-order-lines-table"
                                                        dualViewOrderTableClassName="table table-sm table-hover border rounded inline-separate-order-lines-table"
                                                        dualViewBomTableClassName="table table-sm table-hover border rounded"
                                                        commentsMap={commentsMap}
                                                        onAddComment={handleAddComment}
                                                        onDeleteComment={handleDeleteComment}
                                                        onResolveThread={handleResolveThread}
                                                        orderData={newOrdersData}
                                                        orderBulkModeEnabled={orderBulkModeEnabled}
                                                        setOrderBulkModeEnabled={setOrderBulkModeEnabled}
                                                        orderHasBulkChanges={orderHasBulkChanges}
                                                        setOrderHasBulkChanges={setOrderHasBulkChanges}
                                                        onBulkSubmit={handleBulkLineStatusSubmit}
                                                        onArchiveOrderLine={handleArchiveOrderLineItem}
                                                        onArchiveBomPart={handleArchiveBomPart}
                                                        archivedOrderIds={archivedOrderIds}
                                                        excludedAvailabilityPartsByOrder={excludedAvailabilityPartsByOrder}
                                                        onToggleExcludedAvailabilityPart={handleToggleExcludedAvailabilityPart}
                                                        dropdownColSpan={Math.max(1, effectiveColumnOrder.length - inlineDropdownTrailingCells)}
                                                        dropdownTrailingCells={inlineDropdownTrailingCells}
                                                        portalPartLinkDropdowns
                                                    />
                                                    </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                    )}
                                </div>
                            {/* Dropdown rendered outside the table */}
                            {dropdownOpenId && (
                                <OrderStatusDropdown
                                    visible={true}
                                    position={dropdownPosition}
                                    orderStatuses={ORDER_STATUS_TAGS}
                                    savedCustomTags={collectCustomOrderStatusTags(
                                        [submittedOrderStatuses, orderStatusSelections],
                                        ORDER_STATUS_TAGS
                                    )}
                                    allowCustomTag
                                    selectedTags={orderStatusSelections[dropdownOpenId] || []}
                                    onToggleTag={(tag) => handleOrderStatusToggle(dropdownOpenId, tag)}
                                    onClose={() => setDropdownOpenId(null)}
                                    menuClassName="order-status-menu"
                                />
                            )}
                            {/* Pagination */}
                            {!loading && filteredRecordsCount > 0 && (
                                <nav
                                    className="d-flex justify-content-between align-items-center mt-3"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem',
                                        padding: '0 16px',
                                    }}
                                >
                                    <span className="mb-0">
                                        {layoutView === LAYOUT_LINE
                                            ? `Showing ${lineRows.length} lines on this page`
                                            : `Showing ${filteredRecordsCount} of ${displayTotalRecords}`}
                                    </span>

                                    {totalPages > 1 && (
                                        <ul
                                            className="pagination d-flex align-items-center mb-0"
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            {/* Previous Button */}
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                                    Previous
                                                </button>
                                            </li>

                                            {/* Page Select Dropdown Styled Like Pagination */}
                                            <li className="page-item">
                                                <select
                                                    className="form-select mx-2"
                                                    style={{ width: 'auto' }}
                                                    value={currentPage}
                                                    onChange={(e) => handlePageChange(Number(e.target.value))}
                                                >
                                                    {[...Array(totalPages)].map((_, index) => (
                                                        <option key={index} value={index + 1}>
                                                            Page {index + 1}
                                                        </option>
                                                    ))}
                                                </select>
                                            </li>

                                            {/* Next Button */}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    )}
                                </nav>
                            )}
                        </>
                    )}
            </div>
            <OrderPopup
                show={showPopup}
                onClose={closePopup}
                orderData={selectedOrder}
                sourceLine={selectedSourceLine}
                orderAccessLinks={orderAccessLinks}
                getOrderFolderLink={getOrderFolderLink}
                commentsMap={commentsMap}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onResolveThread={handleResolveThread}
                commentSourcePage="order-validation"
                onOpenOrderAccess={handleOrderAccessClick}
                onDeleteOrderAccessLink={handleDeleteOrderAccessLink}
                excludedAvailabilityPartsByOrder={excludedAvailabilityPartsByOrder}
                onToggleExcludedAvailabilityPart={handleToggleExcludedAvailabilityPart}
                onArchiveBomPart={handleArchiveBomPart}
                renderLinePresence={renderLinePresence}
                lineStatusLockOwner={lineStatusLockOwner}
                presenceFocus={presenceFocus}
                presenceBlur={presenceBlur}
                lineStatusCell={lineStatusCell}
                stickyNotes={stickyNotes}
                shippingInstructions={shippingInstructions}
            />
            <Modal
                show={showOrderAccessModal}
                onHide={handleCloseOrderAccessModal}
                centered
                className="order-access-modal-root"
                backdropClassName="order-access-modal-backdrop"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Manage Order URLs</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrderForAccess && (
                        <div>
                            <div className="mb-3">
                                <div className="d-flex align-items-center">
                                    <p className="mb-0">
                                        <strong>Order:</strong> {selectedOrderNumber ? (
                                            <a
                                                href={`https://gigaflightinc.cetecerp.com/order/MN${selectedOrderNumber}/view`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                {selectedOrderNumber}
                                            </a>
                                        ) : (
                                            'N/A'
                                        )}
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2 text-decoration-none text-primary d-flex align-items-center"
                                        onClick={() => handleCopyToClipboard(selectedOrderNumber, 'order')}
                                        disabled={!selectedOrderNumber}
                                        title="Copy order number"
                                    >
                                        <i className="fa fa-copy" aria-hidden="true"></i>
                                    </button>
                                    <a
                                        href="https://aeroplicity.com/file-share/all"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-link p-0 ms-2 text-decoration-none text-primary d-flex align-items-center"
                                        title="Open Aeroplicity orders folder"
                                    >
                                        <i className="fa fa-folder-open" aria-hidden="true"></i>
                                    </a>
                                </div>
                                {copiedField === 'order' && (
                                    <small className="text-success d-block mt-1">Copied to clipboard</small>
                                )}
                            </div>
                            <div className="mb-3">
                                <div className="d-flex align-items-center">
                                    <p className="mb-0">
                                        <strong>Customer:</strong> {selectedOrderForAccess?.order?.customer?.id ? (
                                            <a
                                                href={`https://gigaflightinc.cetecerp.com/customer/${selectedOrderForAccess.order.customer.id}/view`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                {selectedCustomerName || 'N/A'}
                                            </a>
                                        ) : (
                                            selectedCustomerName || 'N/A'
                                        )}
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2 text-decoration-none text-primary d-flex align-items-center"
                                        onClick={() => handleCopyToClipboard(selectedCustomerName, 'customer')}
                                        disabled={!selectedCustomerName}
                                        title="Copy customer name"
                                    >
                                        <i className="fa fa-copy" aria-hidden="true"></i>
                                    </button>
                                    <a
                                        href="https://aeroplicity.com/file-share/all"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-link p-0 ms-2 text-decoration-none text-primary d-flex align-items-center"
                                        title="Open Aeroplicity orders folder"
                                    >
                                        <i className="fa fa-folder-open" aria-hidden="true"></i>
                                    </a>
                                </div>
                                {copiedField === 'customer' && (
                                    <small className="text-success d-block mt-1">Copied to clipboard</small>
                                )}
                            </div>

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL Name:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newLinkUrlName}
                                        onChange={(e) => setNewLinkUrlName(e.target.value)}
                                        placeholder="e.g., Engineering Docs, Customer Files, etc."
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL:</Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={newLinkUrl}
                                        onChange={(e) => setNewLinkUrl(e.target.value)}
                                        placeholder="https://example.com/folder"
                                    />
                                </Form.Group>
                            </Form>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseOrderAccessModal}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddOrderAccessLink}>
                        Add URL
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Notification */}
            {toast.show && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        backgroundColor: toast.type === 'success' ? '#28a745' : toast.type === 'error' ? '#dc3545' : '#17a2b8',
                        color: 'white',
                        padding: '15px 20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 9999,
                        maxWidth: '400px',
                        animation: 'slideIn 0.3s ease-in-out'
                    }}
                >
                    {toast.message}
                </div>
            )}

            <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>

            <ColumnGroupsModal
                show={showColumnGroupsModal}
                onHide={() => setShowColumnGroupsModal(false)}
                activeGroupId={activeGroupId}
                defaultGroupId={defaultGroupId}
                customGroups={customGroups}
                onApply={handleColumnGroupsApply}
                onSaveCustomGroup={handleSaveCustomGroup}
                onDeleteCustomGroup={handleDeleteCustomGroup}
                groupableColumnIds={GROUPABLE_COLUMN_IDS}
                columnLabels={OV_COLUMN_LABELS}
            />

            <ColumnVisibilityModal
                show={showColumnVisibilityModal}
                onHide={() => setShowColumnVisibilityModal(false)}
                columns={columnVisibilityList}
                visibleColumns={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                pageName="Order Validation"
            />

            <OrderHistoryModal
                show={!!historyModalOrder}
                onClose={() => setHistoryModalOrder(null)}
                ordernum={historyModalOrder}
                onViewed={markViewed}
            />
            <AllOrdersHistoryModal
                show={showAllOrdersHistory}
                onClose={() => setShowAllOrdersHistory(false)}
                orderNums={validationHistoryOrderNums}
                title="Order Validation History"
            />

            <FloatingRefreshButton
                onRefresh={handleManualPageRefresh}
                ariaLabel="Refresh Order Validation"
                hoverLabel="Refresh"
            />
        </div>
        </div>
    );
};

export default OrderValidation;

